/**
 * Slack-like room: message stream (agent finals + user prompts) + composer
 * with state-aware Send / approval-deck. The composer encapsulates the
 * IDLE / RUNNING / AWAITING_APPROVAL state machine; this component only
 * coordinates the upstream services (channel events, approval claim
 * lease, REST history hydration) and feeds the reducer.
 */
import DeployIntentsBanner from './DeployIntentsBanner.jsx';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Tab } from '@headlessui/react';
import { List, History, Database, Plug, FolderTree, Loader2, CalendarClock, ListChecks, AlertTriangle, Map as ProjectMapIcon, FileSearch, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import useCompactChrome, { useDockSlots } from '@stepflow/ui-shared/hooks/useCompactChrome.js';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { useAgentChannel, appsyncChannelScope, mergeBackfill } from '@/hooks/useAgentChannel';
import { getRestApiBearerToken, appSyncAgentScopeFromBearerToken } from '@/services/apiToken';
import { fetchNetworkChannelEvents } from '@/services/networkChannelEvents';
import { poolExecute, poolApproval, fetchNetworkSummary } from '@/services/agenticNetworksApi';
import { listProjectTasks, stopAutoWavePlan, approveMilestonePlan, fetchBuilderLogTail, getDiscussion, postDiscussion, getContextBuild, startContextBuild, startDirectRun, getRoomMode, setRoomMode, setRoomContextModel, setRoomInternet, setRoomStageCap, setRoomExpert, setRoomFact, setRoomLiaisonAction, setRoomTeamAccess, roomOffering, setRoomExpertRequest, setRoomCertification, setRoomRuleChange, setExpertConsult, setDeployIntent, setExpertTrain, setRoomOnboarding, applyExpertTemplate, planRoom, confirmAnswer, adoptDiscussionMessage, keepAsKnowledge, editDiscussionMessage, deleteDiscussionMessage, signalDiscussionMessage, handoffContext } from '@stepflow/ui-shared/services/dataPlaneApi';
import { useRoomAlarms } from '@stepflow/ui-shared/hooks/useRoomAlarms';
import RoomAlarmsPanel from './RoomAlarmsPanel.jsx';
import { IncludedTokensExceededError } from '@stepflow/ui-shared/utils/billingErrors';
import { formatCodeConnectorResolutionLine } from '@stepflow/ui-shared/utils/codeConnectorResolution';
import { displayVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';
import { economyFromAgents } from '@stepflow/ui-shared/utils/economyMode.js';
import { verboseFromAgents } from '@stepflow/ui-shared/utils/verboseMode.js';
import { manifestToRoster, manifestToReactFlow, isManifest, cappedOutAgentIds } from '@stepflow/ui-shared/utils/manifestToReactFlow.js';
import { mergeRoster } from '@stepflow/ui-shared/utils/agentTree.js';
import RoomBudgetGauge from '@stepflow/ui-shared/components/RoomBudgetGauge.jsx';
import RoomAvatar from '@stepflow/ui-shared/components/dashboard/RoomAvatar.jsx';
import useRoomTint from '@stepflow/ui-shared/hooks/useRoomTint.js';
import { listNetworkReleases } from '@stepflow/ui-shared/services/networkReleasesApi';
import {
  TRIGGER_HEADLESS,
  TRIGGER_HYBRID,
  normalizeTriggerMode,
} from '@stepflow/ui-shared/utils/networkTrigger';
import { projectRoomLifecycleUiState } from '@stepflow/ui-shared/utils/poolLifecycleUiState.js';
import { WORKBENCH_ACTIVITIES_STREAM_OUTER_PADDING } from '@stepflow/ui-shared/utils/workbenchPromptFieldClassNames.js';
import { useApprovalClaim } from '@/hooks/useApprovalClaim';
import {
  AGENT_EVENTS,
  envelopeEventType,
  UI_STATES,
} from '@/constants/agentEvents';
import { reduceAgentRoomState } from '@/utils/agentRoomReducer';
import { useRoomState } from '@stepflow/ui-shared/hooks/useRoomState';
import { buildAttachmentInputBody } from '@/services/networkAttachmentApi';
import ContextPreviewPanel from './ContextPreviewPanel';
import PoolSettingsDrawer from './PoolSettingsDrawer';
import PlanGateDialog from './PlanGateDialog.jsx';
import { PlanWordsContext, planWords } from '../utils/planWords.js';
import DelegateMemberDialog from './DelegateMemberDialog';
import Composer from './composer/Composer.jsx';
import { DiscussionMessage, ContextStartHint, ContextBuildBar, StreamDayDivider, ReplyBar, AgentAnswering, authorLabel } from './composer/ContextDiscussion.jsx';
import ExpertTrainBar from './composer/ExpertTrainBar.jsx';
import LinkPreviews from './composer/LinkCard.jsx';
import ConfirmDialog from '@stepflow/ui-shared/components/ConfirmDialog.jsx';
import { promptDialog, alertDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';
import useRefreshOnGrowth from '../hooks/useRefreshOnGrowth.js';
import useScrollBack from '@stepflow/ui-shared/hooks/useScrollBack.js';
import ThreadPane from './composer/ThreadPane.jsx';
import DocPane from './composer/DocPane.jsx';
import ContextPane, { ContextCard } from './composer/ContextPane.jsx';
import WorkOutputs from './composer/WorkOutputs.jsx';
import HandoffDialog, { PlanBasis } from './composer/HandoffDialog.jsx';
import useRoomWorkspace from '../hooks/useRoomWorkspace.js';
import { personLabel } from '@stepflow/ui-shared/utils/personLabel.js';
import FileCard from './composer/FileCard.jsx';
import CodeFilePanel from './composer/CodeFilePanel.jsx';
import StandinControl from './composer/StandinControl.jsx';
import GateAdvice from './composer/GateAdvice.jsx';
import useGateAdvice from '../hooks/useGateAdvice.js';
import useStandin from '../hooks/useStandin.js';
import { contextModelLabel } from './composer/ContextModelPicker.jsx';
import { codeLangOf } from '@stepflow/ui-shared/utils/codeLang.js';
import useStickToBottom from '@stepflow/ui-shared/hooks/useStickToBottom.js';
import { liveDiscussionFrom, mergeDiscussion, streamTimeline, withStreamGroups, withContinuations, withRosterRollups, isSingleDayGroup, splitThreads, settleAsks, pendingAskNames, liveAskProgress, optimisticMessage, withoutEchoedLocals, streamDaySections, screenPage, keepKindsFor } from '../utils/contextDiscussion.js';
import { splitDirectEnvelopes, directRunViews } from '../utils/directRuns.js';
import DirectRunCard from './DirectRunCard.jsx';
import AgentRowBuilderAction from './turn/AgentRowBuilderAction.jsx';
import RunBlock from './turn/RunBlock.jsx';
import RouteErrorBoundary from '@stepflow/ui-shell/RouteErrorBoundary.jsx';
import ManifestTreeFlow from '@stepflow/ui-workbench/components/ManifestTreeFlow.jsx';
import useBuilderRoomControls from '../hooks/useBuilderRoomControls.js';
import { mergeRoomEnvelopes, latestPlanBeacon, visibleRunBlocks, shouldBlockSend } from '../utils/roomEnvelopes.js';
import ExecutionsTab from './executions/ExecutionsTab.jsx';
import SchedulerTab from './scheduler/SchedulerTab.jsx';
import AgenticDataPlaneTab from './AgenticDataPlaneTab.jsx';
import TasksTab from './tasks/TasksTab.jsx';
import ProjectMapPanel from './tasks/ProjectMapPanel.jsx';
import { deriveProjectStage, isPlanRunnable, taskBindingFields, journeyModeFor, hasWorkedPlan, planTabsDecision, planTabsShown } from '../utils/projectPipeline.js';
import AgentStreamDock from './dock/AgentStreamDock.jsx';
import { useNetworkAsl } from '../hooks/useNetworkAsl.js';
import SecretsCatalog from '@stepflow/ui-shared/components/secrets/SecretsCatalog.jsx';
import AgentWorkspaceBrowser from '@stepflow/ui-shared/components/workspace/AgentWorkspaceBrowser.jsx';
import { secretsRegistryV1Enabled, listSecretsRegistry } from '@stepflow/ui-shared/services/toolsApi';
import { fetchBuilderCliSlot } from '@stepflow/ui-shared/services/builderRunsApi.js';
import { cancelUniversalBuilderRun } from '@stepflow/ui-shared/services/builderCliApi';
import {
  getMcpCredentialStatus,
  listPoolAgentDisables,
  setPoolAgentDisable,
  clearPoolAgentDisable,
  listPoolAgentReuses,
  setPoolAgentReuse,
  clearPoolAgentReuse,
} from '@stepflow/ui-shared/services/poolMembersApi';
import { canRespondToBuilderApproval, canManageRoomConnectors } from '@stepflow/ui-shared/utils/roomRoles';
import { listPoolMembers } from '@stepflow/ui-shared/services/poolMembersApi.js';
import { markMentionsRead, markRoomSeen } from '@stepflow/ui-shared/services/mentionsApi.js';
import { ROOMS_SEEN_EVENT } from '@stepflow/ui-shared/hooks/useRoomUnread.js';
import useRoomTyping, { typingLine as formatTyping } from '@stepflow/ui-shared/hooks/useRoomTyping.js';

/** A SCREENFUL of discussion per page (Slack-style scroll-back), sized to THIS screen — ``screenPage``: the stream's
 *  height (the window's before it mounts) ÷ a compact message + a small buffer. Scroll-back then pages more. */
const discussionPage = (el) => screenPage((el && el.clientHeight) || (typeof window !== 'undefined' ? window.innerHeight : 0));

function normalizeUiStateFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const u = payload.uiState;
  return u ? String(u).toUpperCase().trim() : null;
}

/** Human-friendly label for the header room-state chip. The wire values (from
 *  `UI_STATES` + the backend, e.g. `AWAITING_INITIAL_INPUT`) are engine enums — never show
 *  them raw. Unknown value → Title-Cased words as a safe fallback (never a raw SCREAMING_CASE). */
const UI_STATE_LABELS = Object.freeze({
  IDLE: 'Idle',
  RUNNING: 'Running',
  AWAITING_INITIAL_INPUT: 'Ready',
  AWAITING_APPROVAL: 'Needs approval',
  COMPLETED: 'Done',
  FAILED: 'Failed',
});
function friendlyUiState(uiState) {
  const k = String(uiState || '').toUpperCase().trim();
  if (!k) return '';
  if (UI_STATE_LABELS[k]) return UI_STATE_LABELS[k];
  return k.toLowerCase().replace(/_+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// SFN ARN markers for per-agent .sync SUB-executions (worker/builder children) — used to tell a
// top-level run START from a sub-execution when paging channel-events history one run at a time.
const _SUBEXEC_HISTORY_MARKERS = ['universal-worker', 'universal-builder'];

let __nextLocalId = 1;

function freshOptimisticBubble({ text, actorName, correlationId }) {
  __nextLocalId += 1;
  return {
    id: `opt-${__nextLocalId}-${Date.now().toString(36)}`,
    correlationId,
    actorName,
    text,
    ts: new Date().toISOString(),
  };
}

/** Stable hash for `useMemo` dependency on the envelope buffer. We don't
 *  want to re-run the reducer on every reference change of `messages`
 *  while the underlying contents are stable (ie. another render flush in
 *  the same effect tick). */
function envelopeBufferKey(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const last = arr[arr.length - 1];
  return `${arr.length}:${last?.eventId || last?.timestamp || ''}`;
}

export default function AgenticNetworkRoom({
  networkId,
  poolId,
  displayName,
  /** Caller's legacy single role in this pool — kept for back-compat with
   *  components that haven't moved to ``poolRoles`` yet. */
  poolRole,
  /** Caller's canonical multi-role array in this pool — drives RBAC for
   *  builder approvals and roster management. Includes legacy CREATOR
   *  mapped to OWNER. Empty when the user is not a member of this pool. */
  poolRoles = [],
  /** Release version (from ``GET /my/agentic-networks``). Every room
   *  is pinned to a real ``vN`` at creation time. */
  releaseVersion,
  /** Pool lifecycle status (M5) — drives the composer freeze + persistent
   *  inactivity banner. ``GET /my/agentic-networks`` attaches it server-side
   *  so we don't pay an extra round-trip per room. */
  lifecycleStatus,
  /** ``{ daysUntilRetire }`` warning payload — only present for NOTIFIED
   *  rooms. Server-computed so the banner countdown is consistent across
   *  every member of the pool without TZ drift. */
  inactivityWarning,
  /** Optional deep-link target tab (e.g. ``'executions'`` from the Rooms
   *  landing's "View N executions" CTA). Seeds the initial ``roomTabIndex``;
   *  the user can freely switch afterwards. */
  initialTab,
}) {
  const { user, activeTenantId, workspaceRole } = useAuth();
  const scope = useMemo(() => {
    const fromToken = appSyncAgentScopeFromBearerToken(getRestApiBearerToken());
    return fromToken || appsyncChannelScope(activeTenantId, user?.sub);
  }, [activeTenantId, user?.sub]);

  const { messages, connected, reconnectEpoch } = useAgentChannel(networkId, scope, poolId);

  // §26.4 SERIAL HANDSHAKE — a Send carrying fresh "+" uploads waits for the room-uploads context
  // (kicked at UPLOAD time by the composer) to be ready before the run starts. The worker publishes
  // a ``CONTEXT_READY`` beacon on this room's channel, correlated by ``stagingId``.
  // CONTEXT MODE (MODES.md §3, M4): the room discussion — loaded once (the truth), then live
  // ``CONTEXT_MESSAGE`` beacons append. Shown above the composer while it is in Context mode.
  const [composerMode, setComposerMode] = useState('');
  const [discussion, setDiscussion] = useState({ messages: [], agents: [], loading: false, error: '' });
  const [discussionPending, setDiscussionPending] = useState('');
  // ASKS IN FLIGHT (MODES.md §10.2): {questionId: {agentName, since}} — settled by the live answer / a failure beacon
  const [asksPending, setAsksPending] = useState({});
  useEffect(() => { setAsksPending({}); }, [networkId, poolId]);
  const _discussionOn = composerMode === 'context';
  // SCROLL-BACK PAGING (Slack-style, MODES.md §3): open on a screenful of the newest messages; scrolling up loads
  // the next older page early (``useScrollBack``) without moving what you're reading.
  const [discussionHasMore, setDiscussionHasMore] = useState(false);
  // EDIT / DELETE (MODES.md §3, Slack-style): my own messages → edit + delete; a moderator (room owner / workspace
  // owner, per the server's ``canModerate``) → delete any. The server enforces; this only lights the "⋯" menu.
  const [canModerate, setCanModerate] = useState(false);
  const [deleteAsk, setDeleteAsk] = useState(null);
  const handleEditMessage = useCallback(async (m, text) => {
    const res = await editDiscussionMessage(networkId, m, text, { pool: poolId });
    if (res?.message) setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, [res.message]) }));
  }, [networkId, poolId]);
  const loadCtxBuildRef = useRef(null);
  // TEAM SIGNALS (MODES.md §3a): Agree / Disagree / Unclear → merged at once (the live copy replaces it everywhere)
  const handleSignal = useCallback(async (m, signal, on) => {
    try {
      const res = await signalDiscussionMessage(networkId, m, signal, on, { pool: poolId });
      if (res?.message) setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, [res.message]) }));
      loadCtxBuildRef.current && loadCtxBuildRef.current();     // readiness + "N new items" move with it
    } catch (err) {
      setDiscussion((d) => ({ ...d, error: (err && err.message) || "Couldn't record that — try again." }));
    }
  }, [networkId, poolId]);
  const confirmDeleteMessage = useCallback(async () => {
    const m = deleteAsk;
    setDeleteAsk(null);
    if (!m) return;
    try {
      const res = await deleteDiscussionMessage(networkId, m, { pool: poolId });
      if (res?.message) setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, [res.message]) }));
    } catch (err) {
      setDiscussion((d) => ({ ...d, error: (err && err.message) || "Couldn't delete it — try again." }));
    }
  }, [deleteAsk, networkId, poolId]);
  // a deleted message with no answers under it just disappears; one that was answered stays as a tombstone
  const visibleDiscussion = useMemo(() => {
    const list = withoutEchoedLocals(discussion.messages || []);   // a just-sent message, until the real one is here
    if (!list.some((m) => m && m.deleted)) return list;
    const answered = new Set(list.filter((m) => m && !m.deleted && (m.replyTo || m.threadOf)).map((m) => m.threadOf || m.replyTo));
    return list.filter((m) => !(m && m.deleted) || answered.has(m.id));
  }, [discussion.messages]);
  // THREADS (MODES.md §3b, Slack-style, ONE level): replies live under their root, not in the stream
  const threads = useMemo(() => splitThreads(visibleDiscussion), [visibleDiscussion]);
  // THE THREAD PANE (Slack): ONE thread open at a time, in its own column beside the stream (the network diagram
  // stays visible); the "N replies" link or "Reply in thread" opens it (the latter focuses the reply box)
  const [openThread, setOpenThread] = useState(null);       // {root, focus}
  // THE DOC PANE (CONTEXT-DOCS.md §P1) shares that column — ONE secondary pane at a time (opening one closes the other)
  const [openDoc, setOpenDoc] = useState(null);             // {message, att}
  // THE CONTEXT PANE (CONTEXT-DOCS.md §P3) — a generated version, same column, one at a time
  const [openContext, setOpenContext] = useState(null);     // the context card's message
  useEffect(() => { setOpenThread(null); setOpenDoc(null); setOpenContext(null); }, [networkId, poolId]);
  const openThreadFor = useCallback((root) => { setOpenDoc(null); setOpenContext(null); setOpenThread({ root, focus: false }); }, []);
  const handleReplyInThread = useCallback((root) => { setOpenDoc(null); setOpenContext(null); setOpenThread({ root, focus: true }); }, []);
  const openDocFor = useCallback((message, att) => { setOpenThread(null); setOpenContext(null); setOpenDoc({ message, att }); }, []);
  const openContextFor = useCallback((m) => { setOpenThread(null); setOpenDoc(null); setOpenContext(m); }, []);
  // the open doc follows its message LIVE (Reading… → Ready lands while the pane is open — CONTEXT-DOCS §P2)
  const docLive = useMemo(() => {
    if (!openDoc) return null;
    const m = (discussion.messages || []).find((x) => x && x.id === openDoc.message.id) || openDoc.message;
    const att = (m.attachments || []).find((a) => a.index === openDoc.att.index) || openDoc.att;
    return { message: m, att };
  }, [openDoc, discussion.messages]);
  // a CODE file (.py .json .yaml … — a `</>` snippet or a 📎 file) shows as a code panel (CONTEXT-DOCS §P4)
  const renderFiles = useCallback((m) => (m.attachments || []).map((att) => (codeLangOf(att.name) ? (
    <CodeFilePanel key={`${m.id}-${att.index}`} networkId={networkId} poolId={poolId} message={m} att={att} onOpen={openDocFor} />
  ) : (
    <FileCard key={`${m.id}-${att.index}`} networkId={networkId} poolId={poolId} message={m} att={att} onOpen={openDocFor}
      active={!!openDoc && openDoc.message.id === m.id && openDoc.att.index === att.index} />
  ))), [networkId, poolId, openDocFor, openDoc]);
  // LINK PREVIEWS (CONTEXT-DOCS.md §L): the cards under a message; its author / a moderator removes one (live for all)
  const renderLinks = useCallback((m) => (
    <LinkPreviews networkId={networkId} poolId={poolId} message={m}
      canHide={canModerate || (!!user?.sub && m.authorSub === user.sub)}
      onHidden={(v) => setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, [v]) }))} />
  ), [networkId, poolId, canModerate, user?.sub]);
  const threadRoot = useMemo(() => {
    if (!openThread) return null;
    return (threads.top || []).find((m) => m.id === openThread.root.id) || openThread.root;   // live counts
  }, [openThread, threads.top]);
  const liveReplies = useMemo(() => liveDiscussionFrom(messages).filter((m) => m && m.threadOf), [messages]);
  // EXPERTS AS TOOLS (EXPERT-ROOMS §19): the expert answered the agent's question later → the person asks THAT agent
  // again IN THE ROOM (agents are asked in the room, never inside a thread — a person triggers it), carrying the
  // expert's answer: its earlier answer + this = where it stopped
  const handleThreadContinue = useCallback(async (_root, cont) => {
    const res = await postDiscussion(networkId, {
      text: `Continue your earlier answer now that ${cont.expertName} has answered:\n\n${cont.answer}`,
      agentId: cont.agentId }, { pool: poolId });
    if (res?.message) setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, [res.message]) }));
    return res;
  }, [networkId, poolId]);
  const handleThreadSend = useCallback(async (root, text) => {
    const res = await postDiscussion(networkId, { text, threadOf: root.id, threadSk: root.sk || '' }, { pool: poolId });
    if (res?.root) setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, [res.root]) }));
    return res;
  }, [networkId, poolId]);
  const streamScrollRef = useRef(null);
  // ROOM TINT (ROOMS.md §6b): inside the room, the app's neutral lines wear the room's identity color
  useRoomTint(networkId, poolId);
  const loadOlderDiscussion = useCallback(async () => {
    const oldest = (discussion.messages || [])[0];
    if (!networkId || !oldest || !oldest.sk) { setDiscussionHasMore(false); return; }
    const res = await getDiscussion(networkId, { pool: poolId, limit: discussionPage(streamScrollRef.current), before: oldest.sk });
    setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, res?.messages || []) }));
    setDiscussionHasMore(!!res?.hasMore && (res?.messages || []).length > 0);
  }, [networkId, poolId, discussion.messages]);
  // STICK TO BOTTOM + "New messages ↓" (Slack): pinned while I'm at the bottom; scrolled up → a count pill
  const _topLevel = useMemo(() => (discussion.messages || []).filter((m) => m && !m.threadOf), [discussion.messages]);
  const stick = useStickToBottom({
    rootRef: streamScrollRef, items: _topLevel,
    isMine: (m) => !!m && m.kind === 'user' && !!user?.sub && m.authorSub === user.sub,
    resetKey: `${networkId}:${poolId || ''}`,
  });
  const { sentinelRef: olderSentinelRef } = useScrollBack({
    rootRef: streamScrollRef, hasMore: discussionHasMore, onLoadOlder: loadOlderDiscussion,
    firstKey: ((discussion.messages || [])[0] || {}).id || '',
  });
  // network / room switch: never show the previous room's discussion
  useEffect(() => { setDiscussion({ messages: [], agents: [], loading: false, error: '' }); }, [networkId, poolId]);
  // the discussion is ROOM HISTORY (activity) — loaded whatever mode the room is in
  useEffect(() => {
    if (!networkId) return undefined;
    let cancelled = false;
    setDiscussion((d) => ({ ...d, loading: true, error: '' }));
    setDiscussionHasMore(false);
    getDiscussion(networkId, { pool: poolId, limit: discussionPage(streamScrollRef.current) })
      .then((res) => {
        if (cancelled) return;
        setDiscussion((d) => ({ messages: mergeDiscussion(d.messages, res?.messages || []),
          agents: Array.isArray(res?.agents) ? res.agents : d.agents, loading: false, error: '' }));
        setDiscussionHasMore(!!res?.hasMore);
        setCanModerate(!!res?.canModerate);
      })
      .catch(() => { if (!cancelled) setDiscussion((d) => ({ ...d, loading: false, error: "Couldn't load the discussion." })); });
    return () => { cancelled = true; };
  }, [networkId, poolId]);
  // ASK PROGRESS (MODES.md §10.2a): every agent answering in this room RIGHT NOW (anyone's question) — phase, then the
  // answer streaming in — from the live channel; my own just-sent asks show at once. Grouped under their question.
  const askProgress = useMemo(() => liveAskProgress(messages, discussion.messages, asksPending),
    [messages, discussion.messages, asksPending]);
  // THE ANSWER NAMES ITS QUESTION (MODES.md §10.2b): id → message, and "go to the question" (scroll + a brief flash)
  const discussionById = useMemo(() => new Map((discussion.messages || []).map((m) => [m.id, m])), [discussion.messages]);
  const questionOf = useCallback((id) => discussionById.get(id) || null, [discussionById]);
  const jumpToMessage = useCallback((id) => {
    const root = streamScrollRef.current;
    const el = root && root.querySelector(`[data-testid="context-msg-${CSS.escape(String(id))}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('sf-flash');
    setTimeout(() => el.classList.remove('sf-flash'), 1600);
  }, []);
  const askProgressByQ = useMemo(() => {
    const out = {};
    for (const p of askProgress) (out[p.questionId] = out[p.questionId] || []).push(p);
    return out;
  }, [askProgress]);
  useEffect(() => {
    const { pending, errors } = settleAsks(asksPending, discussion.messages, messages);
    if (pending !== asksPending) setAsksPending(pending);
    if (errors.length) setDiscussion((d) => ({ ...d, error: errors[errors.length - 1] }));
  }, [discussion.messages, messages]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const live = liveDiscussionFrom(messages);
    if (live.length) setDiscussion((d) => {
      const merged = mergeDiscussion(d.messages, live);
      return merged === d.messages ? d : { ...d, messages: merged };
    });
  }, [messages]);
  // PEOPLE to @-tag (MENTIONS.md): this room's ACTIVE members except me — loaded once per room (the drawer keeps its
  // own copy); and opening the room clears MY unread mentions here (the sidebar's bold + count for this room).
  const [mentionPeople, setMentionPeople] = useState([]);
  // EVERYONE in the room (me included) — the people directory for avatars / names (``personOf``)
  const [roomPeople, setRoomPeople] = useState([]);
  useEffect(() => {
    if (!networkId || !poolId) { setMentionPeople([]); setRoomPeople([]); return undefined; }
    let gone = false;
    listPoolMembers(networkId, poolId)
      .then((d) => {
        if (gone) return;
        const me = user?.sub || '';
        // ONE rule (L152): a real full name, else the email — personLabel everywhere a person is shown
        setRoomPeople((d?.members || [])
          .filter((m) => m && m.userSub)
          .map((m) => ({ sub: m.userSub, name: personLabel({ userName: m.userName, email: m.email }, ''),
            email: m.email || '', picture: m.picture || m.avatarUrl || '' })));
        setMentionPeople((d?.members || [])
          .filter((m) => m && m.userSub && m.userSub !== me && String(m.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
          .map((m) => ({ sub: m.userSub, name: personLabel({ userName: m.userName, email: m.email }, ''), email: m.email || '' }))
          .filter((p) => p.name));
      })
      .catch(() => { if (!gone) setMentionPeople([]); });
    markMentionsRead(networkId, poolId)
      .then(() => window.dispatchEvent(new CustomEvent('stepflow:mentions-changed')))
      .catch(() => {});
    return () => { gone = true; };
  }, [networkId, poolId, user?.sub]);

  // ROOM UNREAD (MENTIONS.md §5): I'm caught up on this room — on opening it, while I watch new activity land (≤ every
  // 15 s, only when the tab is visible) and on leaving; the sidebar un-bolds it at once.
  const _seenAt = useRef(0);
  const catchUp = useCallback((force = false) => {
    if (!networkId) return;
    if (!force && (Date.now() - _seenAt.current < 15000 || document.visibilityState !== 'visible')) return;
    _seenAt.current = Date.now();
    window.dispatchEvent(new CustomEvent(ROOMS_SEEN_EVENT, { detail: { networkId, poolId } }));
    markRoomSeen(networkId, poolId).catch(() => {});
  }, [networkId, poolId]);
  useEffect(() => {
    catchUp(true);
    return () => { catchUp(true); };
  }, [catchUp]);
  const _activityCount = (messages || []).length + ((discussion && discussion.messages) || []).length;
  useEffect(() => { if (_activityCount) catchUp(false); }, [_activityCount, catchUp]);

  // ROOM PRESENCE (MENTIONS.md §6): "<user> typing…" over the room's own presence channel (never stored).
  const typing = useRoomTyping({ networkId, scope, poolId, meSub: user?.sub || '' });
  const typingText = useMemo(() => formatTyping(typing.typers.map((t) => {
    const p = (mentionPeople || []).find((m) => m.sub === t.sub);
    return (p && (p.name || p.email)) || t.email || 'Someone';
  })), [typing.typers, mentionPeople]);

  const handleSendContext = useCallback(async (text, { agentId = '', agentIds = [], attachments = [], mode = 'ask', mentions = [] } = {}) => {
    if ((!text && !(attachments || []).length) || !networkId) return;
    const agentName = agentId ? ((discussion.agents.find((a) => a.id === agentId) || {}).name || agentId) : '';
    if (agentId && mode === 'do') {
      // DO (MODES.md §10.3): the agent itself works on it — its block in the main stream, its live stream in the
      // dock. A worker agent / a busy agent / no direct work here → the quick answer below.
      try {
        const res = await startDirectRun(networkId, { agentId, text, mode, attachments }, { pool: poolId });
        if (res && res.route === 'direct' && res.runId) {
          setDirectModes((m) => ({ ...m, [res.runId]: res.mode || mode }));
          setRoomTabIndex(0);
          openAgentInDock(`${agentId}@${res.runId}`);
          try {
            if (!sessionStorage.getItem('sf-direct-lane-notice')) {
              sessionStorage.setItem('sf-direct-lane-notice', '1');
              pushLocalSystemEvent(`Direct work: ${agentName} works on its own, not on a planned task. What it does `
                + `stays in context/${agentId}/ and its own conversation; planned tasks keep theirs.`);
            }
          } catch (e) { /* storage unavailable — skip the notice */ }
          return;
        }
        if (res && res.message) setDiscussion((d) => ({ ...d, error: `Quick answer — ${res.message}` }));
      } catch (err) {
        setDiscussion((d) => ({ ...d, error: (err && err.message) || "Couldn't start the agent — try again." }));
        throw err;
      }
    }
    // ASK (MODES.md §10.2) — one grounded call as the agent, answered ASYNC: the question returns now, the answer
    // lands live. (No worker → the server answers inline and the reply comes back here.)
    if (agentName) setDiscussionPending(agentName);
    // SHOWN AT ONCE (MODES.md §10.2a): my message + "<agent> · Getting started" appear the moment I hit Enter — the
    // server's answer replaces them (no "did anything happen?" gap)
    const askedIds = (agentIds && agentIds.length ? agentIds : (agentId ? [agentId] : []));
    const local = optimisticMessage({ text, me: user?.sub || '', meName: user?.name || user?.email || '', agentId,
      agentName });
    const localWaits = Object.fromEntries(askedIds.map((id) => [`${local.id}:${id}`, { questionId: local.id, agentId: id,
      agentName: ((discussion.agents || []).find((a) => a.id === id) || {}).name || id, since: Date.now() }]));
    if (!(attachments || []).length) {
      setDiscussion((d) => {
        const last = (d.messages || [])[(d.messages || []).length - 1];
        return { ...d, messages: [...(d.messages || []), { ...local, sk: `${(last && (last.sk || last.at)) || ''}\uffff` }] };
      });
      if (askedIds.length) setAsksPending((p) => ({ ...p, ...localWaits }));
    }
    const dropLocal = () => {
      setDiscussion((d) => ({ ...d, messages: (d.messages || []).filter((m) => m.id !== local.id) }));
      setAsksPending((p) => Object.fromEntries(Object.entries(p).filter(([, v]) => v.questionId !== local.id)));
    };
    try {
      const res = await postDiscussion(networkId, { text, agentId, agentIds, attachments, mentions }, { pool: poolId });
      dropLocal();
      const waits = res?.pendings || (res?.pending ? [res.pending] : []);
      if (waits.length) {
        // one question, several agents → one pending entry per (question, agent)
        setAsksPending((p) => ({ ...p, ...Object.fromEntries(waits.map((w) => [`${w.questionId}:${w.agentId}`,
          { agentName: w.agentName || agentName, agentId: w.agentId, questionId: w.questionId, since: Date.now() }])) }));
      }
      setDiscussion((d) => ({ ...d, error: res?.replyError || '',
        messages: mergeDiscussion(d.messages, [res?.message, ...(res?.replies || (res?.reply ? [res.reply] : []))]
          .filter(Boolean)) }));
    } catch (err) {
      dropLocal();
      setDiscussion((d) => ({ ...d, error: (err && err.message) || "Couldn't send — try again." }));
      throw err;                  // the composer restores the text
    } finally {
      setDiscussionPending('');
      loadCtxBuild();
    }
  }, [networkId, poolId, discussion.agents]);  // eslint-disable-line react-hooks/exhaustive-deps
  // ADOPT (MODES.md §10.4): a person makes an agent's answer the team's decision — live to everyone
  const handleAdopt = useCallback(async (m) => {
    if (!m || !m.id || !networkId) return;
    try {
      const res = await adoptDiscussionMessage(networkId, m.id, { pool: poolId });
      setDiscussion((d) => ({ ...d, error: '',
        messages: mergeDiscussion(d.messages, [res?.message, res?.activity].filter(Boolean)) }));
      loadCtxBuild();
    } catch (err) {
      setDiscussion((d) => ({ ...d, error: (err && err.message) || "Couldn't adopt it — try again." }));
    }
  }, [networkId, poolId]);  // eslint-disable-line react-hooks/exhaustive-deps
  // KEEP AS KNOWLEDGE (KNOWLEDGE.md §14, K5): a message becomes a PROPOSED item the network's owner reviews
  //   (Settings → Knowledge). A document / procedure asks a title; a trained answer asks the question it answers.
  const handleKeep = useCallback(async (m, kind) => {
    if (!m || !m.text || !networkId) return;
    const req = { kind, text: m.text, msgId: m.id };
    if (kind === 'qa') {
      const asked = (m.threadOf && (discussion.messages || []).find((x) => x.id === m.threadOf)?.text) || '';
      const q = await promptDialog({ title: 'Keep as a trained answer', message: 'Which question does this answer?',
        defaultValue: asked.slice(0, 300), confirmLabel: 'Keep' });
      if (!q) return;
      req.question = q;
    } else {
      const t = await promptDialog({ title: kind === 'procedure' ? 'Keep as a procedure' : 'Keep as a document',
        message: 'Give it a name.', defaultValue: (m.text.split('\n')[0] || '').replace(/^#+\s*/, '').slice(0, 80),
        confirmLabel: 'Keep' });
      if (!t) return;
      req.title = t;
    }
    try {
      await keepAsKnowledge(networkId, req, { pool: poolId });
      await alertDialog({ title: 'Kept', message: "It waits for the network's owner — Settings → Knowledge." });
    } catch (err) {
      setDiscussion((d) => ({ ...d, error: (err && err.message) || "Couldn't keep it — try again." }));
    }
  }, [networkId, poolId, discussion.messages]);  // eslint-disable-line react-hooks/exhaustive-deps
  // BUILD THE CONTEXT (MODES.md §3, M5): what's not yet folded in ("N new items") + the build status. The
  // server builds async; ``CONTEXT_BUILT`` on the room channel refreshes it.
  const [ctxBuild, setCtxBuild] = useState({ status: 'idle', pending: { messages: 0, files: 0 }, builtAt: '', lastError: '' });
  const loadCtxBuild = useCallback(() => {
    if (!networkId) return;
    getContextBuild(networkId, { pool: poolId })
      .then((res) => { if (res) setCtxBuild((c) => ({ ...c, ...res })); })
      .catch(() => {});
  }, [networkId, poolId]);
  useEffect(() => { if (_discussionOn || composerMode === 'plan') loadCtxBuild(); }, [_discussionOn, composerMode, loadCtxBuild]);
  loadCtxBuildRef.current = loadCtxBuild;
  // who signalled → their roster name (the pills' "Pani and Raj agree")
  // THE PEOPLE DIRECTORY (one lookup for every avatar / name): the room roster (me included) → me from my session →
  // anyone who authored a message we have. ``picture`` flows through, so profile pictures light up everywhere.
  const personOf = useMemo(() => {
    const dir = new Map();
    for (const m of discussion.messages || []) {
      if (m && m.authorSub && m.authorName && !dir.has(m.authorSub)) dir.set(m.authorSub, { sub: m.authorSub, name: m.authorName });
    }
    if (user?.sub) dir.set(user.sub, { sub: user.sub, name: personLabel({ name: user.name, email: user.email }, ''),
      email: user.email || '', picture: user.picture || '' });
    for (const p of roomPeople || []) dir.set(p.sub, { ...(dir.get(p.sub) || {}), ...Object.fromEntries(Object.entries(p).filter(([, v]) => v)) });
    return (sub) => (sub && dir.get(sub)) || null;
  }, [roomPeople, user, discussion.messages]);
  const signalNameOf = useCallback((sub) => {
    const p = personOf(sub);
    return (p && (p.name || p.email)) || '';
  }, [personOf]);
  const _ctxBuiltSeq = useMemo(
    () => messages.filter((m) => m && (m.eventType ?? m.type) === 'CONTEXT_BUILT').length, [messages]);
  useEffect(() => { if (_ctxBuiltSeq) loadCtxBuild(); }, [_ctxBuiltSeq, loadCtxBuild]);
  // LIVE COUNT: a teammate's / an agent's message landing over AppSync re-fetches "N new items" (debounced — a burst
  // is one call); only while Context is showing. Before this the number only moved on my own send / a build.
  const _liveDiscussionCount = useMemo(() => liveDiscussionFrom(messages).length, [messages]);
  useRefreshOnGrowth(_liveDiscussionCount, _discussionOn, loadCtxBuild);
  const handleBuildContext = useCallback(async () => {
    setCtxBuild((c) => ({ ...c, status: 'building', lastError: '' }));
    try {
      await startContextBuild(networkId, { pool: poolId });
    } catch (err) {
      setCtxBuild((c) => ({ ...c, status: 'idle', lastError: (err && err.message) || "Couldn't build the context." }));
    } finally {
      loadCtxBuild();
    }
  }, [networkId, poolId, loadCtxBuild]);
  // CONTEXT → PLAN goes through ONE door — the hand-off review (CONTEXT-DOCS.md §H3): Plan works only from an
  // APPROVED, frozen context. Already handed off, nothing new, no drift → straight through.
  const [leaveAsk, setLeaveAsk] = useState(null);   // { resolve } while the review is open
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffError, setHandoffError] = useState('');
  const handleLeaveContext = useCallback(() => {
    const plan = ctxBuild.plan || {};
    const unbuilt = (ctxBuild.pending || {}).messages > 0;
    if (plan.handoff && !plan.drift && !unbuilt) return Promise.resolve(true);
    setHandoffError('');
    return new Promise((resolve) => setLeaveAsk({ resolve }));
  }, [ctxBuild]);
  const confirmHandoff = useCallback(async ({ version, docs, approve }) => {
    setHandoffBusy(true); setHandoffError('');
    try {
      await handoffContext(networkId, { version, docs, approve }, { pool: poolId });
      loadCtxBuild();
      const a = leaveAsk; setLeaveAsk(null);
      if (a) a.resolve(true);
    } catch (e) {
      setHandoffError((e && e.message) || "Couldn't hand off — try again.");
    }
    setHandoffBusy(false);
  }, [networkId, poolId, leaveAsk, loadCtxBuild]);
  // every READ document of the room — the hand-off's checklist (all ticked by default)
  const handoffDocs = useMemo(() => {
    const seen = new Map();
    for (const m of discussion.messages || []) {
      for (const a of (m && m.attachments) || []) if (a && a.sha && !seen.has(a.sha)) seen.set(a.sha, { sha: a.sha, name: a.name });
    }
    return [...seen.values()];
  }, [discussion.messages]);
  const contextReadyRef = useRef(new Set());
  const contextWaitersRef = useRef(new Map());
  useEffect(() => {
    for (const m of messages) {
      if (!m || (m.eventType ?? m.type) !== 'CONTEXT_READY') continue;
      const sid = String(m.payload?.stagingId ?? m.stagingId ?? '').trim();
      if (!sid || contextReadyRef.current.has(sid)) continue;
      contextReadyRef.current.add(sid);
      const waiters = contextWaitersRef.current.get(sid);
      if (waiters) {
        waiters.forEach((fn) => { try { fn(); } catch { /* noop */ } });
        contextWaitersRef.current.delete(sid);
      }
    }
  }, [messages]);

  // Resolve once the CONTEXT_READY beacon for ``stagingId`` has arrived, or after ``timeoutMs``
  // (FAIL-OPEN — a missed/slow beacon must NEVER block the run; context then lands next turn, the
  // pre-§26 behavior). Returns immediately when already ready or when there's no stagingId.
  const awaitContextReady = useCallback((stagingId, timeoutMs = 25000) => {
    const sid = String(stagingId || '').trim();
    if (!sid || contextReadyRef.current.has(sid)) return Promise.resolve();
    return new Promise((resolve) => {
      const arr = contextWaitersRef.current.get(sid) || [];
      let done = false;
      const fire = () => { if (!done) { done = true; resolve(); } };
      arr.push(fire);
      contextWaitersRef.current.set(sid, arr);
      setTimeout(fire, timeoutMs);
    });
  }, []);

  // Phase 3 — approval claim lease. Multiple supervisors in the same pool
  // subscribe to the same APPROVAL_REQUESTED, so we coordinate ownership
  // via the lease before anyone can actually send APPROVE / DENY / MODIFY
  // to Step Functions.
  const {
    claim: claimApprovalLease,
    delegate: delegateApprovalLease,
    escalate: escalateApprovalLease,
    getApproval: getApprovalLease,
    isSelfClaimer,
  } = useApprovalClaim(networkId, poolId, messages, user?.sub || '');

  // Floating agent-map drag state — persisted per network so each room
  // remembers where the user parked the overlay last time. Resetting via
  // The stream is always WIDE (fills its column; the diagram stays docked) — the wide/centered toggle was removed
  // 2026-10-10 by the owner: wide is the one layout.

  // Docked agent-tree panel: a DOCK beside the wide stream (2026-10-10) — collapsed to a slim rail by default, the user
  // opens it with the arrow and docks it back; open/closed + width are persisted per user. Width is drag-resizable.
  // COMPACT CHROME (ROOMS.md §2.1): the room header + tabs render INTO the shell's dock row (portals) when the user
  // chose compact on this machine; ``compact`` is true only once the dock's slots exist (else the full header shows).
  const [compactChrome] = useCompactChrome();
  const dockSlots = useDockSlots(compactChrome);
  const compact = !!dockSlots;
  const _TREE_OPEN_KEY = 'stepflow:tree-panel-open';
  const [treeOpen, setTreeOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage?.getItem(_TREE_OPEN_KEY) === '1'; } catch (_e) { return false; }
  });
  useEffect(() => {
    try { window.localStorage?.setItem(_TREE_OPEN_KEY, treeOpen ? '1' : '0'); } catch (_e) { /* ignore */ }
  }, [treeOpen]);
  const _TREE_W_KEY = 'stepflow:tree-panel-width';
  const _TREE_W_DEFAULT = 560;
  const [treePanelWidth, setTreePanelWidth] = useState(() => {
    if (typeof window === 'undefined') return _TREE_W_DEFAULT;
    const v = Number(window.localStorage?.getItem(_TREE_W_KEY));
    return Number.isFinite(v) && v >= 280 ? Math.min(v, 720) : _TREE_W_DEFAULT;
  });
  useEffect(() => {
    try { window.localStorage?.setItem(_TREE_W_KEY, String(treePanelWidth)); } catch (_e) { /* ignore */ }
  }, [treePanelWidth]);
  // Drag the divider: moving LEFT widens the (right-docked) tree. Bounded 280–720px.
  const _treeResizeStart = useRef(null);
  const onTreeDividerDown = useCallback((e) => {
    _treeResizeStart.current = { x: e.clientX, w: treePanelWidth };
    const onMove = (ev) => {
      const s = _treeResizeStart.current;
      if (!s) return;
      const next = Math.min(720, Math.max(280, s.w + (s.x - ev.clientX)));
      setTreePanelWidth(next);
    };
    const onUp = () => {
      _treeResizeStart.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [treePanelWidth]);

  const [historyEnvelopes, setHistoryEnvelopes] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Incremental history paging (perf): load only the LAST run on mount, then let the user pull
  // one more at a time. A generative run is ~80-90 fat channel events, and each page is a large
  // (multi-MB) payload that takes ~4s — so eager-loading N runs stacked several 4s serial requests
  // and made the room feel slow to open. ``historyCursor`` = the next-page cursor from the backend
  // (null when the feed is exhausted); ``historyPaging`` gates the "Load previous run" button.
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyPaging, setHistoryPaging] = useState(false);
  const historyRunStartsRef = useRef(new Set());   // distinct top-level run ids seen so far
  /** Optimistic bubbles awaiting backend reconciliation by correlationId. */
  const [optimisticBubbles, setOptimisticBubbles] = useState([]);
  /** KICK-SEEDED run id: the ``executionName`` ``poolExecute`` returns the instant a run is accepted.
   *  Passed to the reducer as ``pendingRunId`` so it can AUTHORITATIVELY start that run's block the
   *  moment its first event arrives — even if ``currentRunId`` still points at the prior run and the
   *  run-start beacon (USER_PROMPT / EXECUTION_STARTED) is delayed or reordered. That adoption race is
   *  what left the roster on standby when uploaded artifacts delayed the kick: the run's agent events
   *  arrived while ``currentRunId`` was still the previous run, so they never bound to the visible
   *  roster. With the kick's id known up front, the very first matching event force-starts the run. */
  const [kickedRunId, setKickedRunId] = useState(null);
  /** TaskIds the supervisor just resolved locally; we suppress the
   *  reducer's pendingApproval until APPROVAL_RESOLVED echoes back. */
  const [resolvedTaskIds, setResolvedTaskIds] = useState([]);
  /** Ephemeral system rows for events that have no AppSync envelope (delegate
   *  success, claim error, etc.). They render below the latest turn. */
  const [localSystemEvents, setLocalSystemEvents] = useState([]);
  // ``ttlMs``: transitory notices (e.g. "agent disabled — next run") auto-dismiss after a few seconds
  // instead of piling up. Errors omit ttl → stay sticky so they aren't missed.
  const pushLocalSystemEvent = useCallback((text, opts = {}) => {
    const id = `sys-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setLocalSystemEvents((prev) => [...prev, { kind: 'system', id, text }]);
    const ttl = Number(opts.ttlMs) || 0;
    if (ttl > 0) {
      setTimeout(() => setLocalSystemEvents((prev) => prev.filter((e) => e.id !== id)), ttl);
    }
  }, []);
  const [sending, setSending] = useState(false);
  // BOOTING STATUS (the "void" between Send and the first stream event): a run's SFN cold-start +
  // Lead router + first agent boot (+ Textract on artifacts) is a real 5–30s gap with zero stream
  // traffic. These drive a live "run booting" card so the room never looks frozen. ``bootSecs`` is
  // the elapsed clock since Send (ticks each second); ``bootAccepted`` flips true once ``poolExecute``
  // resolves (StartExecution accepted → "waking agents"); ``bootArtifacts`` is the artifact count in
  // THIS send (shows "Preparing N artifact(s)…"). All reset when the first live event clears sending.
  const [bootAccepted, setBootAccepted] = useState(false);
  const [bootArtifacts, setBootArtifacts] = useState(0);
  // NON-BLOCKING upload-context chip: shown on a send that carries attachments (the room's
  // room-uploads L0/L1/L2 is consolidated ASYNC by the worker, available to agents from the next
  // run). Purely informational — it does NOT gate Send or the run. Auto-dismisses on a timer.
  const [contextBuilding, setContextBuilding] = useState(null);  // { label } | null
  const [bootSecs, setBootSecs] = useState(0);   // elapsed seconds since Send; ticks once a second
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
  const [membersDrawerOpen, setMembersDrawerOpen] = useState(false);
  // Room tab control (Activities=0, Context=1, Connections=2, Workspace=3,
  // Executions=4). Controlled so the drawer's "Create in Connections" can switch
  // to the Connections tab. A one-shot ``connectionsAutoOpenTool`` tells the
  // room SecretsCatalog which creator to open. Forward-only (the drawer unmounts
  // on close, so there's no auto-return/assign — the user re-opens the drawer
  // and picks the now-created connection from the list).
  const ROOM_CONNECTIONS_TAB_INDEX = 2;
  const ROOM_EXECUTIONS_TAB_INDEX = 4;
  // TASKS is appended LAST (only for project rooms): Activities0 Context1 Connections2 Workspace3
  // Executions4 Scheduler5 Tasks6. Used to tell the kept-mounted TasksTab when it's the ACTIVE tab
  // so it can refresh its data on return without clobbering the user's selection.
  // THE ROOM'S MODE (MODES.md §7): Tasks + Project map exist only once the room is in Plan / Run / Verify — so
  // their index is -1 in Context (nothing addresses a tab that isn't there). See ``planTabsOn`` below.
  // OFF until the room's mode is KNOWN to be Plan / Run / Verify — never render-then-remove (a Context room never
  // mounts TasksTab / Project map, nor fetches their data).
  // Stamped with the ROOM it was decided for: another room reads OFF at once (no stale tabs for a render), a
  // reconnect keeps the same room → no blink.
  const [planTabs, setPlanTabs] = useState({ room: '', on: false });
  const planTabsOn = planTabsShown(planTabs, `${networkId}/${poolId}`);
  const tasksTabIndex = planTabsOn ? 6 : -1;
  // Deep-link tab → initial index (only ``executions`` is wired today; extend the
  // map as more tabs get deep-linked). Default Activities (0).
  const ROOM_TAB_INDEX_BY_NAME = { executions: ROOM_EXECUTIONS_TAB_INDEX };
  const [roomTabIndex, setRoomTabIndex] = useState(
    () => ROOM_TAB_INDEX_BY_NAME[String(initialTab || '').toLowerCase()] ?? 0,
  );
  const [connectionsAutoOpenTool, setConnectionsAutoOpenTool] = useState(null);
  const navigateRoomToConnections = useCallback(({ tool } = {}) => {
    setConnectionsAutoOpenTool(tool || null);
    setMembersDrawerOpen(false); // close the drawer so the Connections tab is visible
    setRoomTabIndex(ROOM_CONNECTIONS_TAB_INDEX);
  }, []);
  const [memberCount, setMemberCount] = useState(null);
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  /** Network designer sub (GET /networks/{id} ``userId``) — gates Edit/Delete on NETWORK-scoped registry rows. */
  const [networkOwnerUserId, setNetworkOwnerUserId] = useState('');
  // Economy/Test mode (frozen __roomContext__ 'Shared' setting for this room's release).
  // DEFAULT ON — matches the backend resolver (unset → on). Drives the "Economy" header chip.
  const [economyMode, setEconomyMode] = useState(true);
  // VERBOSE (cost telemetry in run blocks) — DEFAULT OFF, the mirror of economyMode above.
  // Read from the SAME frozen __roomContext__ fetch so it reflects what the room RUNS with,
  // not a live draft the user hasn't released.
  const [verboseMode, setVerboseMode] = useState(false);
  // Manual stage cap (frozen __roomContext__ Shared setting) — 0 = Auto (no cap). When >0, agents
  // deeper than N stages are DIMMED in the room tree/roster ("part of the team, disabled this
  // release"). Read from the SAME frozen fetch as economyMode below.
  const [stageDepthCap, setStageDepthCap] = useState(0);
  // PROJECT (phased) network — the archiver stamps ``phases`` on the released network row.
  // Non-empty → this is a milestone-driven PROJECT: the composer shows the Plan/Task/Wave selector +
  // the deterministic next-step proposal + the Approve band (PROJECT-NETWORKS.md §8). '' / absent →
  // transactional. (The old LLM "Suggest next" button was retired in favour of the deterministic path.)
  // legacy floor: the release carries milestones (``phases``) — the room STAGE (below) is the answer (PRODUCT.md §2)
  const [phasesProject, setIsProject] = useState(false);
  // PROJECT PLAN STATE (Unit F) — the canonical __planner__ ledger surfaced for the composer's
  // task selector + finalize gate. ``finalized`` gates a normal run; ``readyTasks`` is the ready
  // set ({id,title,leadAgent,milestone}); ``currentMilestone`` labels it. Fetched only for
  // project rooms; a bump refetches after a run settles (readiness moves as tasks complete).
  const [planState, setPlanState] = useState({
    finalized: true, readyTasks: [], currentMilestone: '',
    // Milestone roadmap fields (already in GET /tasks; captured so the composer can propose a
    // deterministic next prompt + surface an inline "Approve {milestone}" affordance).
    milestones: [], planningCursor: '', approvedMilestones: [], allTasks: [],
  });
  // the journey default for a room whose mode was never set — computed from SHARED data, so it's the same for
  // everyone: no plan → Context, a plan but nothing runnable → Plan, something ready → Run.
  const journeyMode = journeyModeFor(planState);   // a new room's template skeleton alone stays Context
  // Bump to force a plan-state re-fetch (e.g. after an inline milestone approve) without waiting
  // for a run to settle. Paired with the /tasks effect's dep list.
  const [planStateBump, setPlanStateBump] = useState(0);
  // ASYNC PLAN-UPDATE (2026-09-05): true between a PLAN_UPDATE_RUNNING beacon (f.synth fired the async
  // plan-update lambda) and the terminal PLAN_UPDATED (done) → drives a 'plan updating…' pending state.
  const [planUpdating, setPlanUpdating] = useState(false);
  // SHARED NETWORK WORKSPACE (CORE-AGENTS.md §4.4): the frozen ``__roomContext__``
  // defaultWorkspaceId. Non-empty → the network assigned a shared workspace, pinned
  // room-wide at release + LOCKED in the drawer (the owner can't swap it). '' → the
  // room's own bootstrap workspace, freely editable.
  const [networkWorkspaceId, setNetworkWorkspaceId] = useState('');
  // the room workspace (CONTEXT-DOCS §H1 — where Do outputs are read from)
  const roomWs = useRoomWorkspace(networkId, poolId, networkWorkspaceId);
  // §13: the Lead this room links to ('' for a Lead/standalone). Drives "Shared from Lead"
  // labelling so a linked room's shared workspace isn't mislabeled as a network workspace.
  const [leadPoolId, setLeadPoolId] = useState('');
  // ROOM-SCOPES Phase 1 — per-room agent disables ({agentId → 'branch'|'self'}). Loaded once per
  // room open, refreshed on POOL_AGENT_DISABLE_CHANGED (see below), and optimistically patched on
  // local mutations. Dims the tree nodes (branch = whole subtree) + roster rows for THIS room only.
  const [agentDisables, setAgentDisables] = useState({});
  // TWO-TIER source per disabled agent: {agentId: 'frozen'|'room'}. 'frozen' = the room's
  // immutable carve (owner can't enable — different color + no enable action); 'room' =
  // owner's own toggleable disable. Parallel to agentDisables so the {agentId:mode} contract
  // ManifestTreeFlow/mergeRoster consume stays unchanged.
  const [agentDisableSources, setAgentDisableSources] = useState({});
  // RESULT REUSE (RESULT-REUSE.md) — per-agent "Reuse last result" marks for THIS room
  // ({agentId → {capturedMilestone, capturedRunId}}). Milestone-bounded + auto-cleared server-side;
  // here it just rings the node + flips the menu to "Stop reusing". Project/phased networks only.
  const [agentReuses, setAgentReuses] = useState({});
  const refreshAgentReuses = useCallback(() => {
    if (!networkId || networkId === 'new' || !poolId) return;
    listPoolAgentReuses(networkId, poolId)
      .then((data) => {
        const rows = Array.isArray(data?.reuses) ? data.reuses : [];
        const next = {};
        for (const r of rows) {
          if (r?.agentId) next[r.agentId] = {
            capturedMilestone: r.capturedMilestone || '',
            capturedRunId: r.capturedRunId || '',
          };
        }
        setAgentReuses(next);
      })
      .catch(() => { /* best-effort: an unread reuse list just renders no rings */ });
  }, [networkId, poolId]);
  // ROOM ALARMS (RELEASE-ALARMS.md §Rooms) — the room group's live integration-test alarms. The
  // dynamic right-aligned ``Alarm (N)`` tab renders ONLY when something's tripped (count > 0).
  const { alarms: roomAlarms, trippedCount: roomAlarmCount, reset: resetRoomAlarmFn,
          bump: bumpRoomAlarms } = useRoomAlarms({ networkId, poolId });
  // IMMEDIATE ALERT: a trip fires a failure-only ROOM_ALARM beacon on this room's channel — refetch
  // the moment it lands so the Alarm (N) tab appears INSTANTLY (the hook's poll is only the fallback).
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last && (last.eventType ?? last.type) === 'ROOM_ALARM') bumpRoomAlarms();
  }, [messages, bumpRoomAlarms]);
  // FROZEN network-designed carve ({agentId → 'branch'|'self'}) — each agent's ``disabledMode``
  // config field, captured into the release footprint and read back frozen (release = complete
  // freeze, like every other field). Populated from the frozen ``n.agents`` in the network-summary
  // effect below. Held in a ref so ``refreshAgentDisables`` can seed from it WITHOUT re-creating
  // the callback (which would churn its deps). The room-owner DISABLE rows layer ON TOP of this —
  // the exact same union the runtime does in execute_network._resolve_disabled_agents.
  const frozenDisablesRef = useRef({});
  const refreshAgentDisables = useCallback(() => {
    if (!networkId || networkId === 'new' || !poolId) return;
    // The server returns only rows EFFECTIVE for the room's current release version
    // (VERSION-FROZEN topology — older-version carves are inert), so the map we build
    // is already version-scoped; no client-side version filtering needed.
    listPoolAgentDisables(networkId, poolId)
      .then((data) => {
        const rows = Array.isArray(data?.agents) ? data.agents : [];
        const next = {};
        const src = {};
        // 1. FROZEN carve first — the network-designed base topology this room was released on.
        //    Locked ('frozen' source): the owner can't re-enable it, only a re-release changes it.
        for (const [aid, mode] of Object.entries(frozenDisablesRef.current || {})) {
          if (aid && (mode === 'branch' || mode === 'self')) {
            next[aid] = mode;
            src[aid] = 'frozen';
          }
        }
        // 2. Room-owner DISABLE rows overlay on top (owner's own toggleable tier).
        for (const r of rows) {
          if (r?.agentId && (r.mode === 'branch' || r.mode === 'self')) {
            next[r.agentId] = r.mode;
            src[r.agentId] = (r.source === 'frozen') ? 'frozen' : 'room';
          }
        }
        setAgentDisables(next);
        setAgentDisableSources(src);
      })
      .catch(() => { /* best-effort: an unread disable list just renders nothing dimmed */ });
  }, [networkId, poolId]);
  useEffect(() => {
    // Clear + re-list on room switch AND on version change: a room upgrade RESETS the
    // carve (old-version rows go inert), so the prior version's dimming must not linger.
    setAgentDisables({});
    setAgentDisableSources({});
    refreshAgentDisables();
    setAgentReuses({});
    refreshAgentReuses();
  }, [refreshAgentDisables, refreshAgentReuses, releaseVersion]);
  // Releases list — used to render the room's MAJOR.MINOR version label
  // (e.g. "1.10") on the chip next to the room title.
  const [networkReleases, setNetworkReleases] = useState([]);
  useEffect(() => {
    if (!networkId) return undefined;
    let cancelled = false;
    listNetworkReleases(networkId, { limit: 50 })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.releases) ? data.releases : [];
        setNetworkReleases(list);
      })
      .catch(() => {
        if (cancelled) return;
        setNetworkReleases([]);
      });
    return () => { cancelled = true; };
  }, [networkId, releaseVersion]);
  const versionLabel = useMemo(() => {
    const rv = Number(releaseVersion);
    if (!Number.isFinite(rv) || rv < 1) return '';
    return displayVersionLabel(rv, networkReleases);
  }, [networkReleases, releaseVersion]);
  useEffect(() => {
    let cancelled = false;
    if (!networkId || networkId === 'new') return undefined;
    (async () => {
      try {
        // Pass poolId so a release-pinned room gets its FROZEN __roomContext__
        // (the economyMode the room actually runs with, not the live draft).
        const n = await fetchNetworkSummary(networkId, { poolId });
        const uid = String(n?.userId || '').trim();
        if (!cancelled) setNetworkOwnerUserId(uid || '');
        if (!cancelled) setLeadPoolId(String(n?.leadPoolId || '').trim());
        // Economy/Test mode from the (frozen) Shared row — DEFAULT ON: unset/absent → on,
        // off ONLY on an explicit opt-out. Mirrors release_resolution._is_economy_on.
        if (!cancelled) setEconomyMode(economyFromAgents(n?.agents));
        if (!cancelled) setVerboseMode(verboseFromAgents(n?.agents));
        // PROJECT detection: the released row carries ``phases`` (archiver2 stamp) for a
        // milestone-driven network. Non-empty list → enable the project composer surfaces.
        if (!cancelled) {
          const _ph = n?.phases || n?.Phases;
          setIsProject(Array.isArray(_ph) && _ph.length > 0);
        }
        // Manual stage cap from the SAME frozen Shared row (0 = Auto). Drives the dimmed
        // "disabled this release" agents in the tree + roster.
        if (!cancelled) {
          const _rc = (n?.agents && typeof n.agents === 'object') ? n.agents.__roomContext__ : null;
          const _cap = Math.max(0, Number.parseInt(_rc?.stageDepthCap, 10) || 0);
          setStageDepthCap(_cap);
          // SHARED NETWORK WORKSPACE (CORE-AGENTS.md §4.4): the workspace the network
          // creator assigned on the Shared row. When set, it's pinned room-wide at release
          // and the room owner can't swap it — the drawer shows it read-only. Thread the id
          // down so the drawer can lock its workspace picker.
          setNetworkWorkspaceId(String(_rc?.defaultWorkspaceId || '').trim());
        }
        // FROZEN network-designed carve — each agent's frozen ``disabledMode`` (release =
        // complete freeze). Because this room passed ``poolId``, ``n.agents`` IS the release
        // footprint, so a network-level disable released into this version shows up here. Seed
        // the ref + re-list so the frozen carve dims the tree/roster the instant the room loads,
        // BEFORE any run — unioned with the room-owner rows in refreshAgentDisables.
        if (!cancelled) {
          const frozen = {};
          const _agents = (n?.agents && typeof n.agents === 'object') ? n.agents : {};
          for (const [aid, cfg] of Object.entries(_agents)) {
            if (aid === '__roomContext__') continue;
            const m = String(cfg?.disabledMode || '').trim().toLowerCase();
            if (m === 'branch' || m === 'self') frozen[aid] = m;
          }
          frozenDisablesRef.current = frozen;
          refreshAgentDisables();
        }
      } catch {
        if (!cancelled) setNetworkOwnerUserId('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId, poolId, refreshAgentDisables]);
  /** Stable for ``PoolSettingsDrawer`` — inline lambdas there made ``refresh`` churn every
   *  render and re-trigger ``GET .../members`` in a tight loop. */
  const handleMemberCountFromRoster = useCallback((count) => {
    setMemberCount(count);
  }, []);
  const handleTriggerModeFromDrawer = useCallback((next) => {
    setTriggerMode(normalizeTriggerMode(next));
  }, []);
  /** Pool runtime governance — currently just trigger mode. Defaults to
   *  HYBRID so the prompt-box gating doesn't go pessimistic during the
   *  initial members-fetch (matches pre-Phase-B behavior for legacy pools
   *  on disk that haven't been migrated to a stored TriggerMode yet). */
  const [triggerMode, setTriggerMode] = useState(TRIGGER_HYBRID);
  const bottomRef = useRef(null);

  /** Hydrate from REST history (same shape as live AppSync envelopes). The
   *  reducer treats history + live as one chronological buffer; the live
   *  hook (`useAgentChannel`) already dedupes by eventId on top so a
   *  reconnect won't double-count. */
  // A run START is a top-level USER_PROMPT or non-sub-execution EXECUTION_STARTED (a sub-exec is a
  // per-agent .sync child on universal-worker/-builder — NOT a run boundary). Used to count how
  // many WHOLE runs we've paged in so each loaded run arrives complete (prompt + finals + summary).
  const isRunStartEnvelope = useCallback((env) => {
    const t = envelopeEventType(env);
    if (t === 'USER_PROMPT') return true;
    if (t !== 'EXECUTION_STARTED') return false;
    const arn = String(env?.payload?.stateMachineArn || env?.stateMachineArn || '');
    return !_SUBEXEC_HISTORY_MARKERS.some((m) => arn.includes(m));
  }, []);

  /** Load ONE more complete run into history (newest-first). Pages the channel feed from the
   *  current cursor until it has seen ONE additional top-level run START (so the run loads whole),
   *  or the feed ends. Called once on mount for the latest run, then by the "Load previous run"
   *  button. Small pages (~150) keep each request fast; a hard page backstop prevents a runaway. */
  const loadMoreHistory = useCallback(async () => {
    if (!networkId || !poolId) return;
    setHistoryPaging(true);
    let cursor = historyCursor;
    const startCount = historyRunStartsRef.current.size;
    const collected = [];
    let nextCursor = null;
    let pages = 0;
    const MAX_PAGES = 4;   // backstop: 4 × 150 = 600 events for one run — plenty even for a wide run
    try {
      let more = true;
      while (more) {
        const resp = await fetchNetworkChannelEvents(networkId, { limit: 150, poolId, cursor });
        const page = Array.isArray(resp.events) ? resp.events : [];
        collected.push(...page);
        for (const env of page) {
          if (isRunStartEnvelope(env)) {
            const rid = String(env?.executionId || env?.correlationId || '').trim();
            if (rid) historyRunStartsRef.current.add(rid);
          }
        }
        nextCursor = resp.nextCursor || null;
        cursor = nextCursor;
        pages += 1;
        // Stop once we've pulled in ONE more run's start than we had, or the feed is exhausted.
        const gotOneMoreRun = historyRunStartsRef.current.size > startCount;
        if (!nextCursor || gotOneMoreRun || pages >= MAX_PAGES) more = false;
      }
      // Append older (newest-first) events beneath what we have; reducer re-sorts chronologically.
      // combinedEnvelopes dedupes by eventId, so a boundary overlap can't double-count.
      if (collected.length) {
        setHistoryEnvelopes((prev) => [...prev, ...collected]);
      }
      setHistoryCursor(nextCursor);
      setHistoryHasMore(!!nextCursor);
    } catch {
      /* non-fatal */
    } finally {
      setHistoryPaging(false);
      setHistoryLoaded(true);
    }
  }, [networkId, poolId, historyCursor, isRunStartEnvelope]);

  // On room open / network switch: reset history + load ONLY the latest run (fast). Older runs
  // load on demand via the "Load previous run" button — eager-loading N runs stacked several ~4s
  // requests and made the room slow to open.
  useEffect(() => {
    // DIAG (temporary): make the history load OBSERVABLE. A silent early-return here (missing
    // poolId) is the most likely reason a network switch shows "no API call" in the browser — the
    // effect bails before the fetch. Log the entry + the bail so it can't hide.
    if (!networkId || !poolId) {
      // eslint-disable-next-line no-console
      console.warn('[room-history] SKIPPED history load — no fetch will fire.',
        { networkId, poolId, reason: !networkId ? 'no networkId' : 'no poolId' });
      return undefined;
    }
    // eslint-disable-next-line no-console
    console.info('[room-history] loading last run via GET /channel-events', { networkId, poolId });
    setHistoryEnvelopes([]);
    setHistoryLoaded(false);
    setHistoryCursor(null);
    setHistoryHasMore(false);
    historyRunStartsRef.current = new Set();
    setKickedRunId(null);   // a kick-seeded run id belongs to the room we just left — never carry it across
    let cancelled = false;
    (async () => {
      const collected = [];
      let cursor;
      let nextCursor = null;
      let pages = 0;
      const MAX_PAGES = 4;
      try {
        let more = true;
        while (more) {
          // eslint-disable-next-line no-console
          console.info('[room-history] fetch page', { networkId, poolId, cursor: cursor || null, pages });
          const resp = await fetchNetworkChannelEvents(networkId, { limit: 150, poolId, cursor });
          if (cancelled) return;
          const page = Array.isArray(resp.events) ? resp.events : [];
          // eslint-disable-next-line no-console
          console.info('[room-history] page result', { events: page.length, nextCursor: resp.nextCursor || null });
          collected.push(...page);
          for (const env of page) {
            if (isRunStartEnvelope(env)) {
              const rid = String(env?.executionId || env?.correlationId || '').trim();
              if (rid) historyRunStartsRef.current.add(rid);
            }
          }
          nextCursor = resp.nextCursor || null;
          cursor = nextCursor;
          pages += 1;
          // Stop as soon as we have ONE whole run (its start seen), or the feed ends.
          if (!nextCursor || historyRunStartsRef.current.size >= 1 || pages >= MAX_PAGES) more = false;
        }
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.info('[room-history] loaded', { totalEvents: collected.length, runsFound: historyRunStartsRef.current.size, hasMore: !!nextCursor });
        setHistoryEnvelopes([...collected].reverse());   // newest-first → chronological
        setHistoryCursor(nextCursor);
        setHistoryHasMore(!!nextCursor);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[room-history] FETCH FAILED — the call fired but errored (auth/404/CORS?):', err);
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [networkId, poolId, isRunStartEnvelope]);

  /** ``memberCount`` + initial ``triggerMode`` from the same ``GET .../members`` that
   *  ``PoolSettingsDrawer`` runs on mount (see there). Avoids duplicate REST calls. */

  /** React to live ``POOL_SETTINGS_CHANGED`` envelopes (creator flips the
   *  trigger mode from the Settings drawer or via Overwatch). The reducer
   *  doesn't need to know about settings — we just sip the latest value
   *  off the envelope buffer ourselves. */
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const env = messages[i];
      if (envelopeEventType(env) !== 'POOL_SETTINGS_CHANGED') continue;
      const tm = env?.payload?.settings?.triggerMode;
      if (tm) {
        setTriggerMode(normalizeTriggerMode(tm));
      }
      break;
    }
  }, [messages]);

  /** PLAN LIVE (§2.5g) + ASYNC PLAN-UPDATE — moved below ``combinedEnvelopes``: the beacon state is
   *  derived from the MERGED history+live buffer (replay-safe), not the live buffer alone. See the
   *  ``latestPlanBeacon`` effect after the buffer merge. */
  const lastPlanEventIdRef = useRef(null);

  /** §13 cross-room live reflect: a ``CONNECTION_CHANGED`` beacon (a shared connection was created/
   *  edited/deleted in THIS room OR a sibling room of the instance — fanned out by the backend to
   *  every instance channel) → bridge to the existing ``stepflow-secret-registry-changed`` refetch so
   *  the Connections tab/drawer + the credential gate re-pull. SIGNAL only; de-duped by eventId. */
  const lastConnEventIdRef = useRef(null);
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const env = messages[i];
      if (envelopeEventType(env) !== 'CONNECTION_CHANGED') continue;
      const eid = env?.eventId || `${env?.ts || ''}:${env?.payload?.tool || ''}`;
      if (eid && eid === lastConnEventIdRef.current) return;   // already handled
      lastConnEventIdRef.current = eid;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('stepflow-secret-registry-changed',
          { detail: { source: 'connection-changed-peer' } }));
      }
      return;
    }
  }, [messages]);

  /** §13 cross-room live reflect: a ``WORKSPACE_CHANGED`` beacon (the SHARED workspace pointer was
   *  pinned/cleared in THIS room OR a sibling room of the instance — the workspace is one tree shared
   *  Lead↔linked) → bridge to ``stepflow-secret-registry-changed`` so the room drawer + Connections
   *  workspace row re-pull. SIGNAL only; de-duped by eventId. */
  const lastWsEventIdRef = useRef(null);
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const env = messages[i];
      if (envelopeEventType(env) !== 'WORKSPACE_CHANGED') continue;
      const eid = env?.eventId || `${env?.ts || ''}:${env?.payload?.reason || ''}`;
      if (eid && eid === lastWsEventIdRef.current) return;   // already handled
      lastWsEventIdRef.current = eid;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('stepflow-secret-registry-changed',
          { detail: { source: 'workspace-changed-peer' } }));
      }
      return;
    }
  }, [messages]);

  /** §13 cross-room live reflect: a ``CONTEXT_CHANGED`` beacon (the SHARED, Lead-anchored context
   *  changed in THIS room OR a sibling — a room upload consolidated, or a tier was SYNC'd/UNSYNC'd;
   *  Lead + linked read ONE context set) → dispatch ``stepflow-context-changed`` so the Context tab +
   *  View-Context drawer re-fetch live. The context peer of the WORKSPACE_CHANGED bridge above.
   *  SIGNAL only; de-duped by eventId. */
  const lastCtxEventIdRef = useRef(null);
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const env = messages[i];
      if (envelopeEventType(env) !== 'CONTEXT_CHANGED') continue;
      const eid = env?.eventId || `${env?.ts || ''}:${env?.payload?.activeTier || ''}`;
      if (eid && eid === lastCtxEventIdRef.current) return;   // already handled
      lastCtxEventIdRef.current = eid;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('stepflow-context-changed',
          { detail: { source: 'context-changed-peer', payload: env?.payload || null } }));
      }
      return;
    }
  }, [messages]);

  /** React to live ``POOL_AGENT_DISABLE_CHANGED`` envelopes (a peer room owner flipped an agent
   *  from THEIR tree). Patch the map in place from the payload when it's complete (cheap — mirrors
   *  the drawer's POOL_AGENT_SUPERVISOR_CHANGED handling); fall back to a re-list otherwise.
   *  De-dupe by eventId so one mutation doesn't trigger N patches across renders. */
  const lastDisableEventIdRef = useRef(null);
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last?.eventType !== 'POOL_AGENT_DISABLE_CHANGED') return;
    const id = last?.eventId;
    if (!id || id === lastDisableEventIdRef.current) return;
    lastDisableEventIdRef.current = id;
    // A peer flipped an agent → the SERVER disable state already changed, so the credential run-gate
    // must recompute (a now-disabled agent's tool stops blocking; a re-enabled one re-blocks). This
    // effect is declared above ``refreshMcpGate``, so signal via the shared event its listener honors.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('stepflow-secret-registry-changed', { detail: { source: 'agent-disable-peer' } }));
    }
    const payload = last?.payload || {};
    const agentId = payload.agentId;
    const action = String(payload.action || '').toUpperCase();
    // RESULT REUSE shares this beacon (mode='reuse'). Reconcile the reuse map, not the disable map.
    if (payload.mode === 'reuse') {
      refreshAgentReuses();
      return;
    }
    if (!agentId || (action !== 'SET' && action !== 'CLEARED')) {
      refreshAgentDisables();   // defensive: can't reconcile locally → re-list
      return;
    }
    setAgentDisables((prev) => {
      const next = { ...prev };
      if (action === 'CLEARED') delete next[agentId];
      else if (payload.mode === 'branch' || payload.mode === 'self') next[agentId] = payload.mode;
      else return prev;
      return next;
    });
    // Live disable/enable events are only ever the toggleable 'room' tier (frozen carves
    // are written at room-create, never via this event). SET → 'room'; CLEARED → drop.
    setAgentDisableSources((prev) => {
      const next = { ...prev };
      if (action === 'CLEARED') delete next[agentId];
      else next[agentId] = 'room';
      return next;
    });
  }, [messages, refreshAgentDisables, refreshAgentReuses]);

  /** Combined chronological envelope buffer fed to the reducer. We dedupe
   *  by eventId so a live message that matches a REST-hydrated row wins
   *  the live timestamp and metadata. */
  const messagesKey = envelopeBufferKey(messages);
  const historyKey = envelopeBufferKey(historyEnvelopes);
  /** Prefer live ``BUILDER_INPUT_WAIT`` (non-persistent). After reconnect or
   *  reload, Dynamo-backed hydration merges in below (see ``resolvedBuilderCliWait``).
   *  Auto-dismiss: if a LOG_LINE with stream 'universal_builder' appears AFTER the
   *  last BUILDER_INPUT_WAIT, the builder has resumed and the wait is stale. */
  const activeBuilderCliWait = useMemo(() => {
    if (!Array.isArray(messages) || messages.length === 0) return null;
    // Find the latest WAIT.
    let waitIdx = -1;
    let waitEnv = null;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const env = messages[i];
      if (!env || envelopeEventType(env) !== AGENT_EVENTS.BUILDER_INPUT_WAIT) continue;
      waitIdx = i;
      waitEnv = env;
      break;
    }
    if (!waitEnv) return null;
    const pl = waitEnv.payload && typeof waitEnv.payload === 'object' ? waitEnv.payload : {};
    const eventId = String(waitEnv.eventId || '');
    const runId = String(pl.runId || waitEnv.executionId || waitEnv.correlationId || '').trim();
    if (!eventId || !runId) return null;


    // Match RESOLVED to WAIT by ``waitEventId`` first (precise — covers
    // multi-turn sessions where the same runId hosts many WAITs and
    // out-of-order AppSync delivery). Scan the entire buffer (not only
    // post-WAIT) since a RESOLVED could arrive before its WAIT due to
    // reconnect replay ordering.
    //
    // Fallback: if RESOLVED has no ``waitEventId`` (legacy backend), fall
    // back to the previous heuristic (RESOLVED for same runId appearing
    // *after* the WAIT in chronological order).
    for (let j = 0; j < messages.length; j += 1) {
      const env = messages[j];
      if (!env || envelopeEventType(env) !== AGENT_EVENTS.BUILDER_INPUT_RESOLVED) continue;
      const rPl = env.payload && typeof env.payload === 'object' ? env.payload : {};
      const rWaitId = String(rPl.waitEventId || '').trim();
      if (rWaitId) {
        if (rWaitId === eventId) return null;
        continue;
      }
      // Legacy RESOLVED — only consider if it came after the WAIT.
      if (j <= waitIdx) continue;
      const rRun = String(rPl.runId || env.executionId || env.correlationId || '').trim();
      if (rRun && rRun === runId) return null;
    }
    const promptStr = typeof pl.prompt === 'string' ? pl.prompt : '';
    const interaction = pl.interaction && typeof pl.interaction === 'object' ? pl.interaction : null;
    return { eventId, runId, prompt: promptStr, interaction };
  }, [messages]);

  /** Set of WAIT eventIds that have been answered (any user). Built from
   *  BUILDER_INPUT_RESOLVED envelopes in the live message buffer; matched by
   *  ``waitEventId`` in the payload. Used by the inline "Awaiting approval"
   *  indicator in TurnGroup so peer browsers dismiss it as soon as anyone
   *  responds — same dismiss semantics as the alert band. */
  const resolvedWaitEventIds = useMemo(() => {
    const out = new Set();
    if (!Array.isArray(messages)) return out;
    for (const env of messages) {
      if (!env || envelopeEventType(env) !== AGENT_EVENTS.BUILDER_INPUT_RESOLVED) continue;
      const rPl = env.payload && typeof env.payload === 'object' ? env.payload : {};
      const rWaitId = String(rPl.waitEventId || '').trim();
      if (rWaitId) out.add(rWaitId);
    }
    return out;
  }, [messages]);

  /** When live AppSync misses ``BUILDER_INPUT_WAIT`` after a refresh/reconnect,
   *  hydrate from Dynamo (``stream_runner`` seeds ``inputWaitActive``). */
  const [hydratedBuilderCliWait, setHydratedBuilderCliWait] = useState(null);

  useEffect(() => {
    if (activeBuilderCliWait) {
      setHydratedBuilderCliWait(null);
    }
  }, [activeBuilderCliWait]);

  /** Dismiss WAIT modals locally after send/cancel — same ``eventId`` won't reopen until a new WAIT arrives. */
  const [handledBuilderWaitIds, setHandledBuilderWaitIds] = useState([]);
  const handledBuilderWaitSet = useMemo(() => new Set(handledBuilderWaitIds), [handledBuilderWaitIds]);
  const markBuilderWaitHandled = useCallback((eventId) => {
    if (!eventId) return;
    setHandledBuilderWaitIds((prev) => (prev.includes(eventId) ? prev : [...prev, eventId]));
  }, []);

  // The de-duplicated, chronological event buffer feeding the room reducer. It is DELIBERATELY
  // UNFILTERED: everything derived from it — ``uiState`` / ``currentRunId`` / ``lastExecution`` —
  // gates the composer freeze + the run-active guard, so hiding events here (an earlier fresh-run
  // "clear on Enter" attempt) reset the run-state mid-run and un-froze the prompt bar. The fresh-run
  // visual clear now happens at the RENDER layer only (``runBlocks``/dock roster gated on ``sending``),
  // never on this buffer.
  const combinedEnvelopes = useMemo(
    () => mergeRoomEnvelopes(historyEnvelopes, messages),
    // The dedupe sets re-run whenever the buffers' lengths/last id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [historyKey, messagesKey],
  );

  // LANE-B log-tail backfill (REALTIME-HUB §0.1): builder LOG_LINEs stream ephemerally, so a mid-build
  // join saw a blank log. We fetch the bounded tail per running builder (once) and PREPEND it as
  // synthetic LOG_LINE envelopes — the reducer replays them first (setting each builder's lineNo
  // high-water), then live lines with a higher lineNo append; duplicates are dropped. Purely additive.
  const [logTailEnvelopes, setLogTailEnvelopes] = useState([]);
  // DIRECT WORK (MODES.md §6): direct runs (Context + agent, ``dr-…`` ids) are PARALLEL to the network run, so
  // they are split off BEFORE the room reducer — the ONE exception to the unfiltered-buffer rule above, and safe
  // for the same reason it's needed: no direct envelope belongs to a network run, and letting its prompt in
  // would reset the live network run's block. Each direct run is reduced on its own (directRuns below).
  const directSplit = useMemo(() => splitDirectEnvelopes(
    logTailEnvelopes.length ? [...logTailEnvelopes, ...combinedEnvelopes] : combinedEnvelopes),
  [combinedEnvelopes, logTailEnvelopes]);
  const room = useMemo(
    () => reduceAgentRoomState(directSplit.main, { optimistic: optimisticBubbles, pendingRunId: kickedRunId }),
    [directSplit, optimisticBubbles, kickedRunId],
  );
  const directRuns = useMemo(
    () => directRunViews(directSplit.byRun, (envs) => reduceAgentRoomState(envs)),
    [directSplit],
  );
  const [directModes, setDirectModes] = useState({});   // runId → ask|do (from the start reply)
  // THE ROOM'S MODE (MODES.md §7): ONE mode for everyone in the room — read on load / reconnect / focus, followed
  // live (ROOM_MODE_CHANGED). ``mode: null`` = never set → the journey default below (same for everyone).
  // Stamped with its ROOM (``room``): a switch reads unknown at once, a late reply for the previous room is dropped,
  // and a reconnect re-reads WITHOUT wiping it (no Context/tab flash while it reloads).
  const roomKey = `${networkId}/${poolId}`;
  const roomKeyRef = useRef(roomKey);
  roomKeyRef.current = roomKey;
  const [roomModeState, setRoomModeState] = useState({ mode: null, canSwitch: false, loaded: false, room: '' });
  // IS THIS ROOM PLANNING? (PRODUCT.md §2, R1b) — the room's own stage (server: room_stage, milestones = its legacy
  // floor), the network's phases only until the room's mode has loaded.
  const isProject = phasesProject || (roomModeState.loaded && roomModeState.room === `${networkId}/${poolId}`
    && roomModeState.stage === 'plan');
  const loadRoomMode = useCallback(() => {
    if (!networkId) return;
    const key = `${networkId}/${poolId}`;
    getRoomMode(networkId, { pool: poolId })
      .then((r) => { if (roomKeyRef.current !== key) return;
        setRoomModeState({ mode: r?.mode || null, canSwitch: !!r?.canSwitch, loaded: true, room: key,
          roomType: r?.roomType === 'context' ? 'context' : 'project',
          setByName: r?.setByName || '',
          // THE ROOM'S MODEL (MODES.md §3e): '' = each agent's own; owner-only
          contextModel: r?.contextModel || '', canSetModel: !!r?.canSetModel,
          contextModelSetByName: r?.contextModelSetByName || '',
          // THE ROOM'S INTERNET SEARCH (MODES.md §10.9): default ON (absent → on), the room's run roles change it
          internetSearch: r?.internetSearch !== false, internetSearchDefault: r?.internetSearchDefault !== false,
          internetSearchSetByName: r?.internetSearchSetByName || '', canSetInternet: !!r?.canSetInternet,
          // THE ROOM'S STAGE (PRODUCT.md §2): converse → work → plan, earned by use; cap 'converse' = kept conversational
          stage: r?.stage || 'converse', stageCap: r?.stageCap || '', canSetStageCap: !!r?.canSetStageCap,
          roomPlan: r?.roomPlan || null, publishTarget: r?.publishTarget || null,
          planVocabulary: r?.planVocabulary || null,
          // EXPERT NETWORKS (EXPERT-NETWORKS.md §5): who reviews this room + what this viewer may change
          expertPlugins: r?.expertPlugins || null, expertCandidates: r?.expertCandidates || [],
          clientFacts: r?.clientFacts || [],
          canSetExperts: !!r?.canSetExperts, canSetNetworkExperts: !!r?.canSetNetworkExperts,
          expertFacts: r?.expertFacts || null,
          // SETUP PROPOSALS (EXPERT-NETWORKS.md §7c L3): platform steps the experts prepared for this room
          liaisonActions: r?.liaisonActions || null,
          // EXPERT ROOMS (EXPERT-ROOMS.md §4, X2): this room's part of an expert team + (Lead only) each Team's access
          expertPart: r?.expertPart || null, teamAccess: r?.teamAccess || null,
          // EXPERT SERVICES (EXPERT-ROOMS.md §5, X3): what the experts serving this room offer
          expertServices: r?.expertServices || [],
          // REQUESTS (EXPERT-ROOMS.md §6, X4): what this room asked + (an expert team's room) what it was asked
          expertRequests: r?.expertRequests || null,
          // CERTIFICATION (EXPERT-ROOMS.md §11, X7): the Lead room's checklist for the current release
          expertCertification: r?.expertCertification || null,
          // RULE CHANGES (EXPERT-NETWORKS.md §12): this expert room's adjustments to its rules
          ruleChanges: r?.ruleChanges || null,
          // ZERO-SKILL ONBOARDING (EXPERT-NETWORKS.md §7): the Expert Network's setup steps / the templates to start from
          onboarding: r?.onboarding || null, expertTemplates: r?.expertTemplates || [],
          // CONSULT (EXPERT-ROOMS.md §13): who this room may ask + (an expert's room) its queue — read on load too (it
          // was only set after a PUT, so @Expert had no names to offer until someone had asked once)
          expertConsults: r?.expertConsults || null,
          // TRAIN (EXPERT-ROOMS.md §15d): an expert's DEV room — new items + the suggestions to review
          expertTraining: r?.expertTraining || null,
          // APPROVED INTENTS (EXPERT-ROOMS.md §14d): this room's plans + (an expert's team room) what waits for it
          deployIntents: r?.deployIntents || null }); })
      .catch(() => { if (roomKeyRef.current === key) setRoomModeState((m) => ({ ...m, loaded: true, room: key })); });
  }, [networkId, poolId]);
  useEffect(() => { setRoomModeState({ mode: null, canSwitch: false, loaded: false, room: '' }); }, [networkId, poolId]);
  useEffect(() => { loadRoomMode(); }, [loadRoomMode, reconnectEpoch]);
  // STAND-IN (STAND-IN.md): MY stand-in here + my private drafts; a correction of an answer made FOR me teaches it
  const standin = useStandin(networkId, poolId, user?.sub || '', messages, reconnectEpoch);
  const _learned = useCallback((r) => {
    const l = r && r.learned;
    if (l && l.trigger) pushLocalSystemEvent(`Your stand-in learned: when ${l.trigger} → ${l.fix}`, { ttlMs: 8000 });
  }, [pushLocalSystemEvent]);
  const handleStandinAct = useCallback(async (op, payload) => {
    const r = await standin.run(op, payload);
    if (r?.message) setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, [r.message]) }));
    _learned(r);
    return r;
  }, [standin, _learned]);
  // CONFIRM A PENDING ANSWER (STAND-IN.md §9) — Approve · Modify (my wording; it learns) · Discard
  const handleConfirm = useCallback(async (m, decision, text = '') => {
    try {
      const r = await confirmAnswer(networkId, m, decision, text, { pool: poolId });
      if (r?.message) setDiscussion((d) => ({ ...d, messages: mergeDiscussion(d.messages, [r.message]) }));
      _learned(r);
      standin.reload();
    } catch (err) {
      pushLocalSystemEvent((err && err.message) || "Couldn't confirm that — try again.");
      throw err;
    }
  }, [networkId, poolId, _learned, standin, pushLocalSystemEvent]);
  // one Edit path: my own message → edit; an answer an agent made ON MY BEHALF → correct (it learns)
  const handleEditAny = useCallback(async (m, text) => {
    if (m && m.standin && m.standin.forSub && m.standin.forSub === user?.sub) {
      await handleStandinAct('correct', { messageId: m.id, sk: m.sk || '', text });
      return;
    }
    await handleEditMessage(m, text);
  }, [handleStandinAct, handleEditMessage, user?.sub]);
  const _canEditMsg = useCallback((x) => !!user?.sub && ((x.kind === 'user' && x.authorSub === user.sub)
    || (x.kind === 'agent' && !!x.standin && x.standin.forSub === user.sub)), [user?.sub]);
  useEffect(() => {
    const onFocus = () => loadRoomMode();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadRoomMode]);
  const _modeSeenRef = useRef(new Set());
  useEffect(() => {
    for (const m of messages) {
      const _et = m && (m.eventType ?? m.type);
      if (_et === 'ROOM_INTERNET_CHANGED') {       // MODES.md §10.9 — Internet search removed / added, live
        const mid = String(m.eventId || '');
        if (!mid || _modeSeenRef.current.has(mid)) continue;
        _modeSeenRef.current.add(mid);
        const q = m.payload || {};
        setRoomModeState((s) => ({ ...s, internetSearch: q.enabled !== false, internetSearchDefault: false,
          internetSearchSetByName: q.byName || '' }));
        if (q.by && q.by !== user?.sub && q.by !== user?.userId) {
          pushLocalSystemEvent(`${q.byName || 'A teammate'} ${q.enabled === false ? 'removed' : 'added'} Internet search for this room.`);
        }
        continue;
      }
      if (_et === 'ROOM_MODEL_CHANGED') {          // MODES.md §3e — the owner changed the room's model, live
        const mid = String(m.eventId || '');
        if (!mid || _modeSeenRef.current.has(mid)) continue;
        _modeSeenRef.current.add(mid);
        const q = m.payload || {};
        setRoomModeState((s) => ({ ...s, contextModel: q.tier || '', contextModelSetByName: q.byName || '' }));
        if (q.by && q.by !== user?.sub && q.by !== user?.userId) {
          pushLocalSystemEvent(`${q.byName || 'The room owner'} set the room's model to ${contextModelLabel(q.tier)}.`);
        }
        continue;
      }
      if (_et === 'ROOM_EXPERTS_CHANGED') {        // EXPERT-NETWORKS.md §5 — an Expert Network was plugged in / out
        const mid = String(m.eventId || '');
        if (!mid || _modeSeenRef.current.has(mid)) continue;
        _modeSeenRef.current.add(mid);
        loadRoomMode();
        continue;
      }
      if (_et === 'ROOM_PLAN_CHANGED') {           // PRODUCT.md §3 — a teammate drafted / approved the room's plan
        const mid = String(m.eventId || '');
        if (!mid || _modeSeenRef.current.has(mid)) continue;
        _modeSeenRef.current.add(mid);
        loadRoomMode();
        continue;
      }
      if (_et === 'ROOM_STAGE_CHANGED') {          // PRODUCT.md §2 — the room matured or was held/released, live
        const mid = String(m.eventId || '');
        if (!mid || _modeSeenRef.current.has(mid)) continue;
        _modeSeenRef.current.add(mid);
        loadRoomMode();                            // the server resolves the effective stage (cap + legacy floor)
        continue;
      }
      if (!m || _et !== 'ROOM_MODE_CHANGED') continue;
      const id = String(m.eventId || '');
      if (!id || _modeSeenRef.current.has(id)) continue;
      _modeSeenRef.current.add(id);
      const p = m.payload || {};
      if (!p.mode) continue;
      setRoomModeState((s) => ({ ...s, mode: p.mode, setByName: p.byName || '' }));
      if (p.by && p.by !== user?.sub && p.by !== user?.userId) {
        const label = { context: 'Context', plan: 'Plan', run: 'Run', verify: 'Verify' }[p.mode] || p.mode;
        pushLocalSystemEvent(`${p.byName || 'A teammate'} switched the room to ${label}.`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
  const handleSetContextModel = useCallback(async (tier) => {
    const r = await setRoomContextModel(networkId, tier, { pool: poolId });
    setRoomModeState((st) => ({ ...st, contextModel: r?.contextModel ?? tier, contextModelSetByName: r?.contextModelSetByName || '' }));
  }, [networkId, poolId]);
  const handleSetInternet = useCallback(async (on) => {
    const r = await setRoomInternet(networkId, on, { pool: poolId });
    setRoomModeState((st) => ({ ...st, internetSearch: r?.internetSearch ?? !!on, internetSearchDefault: false,
      internetSearchSetByName: r?.internetSearchSetByName || '' }));
  }, [networkId, poolId]);
  const [planGateOpen, setPlanGateOpen] = useState(false);
  const handlePlanRoom = useCallback(async (req) => {
    const r = await planRoom(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, roomPlan: r?.roomPlan || st.roomPlan }));
    if (req && req.action === 'approve') loadRoomMode();      // the room is at Plan now — its surfaces follow
    return r;
  }, [networkId, poolId, loadRoomMode]);
  // THE SUGGESTED PLAN UPDATE (owner 2026-10-21) — Update replaces this room's untouched suggestion; Dismiss hides
  // that version for everyone in the room. The server re-checks "untouched" (an edited room is never overwritten).
  const handleSuggestedUpdate = useCallback(async (what) => {
    const v = roomModeState.roomPlan?.suggestedUpdate?.toVersion || 0;
    try {
      await handlePlanRoom(what === 'update' ? { action: 'updateSuggested' }
        : { action: 'dismissSuggestedUpdate', version: v });
    } finally {
      loadRoomMode();                     // the plan, its tasks and the offer all follow
    }
  }, [handlePlanRoom, loadRoomMode, roomModeState.roomPlan?.suggestedUpdate?.toVersion]);
  // THE PLAN'S WORDS (EXPERT-NETWORKS.md §1, E1) — Milestone/Epic/Task, or the network's own (Team/Responsibility/Rule)
  const planWordsValue = useMemo(() => planWords(roomModeState.planVocabulary), [roomModeState.planVocabulary]);
  const handleSetStageCap = useCallback(async (cap) => {
    const r = await setRoomStageCap(networkId, cap, { pool: poolId });
    setRoomModeState((st) => ({ ...st, stage: r?.stage || st.stage, stageCap: r?.stageCap ?? (cap || '') }));
  }, [networkId, poolId]);
  const handleSetExpert = useCallback(async (req) => {
    const r = await setRoomExpert(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, expertPlugins: r?.expertPlugins || st.expertPlugins,
      expertCandidates: r?.expertCandidates || st.expertCandidates, clientFacts: r?.clientFacts || st.clientFacts }));
  }, [networkId, poolId]);
  const [composerSeed, setComposerSeed] = useState(null);
  // EXPERT ROOMS (EXPERT-ROOMS.md §8, X4): "@Team …" in the composer → that Team's request form (the message = its note)
  const [offeringSeed, setOfferingSeed] = useState(null);
  const mentionTeams = useMemo(() => {
    const out = [];
    // every serving expert's teams — including this room's OWN expert team (its Lead asks its departments, the
    // onboarding's first step "@Organization connect…" is exactly that)
    for (const x of (roomModeState.expertServices || [])) {
      // a team by name only inside the expert's OWN rooms (its Lead asks its departments); any other room asks the
      // expert by ONE name (``@AWS CCoE`` — EXPERT-ROOMS.md §13) and its Lead routes the question
      if (!x.own) continue;
      for (const o of (x.offerings || [])) {
        if (o.team && o.teamRoom && !out.some((t) => t.label === o.team && t.expertNetworkId === x.expertNetworkId)) {
          out.push({ label: o.team, expertNetworkId: x.expertNetworkId });
        }
      }
    }
    return out;
  }, [roomModeState.expertServices]);
  const handleTeamRequest = useCallback(({ team, expertNetworkId, note }) => {
    const x = (roomModeState.expertServices || []).find((e) => e.expertNetworkId === expertNetworkId);
    const offers = ((x && x.offerings) || []).filter((o) => String(o.team || '').toLowerCase() === String(team).toLowerCase());
    setOfferingSeed({ expertNetworkId, team, offeringId: offers.length === 1 ? offers[0].id : '', note, nonce: Date.now() });
    setMembersDrawerOpen(true);
  }, [roomModeState.expertServices]);
  // Inbox "Finish setting up" (EXPERT-NETWORKS.md §7) deep-links ``?tab=setup`` → open the room settings on Get started
  useEffect(() => {
    if (['setup', 'requests', 'rules', 'questions'].includes(String(initialTab || '').toLowerCase())) setMembersDrawerOpen(true);
  }, [initialTab, networkId, poolId]);
  const handleOnboardingAct = useCallback(async (req) => {
    const r = await setRoomOnboarding(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, onboarding: r?.onboarding || st.onboarding,
      expertFacts: r?.expertFacts !== undefined ? r.expertFacts : st.expertFacts }));
    if (r?.composer) {        // the first request, pre-filled for the person to review + send (never auto-sent)
      setComposerSeed({ text: r.composer, nonce: Date.now() });
      setMembersDrawerOpen(false);
      setRoomTabIndex(0);
    }
  }, [networkId, poolId]);
  const handleApplyTemplate = useCallback(async (id) => {
    await applyExpertTemplate(networkId, id, { pool: poolId });
    loadRoomMode();
  }, [networkId, poolId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const loadToolConnections = useCallback(async (tool) => {
    const { secrets } = await listSecretsRegistry({ toolName: tool, networkId, poolId });
    return (secrets || []).filter((x) => String(x.testStatus || '').toUpperCase() === 'TESTED_OK')
      .map((x) => ({ id: x.secretId, name: x.secretName || x.secretId }));
  }, [networkId, poolId]);
  const handleSetFact = useCallback(async (req) => {
    const r = await setRoomFact(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, expertFacts: r?.expertFacts || st.expertFacts }));
  }, [networkId, poolId]);
  const handleOffering = useCallback((req) => roomOffering(networkId, req, { pool: poolId }), [networkId, poolId]);
  // FULFILMENT (EXPERT-ROOMS.md §6 step 5, X5): THIS team room runs the request as typed input (the ordinary room run,
  // the approver as caller), then links the run — its end reports into the request
  const startFulfilment = useCallback(async (request, prompt) => {
    const kick = await poolExecute(networkId, poolId, { input: { prompt, tenantId: poolId }, logLevel: 'INFO', source: 'ui' });
    const runId = String((kick && (kick.executionName || kick.taskId))
      || (kick && typeof kick.executionArn === 'string' ? kick.executionArn.split(':').pop() : '') || '').trim();
    if (!runId) throw new Error('The run started but its id was not returned — refresh and press Start again.');
    setKickedRunId(runId);
    const r = await setRoomExpertRequest(networkId, { action: 'start', expertNetworkId: request.expertNetworkId,
      requestId: request.requestId, runId }, { pool: poolId });
    setRoomModeState((st) => ({ ...st, expertRequests: r?.expertRequests || st.expertRequests }));
  }, [networkId, poolId]);
  const handleRequestAct = useCallback(async (req) => {
    const r = await setRoomExpertRequest(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, expertRequests: r?.expertRequests || st.expertRequests }));
    if (req.action === 'approve' && r?.fulfilment?.prompt && r?.request) await startFulfilment(r.request, r.fulfilment.prompt);
  }, [networkId, poolId, startFulfilment]);
  const handleCertify = useCallback(async (req) => {
    const r = await setRoomCertification(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, expertCertification: r?.expertCertification || st.expertCertification }));
  }, [networkId, poolId]);
  // CONSULT (EXPERT-ROOMS.md §13): ask an expert (this room) / answer for one (an expert's room) — same endpoint
  const handleConsult = useCallback(async (req) => {
    const r = await setExpertConsult(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, expertConsults: r?.expertConsults !== undefined ? r.expertConsults : st.expertConsults }));
    return r;
  }, [networkId, poolId]);
  // TRAIN (EXPERT-ROOMS.md §15d): Train runs async (a live EXPERT_TRAINED + a short poll bring the suggestions back)
  const handleExpertTrain = useCallback(async (req) => {
    try {
      const r = await setExpertTrain(networkId, req, { pool: poolId });
      setRoomModeState((st) => ({ ...st, expertTraining: r?.expertTraining !== undefined ? r.expertTraining : st.expertTraining }));
    } catch (err) {
      setRoomModeState((st) => ({ ...st, expertTraining: st.expertTraining
        ? { ...st.expertTraining, error: (err && err.message) || "Couldn't do that — try again." } : st.expertTraining }));
    }
  }, [networkId, poolId]);
  const _training = roomModeState.expertTraining;
  const keepKinds = keepKindsFor(roomModeState);
  useEffect(() => {
    if (!_training || _training.status !== 'training') return undefined;
    const t = setTimeout(() => loadRoomMode(), 3000);
    return () => clearTimeout(t);
  }, [_training, loadRoomMode]);
  const _trainedSeq = useMemo(
    () => messages.filter((m) => m && (m.eventType ?? m.type) === 'EXPERT_TRAINED').length, [messages]);
  useEffect(() => { if (_trainedSeq) loadRoomMode(); }, [_trainedSeq, loadRoomMode]);
  const handleRuleChange = useCallback(async (req) => {
    const r = await setRoomRuleChange(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, ruleChanges: r?.ruleChanges || st.ruleChanges }));
    return r;
  }, [networkId, poolId]);
  const handleRequestStart = useCallback((request) => startFulfilment(request, request.fulfilmentPrompt),
    [startFulfilment]);
  const handleTeamAccess = useCallback(async (req) => {
    const r = await setRoomTeamAccess(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, teamAccess: r?.teamAccess || st.teamAccess }));
  }, [networkId, poolId]);
  // a department room's connections: the ones visible to THAT room (the Lead attaches them for it)
  const loadRoomConnections = useCallback(async (tool, room) => {
    const { secrets } = await listSecretsRegistry({ toolName: tool, networkId, poolId: room || poolId });
    return (secrets || []).filter((x) => String(x.testStatus || '').toUpperCase() === 'TESTED_OK'
      && String((x.config || {}).TIER || '').toLowerCase() !== 'admin')
      .map((x) => ({ id: x.secretId, name: x.secretName || x.secretId }));
  }, [networkId, poolId]);
  const handleLiaisonDecide = useCallback(async (req) => {
    const r = await setRoomLiaisonAction(networkId, req, { pool: poolId });
    setRoomModeState((st) => ({ ...st, liaisonActions: r?.liaisonActions || st.liaisonActions }));
  }, [networkId, poolId]);
  const handleRequestMode = useCallback(async (mode) => {
    try {
      const r = await setRoomMode(networkId, mode, { pool: poolId });
      setRoomModeState((st) => ({ ...st, mode: r?.mode || mode }));
      return true;
    } catch (err) {
      pushLocalSystemEvent((err && err.message) || "Couldn't switch the room's mode.");
      return false;
    }
  }, [networkId, poolId, pushLocalSystemEvent]);
  // ROOM TYPE (MODES.md §8): a Context-only release → this room only ever offers Context (also for a network
  // without phases — its rooms still get the discussion + direct work)
  const contextOnly = roomModeState.room === roomKey && roomModeState.roomType === 'context';
  // PRODUCT.md §2 — EVERY room talks (Ask / Do / Context). Before Plan a room's composer IS the discussion; its
  // network still runs as a whole on purpose ("Run network") unless the release is Context only. Plan / Run /
  // Verify arrive with the Plan stage (today: a release with milestones).
  const _modeLoaded = roomModeState.room === roomKey && roomModeState.loaded;
  const discussionOnly = contextOnly || (_modeLoaded && !isProject);
  const modesOn = isProject || discussionOnly;
  const keptConversational = _modeLoaded && roomModeState.stageCap === 'converse';
  const effectiveRoomMode = discussionOnly ? 'context' : (roomModeState.mode || journeyMode);
  // known = the room's own mode is loaded, and (when it was never set) the plan the journey default reads is too —
  // until then nothing switches (no Context flash / tab flicker for a room that has a plan)
  const roomModeKnown = roomModeState.loaded && roomModeState.room === roomKey
    && (!!roomModeState.mode || !!planState.loaded);
  useEffect(() => {
    if (!roomModeKnown) return;
    const on = planTabsDecision({ isProject, contextOnly, mode: effectiveRoomMode });
    setPlanTabs({ room: roomKey, on });
    if (!on) setRoomTabIndex((i) => (i >= 6 ? 0 : i));   // its tab went away → back to Activities
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProject, effectiveRoomMode, roomModeKnown, roomKey]);
  const {
    turnGroups, uiState: _streamUiState, lastExecution,
    agentRoster, pendingBuilderWaits: roomPendingBuilderWaits, currentRunId,
    runReleaseVersions, agentRostersByRun, stageCostByRun,
  } = room;

  /** PLAN LIVE (§2.5g) + ASYNC PLAN-UPDATE, replay-safe (2026-09-20): the MOST RECENT of
   *  ``PLAN_UPDATE_RUNNING`` / ``PLAN_UPDATED`` in the merged history+live buffer decides the
   *  'plan updating…' state. Deriving it from the live buffer only (the old two listeners) lost the
   *  beacon on any reload/reconnect while the async planner was still working — the persisted
   *  history had the event, the listener never looked. A terminal ``PLAN_UPDATED`` also bumps
   *  ``planStateBump`` (the /tasks re-fetch signal), exactly once per eventId.
   *  MUST stay AFTER the ``room`` destructuring: it reads ``currentRunId`` (from ``room``), so
   *  placing it above the ``const { … currentRunId } = room`` puts that const in its temporal dead
   *  zone → "Cannot access before initialization" at render (2026-09-21 crash regression). */
  useEffect(() => {
    // Scope the beacon to the CURRENT run so a stale PLAN_UPDATE_RUNNING/PLAN_UPDATED from a prior
    // run/milestone can't flip 'plan updating…' (old-execution mixing, 2026-09-21).
    const beacon = latestPlanBeacon(combinedEnvelopes, currentRunId);
    if (!beacon || beacon.eventId === lastPlanEventIdRef.current) return;
    lastPlanEventIdRef.current = beacon.eventId;
    setPlanUpdating(beacon.running);
    if (!beacon.running) setPlanStateBump((n) => n + 1);
  }, [combinedEnvelopes, currentRunId]);

  // REALTIME HUB (REALTIME-HUB.md P0): the AUTHORITATIVE per-agent status. The agent tree's STRUCTURE
  // comes from the manifest/stream; its STATUS is reconciled from a fetchable snapshot so a missed /
  // early / reordered beacon can't leave an agent falsely "done". A stream status change is only an
  // invalidation SIGNAL (§0) — it re-fetches truth, it is never trusted as the payload. Heartbeat runs
  // only while the room is RUNNING; forward-only overlay below never regresses a terminal row.
  // ``uiState`` is in the digest so the run TERMINAL (EXECUTION_COMPLETED/FAILED) fires one FINAL
  // reconcile: the worker's AGENT_FINAL_RESPONSE beacon races the engine post-handler's ``done`` write,
  // so the last roster-triggered fetch can read a still-``running`` snapshot — without this final
  // reconcile (heartbeat stops once the run leaves RUNNING) the overlay would pin the agent ``running``
  // until tab focus. By SFN ordering every post write committed before the final reducer published the
  // terminal, so this fetch deterministically reads the settled state.
  // P1: the hub's own PAYLOAD-FREE signal (ROOM_STATE_CHANGED {seq, kind}, published by every RUNSTATE
  // write) also feeds the digest — its ``seq`` is used ONLY as an invalidation trigger (never applied
  // as state), so the reconcile fires even for state changes that have no stream beacon at all.
  const _rtSignalSeq = useMemo(() => {
    let max = 0;
    for (const m of (Array.isArray(messages) ? messages : [])) {
      if (!m || (m.eventType ?? m.type) !== 'ROOM_STATE_CHANGED') continue;
      const s = Number(m.payload?.seq ?? 0);
      if (Number.isFinite(s) && s > max) max = s;
    }
    return max;
  }, [messages]);
  const _rtSignal = useMemo(
    () => `${currentRunId || ''}:${_streamUiState || ''}:${_rtSignalSeq}:${(Array.isArray(agentRoster) ? agentRoster : []).map((r) => `${r.agentId}=${r.status}`).join(',')}`,
    [currentRunId, _streamUiState, _rtSignalSeq, agentRoster],
  );
  const {
    statusByAgent: snapshotStatusByAgent,
    uiState: snapshotUiState,
  } = useRoomState(networkId, poolId, {
    // ``active`` seeds from the stream's uiState; the hook self-sustains while its OWN snapshot says
    // running (fix #3), so it keeps reconciling even if the stream uiState is wrong.
    active: String(_streamUiState || '').toUpperCase() === 'RUNNING',
    signal: _rtSignal,
  });
  // v2 (LIVE_STATE_FROM_SNAPSHOT): the LIVE room lifecycle comes from the SNAPSHOT, not the event
  // stream. snapshotUiState wins whenever present; the stream value is only the pre-first-fetch
  // fallback (and for historical rooms with no live RUNSTATE). This is what makes a stray/late/leaked
  // UI_STATE event unable to flip the room — the room's uiState is a fetched fact, never a pushed one.
  const uiState = snapshotUiState || _streamUiState;

  // LANE-B log-tail backfill: fetch each RUNNING builder's bounded tail ONCE (per run+agent), so a
  // mid-build join/refresh shows recent history instead of a blank log. Guarded by a ref so re-renders
  // (incl. our own setState) don't refetch; best-effort (a miss just means the live stream fills in).
  const _tailFetchedRef = useRef(null);
  if (_tailFetchedRef.current === null) _tailFetchedRef.current = new Set();
  useEffect(() => {
    if (!networkId || !currentRunId || !Array.isArray(agentRoster)) return undefined;
    const todo = agentRoster.filter(
      (r) => r && r.isBuilder && r.agentId && String(r.status) === 'running'
        && !_tailFetchedRef.current.has(`${currentRunId}:${r.agentId}`),
    );
    if (!todo.length) return undefined;
    let cancelled = false;
    (async () => {
      for (const r of todo) {
        _tailFetchedRef.current.add(`${currentRunId}:${r.agentId}`);   // mark before await → fetch once
        try {
          const tail = await fetchBuilderLogTail(networkId, {
            pool: poolId || undefined, runId: currentRunId, agent: r.agentId,
          });
          const lines = Array.isArray(tail && tail.lines) ? tail.lines : [];
          if (cancelled || !lines.length) continue;
          // seq = highest lineNo in the tail; the tail holds the LAST N lines → line k is lineNo
          // (seq - lines.length + k + 1). This lets the reducer dedup live lines by lineNo.
          const start = (Number(tail.seq) || lines.length) - lines.length;
          const envs = lines.map((text, k) => ({
            eventType: 'LOG_LINE',
            correlationId: currentRunId,
            payload: {
              stream: 'universal_builder', text: String(text),
              agentId: r.agentId, lineNo: start + k + 1, raw: true,
            },
          }));
          setLogTailEnvelopes((prev) => [...prev, ...envs]);
        } catch { /* best-effort backfill; the live stream still renders */ }
      }
    })();
    return () => { cancelled = true; };
  }, [networkId, poolId, currentRunId, agentRoster]);

  // RECONNECT BACKFILL (REALTIME-HUB.md §0.3, 2026-09-25 "stream gone mid-run, back on hard refresh"):
  // AppSync Events has no replay, so events published while the socket was down never reach
  // ``messages``. On every reconnect: (1) re-arm the builder log-tail backfill (LOG_LINEs are
  // ephemeral — the tail is the ONLY way back; the reducer dedupes by lineNo), and (2) fetch the
  // newest channel-events page and merge it into history (same dedupe key as the live buffer).
  // This is exactly what a hard refresh did — now it happens automatically, without the refresh.
  useEffect(() => {
    if (!reconnectEpoch || !networkId || !poolId) return undefined;
    _tailFetchedRef.current = new Set();
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchNetworkChannelEvents(networkId, { limit: 150, poolId });
        if (cancelled) return;
        const page = Array.isArray(resp.events) ? resp.events : [];
        setHistoryEnvelopes((prev) => mergeBackfill(prev, page));
        // eslint-disable-next-line no-console
        console.info('[room-history] reconnect backfill', { epoch: reconnectEpoch, fetched: page.length });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[room-history] reconnect backfill failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [reconnectEpoch, networkId, poolId]);

  // Network topology (the full agent tree) so the roster can be SEEDED stable up-front — fetched +
  // cached; also feeds MiniFlow below. Kept here (not at first render only) so the seeded roster is
  // available before the merge.
  const { asl: networkAsl } = useNetworkAsl(networkId, releaseVersion);

  // STABLE seeded roster: the WHOLE topology, all ``queued``, the instant the manifest is known.
  // The live event-accreted ``agentRoster`` then only MUTATES status onto these rows — the list
  // never jumps/reshuffles as distributed agents report in (predictable layout, the user's ask).
  const seededRoster = useMemo(
    () => (isManifest(networkAsl) ? manifestToRoster(networkAsl) : []),
    [networkAsl],
  );
  // Run is settled → a still-``queued`` seeded row was NOT selected by the Lead (gray it out).
  const runSettled = useMemo(() => {
    const s = String(lastExecution?.status || '').toUpperCase();
    return s === 'COMPLETED' || s === 'FAILED'
      || ['IDLE', 'COMPLETED', 'FAILED'].includes(String(uiState || '').toUpperCase());
  }, [lastExecution?.status, uiState]);
  // DYNAMIC ROSTER FOCUS (project rooms): the agents the current milestone's ready tasks actually
  // assigned (leadAgent + involvedAgents, unioned across the ready set). For a task-bound project
  // room this focuses the roster/tree on the RELEVANT agents — the rest render dimmed (``notInTask``)
  // — so the user sees exactly who's in play, up front. Empty (non-project, or no ready tasks) →
  // no focus → every agent stays in-task (unchanged behavior).
  const involvedAgentIds = useMemo(() => {
    if (!isProject) return [];
    const set = new Set();
    for (const t of (planState.readyTasks || [])) {
      if (t?.leadAgent) set.add(String(t.leadAgent));
      for (const a of (t?.involvedAgents || [])) if (a) set.add(String(a));
    }
    return Array.from(set);
  }, [isProject, planState.readyTasks]);
  // The merged roster the panel renders: stable seeded rows overlaid with live status/text/reasoning.
  const mergedRoster = useMemo(
    () => {
      const base = seededRoster.length
        ? mergeRoster(seededRoster, agentRoster, {
          runSettled, stageDepthCap, disabledByAgentId: agentDisables, reuseByAgentId: agentReuses,
          involvedAgents: involvedAgentIds,
        })
        : agentRoster;
      // REALTIME HUB overlay (§0): the authoritative snapshot status WINS over the stream-derived one
      // whenever present — a stream ``done`` that arrived early/out-of-order is corrected DOWN to the
      // snapshot's ``running`` (the "agents done early" bug). Snapshot per-run is monotonic (engine
      // writes queued→running→done); agents not yet in the snapshot keep their stream status. Structure
      // and all labels/text still come from the stream/manifest — only ``status`` is reconciled.
      const snap = snapshotStatusByAgent;
      if (!snap || !Object.keys(snap).length || !Array.isArray(base)) return base;
      return base.map((r) => {
        const s = r && r.agentId ? snap[r.agentId] : undefined;
        if (!s) return r;
        const mapped = s === 'bypass' ? 'done' : s;   // bypass = engine early-return → terminal
        return mapped === r.status ? r : { ...r, status: mapped };
      });
    },
    [seededRoster, agentRoster, runSettled, stageDepthCap, agentDisables, involvedAgentIds, snapshotStatusByAgent],
  );
  // Status map {agentId → {status, notSelected}} for the docked live tree — colors each node by the
  // AppSync stream in real time (running/awaiting/done/failed/skipped). Derived from the merged
  // roster so it matches the line-item panel exactly.
  const treeStatusByAgentId = useMemo(() => {
    const m = {};
    for (const r of (Array.isArray(mergedRoster) ? mergedRoster : [])) {
      if (r && r.agentId) m[r.agentId] = { status: r.status, notSelected: !!r.notSelected, notInTask: !!r.notInTask };
    }
    return m;
  }, [mergedRoster]);
  // The docked tree's React-Flow graph ({nodes,edges}) from the SAME manifest the roster is seeded
  // from — one source of truth for topology.
  const networkAslGraph = useMemo(
    () => (isManifest(networkAsl) ? manifestToReactFlow(networkAsl) : { nodes: [], edges: [] }),
    [networkAsl],
  );
  // DYNAMIC NETWORK VIEW (DYNAMIC-NETWORK-VIEW): render only ENGAGED agents (+ ancestors + root
  // leads), incrementally, instead of the whole pre-seeded org chart — smaller footprint, reads as
  // "the network is thinking about THIS". DEFAULT ON; a header radio flips to the full (as-is) view.
  // Declared BEFORE the graph memo below (which reads it) to avoid a temporal-dead-zone crash.
  const _NETVIEW_KEY = 'stepflow:network-dynamic-view';
  const [dynamicView, setDynamicView] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage?.getItem(_NETVIEW_KEY) !== '0';   // default ON (dynamic)
  });
  useEffect(() => {
    try { window.localStorage?.setItem(_NETVIEW_KEY, dynamicView ? '1' : '0'); } catch (_e) { /* ignore */ }
  }, [dynamicView]);
  // DIMMED (capped-out) agents for the docked tree: agentIds deeper than the FROZEN release stage
  // cap → "part of the team, disabled this release". Derived from the seeded roster (carries
  // ``level``) so it matches the manifest the tree is drawn from. Empty set when cap=0 (Auto).
  const roomDimmedAgentIds = useMemo(
    () => cappedOutAgentIds(seededRoster, stageDepthCap),
    [seededRoster, stageDepthCap],
  );
  // Which agent the user clicked in the docked tree → highlights the node + loads that agent into
  // the shared container (two-way with the line-item panel's own selection).
  const [selectedTreeAgentId, setSelectedTreeAgentId] = useState(null);
  // AGENT STREAM DOCK (VSCode-terminal-style multi-tab agent viewer). Toggle persists per-user;
  // DEFAULT ON (the new UX). When on, clicking an agent opens it as a dock TAB (many concurrent,
  // stream-or-log) instead of the old single inline detail pane; the topology tree stays the
  // launcher. When off, the exact prior behavior renders (old pane) — untouched fallback.
  const _DOCK_KEY = 'stepflow:agent-dock-enabled';
  const [dockEnabled, setDockEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage?.getItem(_DOCK_KEY) !== '0';   // default ON
  });
  useEffect(() => {
    try { window.localStorage?.setItem(_DOCK_KEY, dockEnabled ? '1' : '0'); } catch (_e) { /* ignore */ }
  }, [dockEnabled]);
  // "Open this agent in the dock" signal — id + a monotonic nonce so re-clicking the SAME agent
  // re-opens a tab the user had closed (the dock keys its open effect on the nonce).
  const [dockRequest, setDockRequest] = useState({ agentId: null, nonce: 0 });
  // GROOM SCOPE (§2.6): tasks the user selected in the Tasks tab for a surgical groom. Fed to the
  // Composer, which (in Plan mode) sends it as ``groomScope`` on the run. null = normal plan.
  const [groomScope, setGroomScope] = useState(null);
  // PLAN TIER (PLAN-INTEGRATION.md §2.5) — the milestone-level plan tier the user picked in the Tasks
  // tab: 'auto' (default = architect hint + backstop) | 'standard' | 'verified'. Rides the plan run.
  const [planTier, setPlanTier] = useState('auto');
  const openAgentInDock = useCallback((agentId) => {
    if (!agentId) return;
    setSelectedTreeAgentId(agentId);   // keep the tree highlight in sync
    setDockEnabled(true);              // clicking an agent ALWAYS opens the dock (expand if collapsed)
    setDockRequest((prev) => ({ agentId, nonce: prev.nonce + 1 }));
  }, []);
  // HISTORICAL LOG TABS (UX parity, user ask 2026-09-20): every persisted transcript — a planner
  // run from the Executions tab, any future historical source — opens in the DOCK (the one
  // agent-stream surface), never an inline reader. The caller hands a synthetic done-builder row
  // (unique agentId per run, builderCompletion carrying the logKey); it is merged into the dock's
  // roster slice only (live rows win on id collision) and its tab is focused.
  const [dockExtraRows, setDockExtraRows] = useState([]);
  useEffect(() => { setDockExtraRows([]); }, [networkId, poolId]);   // never carry across rooms
  const openLogInDock = useCallback((row) => {
    if (!row || !row.agentId) return;
    setDockExtraRows((prev) => [...prev.filter((r) => r.agentId !== row.agentId), row]);
    setDockEnabled(true);
    setDockRequest((prev) => ({ agentId: row.agentId, nonce: prev.nonce + 1 }));
  }, []);
  // PLAN DOC → DOCK (2026-09-23): the Project Map "Plan" button opens a task's plan .md as a DOCK TAB
  // (kind 'doc'), replacing the bespoke popover — a plan reads in the same dock as every agent stream.
  // Re-clicking re-focuses; the dock's own ✕/collapse closes it, exactly like an agent tab.
  const openPlanDocInDock = useCallback((taskId, title) => {
    const tid = String(taskId || '').trim();
    if (!tid) return;
    const agentId = `plandoc:${tid}`;
    const row = { agentId, label: title || tid, kind: 'doc', docTaskId: tid };
    setDockExtraRows((prev) => [...prev.filter((r) => r.agentId !== agentId), row]);
    setDockEnabled(true);
    setDockRequest((prev) => ({ agentId, nonce: prev.nonce + 1 }));
  }, []);
  // The click handler the tree + run rows use. The DOCK is the ONLY agent-stream surface — clicking
  // an agent always opens it as a tab (expanding the dock if the user had collapsed it). There is no
  // legacy inline-reader fallback: the old ``#0d1117`` pane inside AgentHierarchyPanel is obsolete
  // (it re-appeared when the dock was collapsed → the "builder pops above the agents" bug).
  const onAgentActivate = useCallback((agentId) => {
    openAgentInDock(agentId);
  }, [openAgentInDock]);

  // FOCUSED RUN (multi-run disambiguation): with several run blocks stacked — each carrying its OWN
  // graph — the user needs a clear visual anchor for "which execution's graph am I looking at". We
  // track the focused runId; ``null`` resolves to the newest run at render (default = latest/live).
  // Clicking a run block focuses it. Only applied when >1 block is on screen (a lone run needs none).
  const [focusedRunId, setFocusedRunId] = useState(null);

  // MULTI-BUILDER controls (Step 4): derive the "N awaiting" notice, the selected wait's band, and
  // the per-row Stop targets from the reducer's run-level roster + pending-waits list. Replaces the
  // single-``activeBuilderCliWait`` path for the hierarchy surface (that path still drives the flat
  // TurnGroup history + the Dynamo-hydrated reconnect band below).
  const builderControls = useBuilderRoomControls({
    pendingBuilderWaits: roomPendingBuilderWaits,
    agentRoster,
    networkId,
    poolId,
    canRespond: canRespondToBuilderApproval(poolRoles, workspaceRole),
  });

  // Per-row builder control (Stop-per-row / Respond) for the LIVE run's tree — shared by the docked
  // panel and the live RunBlock. Only the live run gets these (historical runs are read-only).
  // Respond to an awaiting builder = ONE action → (a) load that builder's stream into the shared
  // window AND (b) open its interaction band above the prompt. The Respond button stops row-click
  // propagation, so we drive the shared selection here too — otherwise the band would open without
  // the stream showing.
  const respondToBuilder = useCallback((agentId) => {
    setSelectedTreeAgentId(agentId);        // shared window → this builder's stream
    builderControls.respondToAgent(agentId); // band above the prompt → this builder's questions
  }, [builderControls]);

  const renderBuilderRowAction = useCallback((row) => (
    <AgentRowBuilderAction
      row={row}
      runningRunId={builderControls.runningBuilderRunIds[row.agentId] || null}
      isAwaiting={row.status === 'awaiting'}
      isSelected={!!builderControls.selectedWait && builderControls.selectedWait.agentId === row.agentId}
      stopping={builderControls.stoppingSet.has(builderControls.runningBuilderRunIds[row.agentId])}
      canRespond={builderControls.canRespond}
      onStop={builderControls.stopBuilder}
      onRespond={respondToBuilder}
    />
  ), [builderControls, respondToBuilder]);

  // ── Pre-flight MCP gate ────────────────────────────────────────────────
  // An agent in this room may enable an *authenticated* MCP (e.g. GitHub).
  // The builder wires MCP servers BEFORE claude-code starts, so a missing
  // credential can't be recovered mid-run — it would silently drop the tool.
  // We block Send up front and tell the user to configure it in Connections.
  // `needsConfig` (computed server-side: enabled ∩ authRequired ∩ unconfigured)
  // is the authority; the room just renders the reason.
  const [mcpNeedsConfig, setMcpNeedsConfig] = useState([]);
  // MCP READINESS (fail-fast framework): credentials that ARE set but failed verification (BAD).
  // Distinct from ``needsConfig`` (no cred set) — both block Send + the trigger 409s on them.
  const [mcpNotReady, setMcpNotReady] = useState([]);
  // DECLARED CONNECTORS (code-connector / cred-broker / generic-rest-api the room's agents
  // need) with live configured-status — drives the Connections-tab rows so the room shows
  // what to credential even before any credential row exists.
  const [declaredConnectors, setDeclaredConnectors] = useState([]);
  // Declared-but-maybe-unconfigured MCPs the room's agents enable (GitHub/GitLab/…) + their
  // agents — surfaced on the Connections tab even before a credential row exists.
  const [declaredMcps, setDeclaredMcps] = useState([]);
  // Human-assigned connector→agent bindings (pool overrides) — so the Connections tab shows which
  // agents use each connector in this room (single source of truth).
  const [connectorAssignments, setConnectorAssignments] = useState([]);
  const [mcpGateBump, setMcpGateBump] = useState(0);
  const refreshMcpGate = useCallback(() => setMcpGateBump((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    if (!networkId || networkId === 'new' || !poolId) {
      setMcpNeedsConfig([]);
      setMcpNotReady([]);
      setDeclaredConnectors([]);
      setDeclaredMcps([]);
      setConnectorAssignments([]);
      return undefined;
    }
    (async () => {
      try {
        const data = await getMcpCredentialStatus(networkId, poolId);
        if (cancelled) return;
        setMcpNeedsConfig(Array.isArray(data?.needsConfig) ? data.needsConfig : []);
        setMcpNotReady(Array.isArray(data?.notReady) ? data.notReady : []);
        setDeclaredConnectors(Array.isArray(data?.declaredConnectors) ? data.declaredConnectors : []);
        setDeclaredMcps(Array.isArray(data?.declaredMcps) ? data.declaredMcps : []);
        setConnectorAssignments(Array.isArray(data?.connectorAssignments) ? data.connectorAssignments : []);
      } catch {
        // Best-effort: a status read failure leaves the gate open (the
        // builder fails safe by skipping uncredentialed MCPs anyway).
        if (!cancelled) {
          setMcpNeedsConfig([]); setMcpNotReady([]); setDeclaredConnectors([]);
          setDeclaredMcps([]); setConnectorAssignments([]);
        }
      }
    })();
    return () => { cancelled = true; };
    // Re-check when the room/run settles so configuring a credential
    // mid-session clears the gate, and on explicit bumps from Connections.
  }, [networkId, poolId, uiState, mcpGateBump]);

  // Synchronized UX: the room drawer (add/remove/pin a tool) and the Connections tab both broadcast
  // ``stepflow-secret-registry-changed``. Re-fetch the connection status on that signal so the tab's
  // declaredMcps/connectorAssignments + the run gate reflect a drawer change WITHOUT a manual Refresh.
  useEffect(() => {
    const onChanged = () => refreshMcpGate();
    window.addEventListener('stepflow-secret-registry-changed', onChanged);
    return () => window.removeEventListener('stepflow-secret-registry-changed', onChanged);
  }, [refreshMcpGate]);

  // PROJECT PLAN STATE (Unit F): fetch the canonical plan's finalize flag + ready set so the
  // composer can gate a run + offer the task selector. Project rooms only. Refetches when the run
  // settles (uiState) — finalizing in the Tasks tab, or a task completing, moves what's ready.
  useEffect(() => {
    if (!isProject || !networkId) return undefined;
    let cancelled = false;
    // LAZY: the composer only needs the LIGHT index (id/milestone/status/wave/ready + connectors/
    // involvedAgents) — never the heavy detail/DoD text. ``view:'index'`` keeps this fast on big plans.
    listProjectTasks(networkId, { view: 'index', ...(poolId ? { pool: poolId } : {}) })
      .then((data) => {
        if (cancelled) return;
        // a room with NO plan yet (not eligible) is still a KNOWN state — the journey default (Context) and the
        // tabs-follow-the-mode rule wait on ``loaded`` (found 2026-10-03: ollie never resolved, tabs stayed)
        if (!data?.eligible) { setPlanState((prev) => ({ ...prev, loaded: true, allTasks: [], readyTasks: [] })); return; }
        const ready = (Array.isArray(data.tasks) ? data.tasks : []).filter((t) => t && t.ready);
        setPlanState({
          loaded: true,
          // RUNNABLE (P5a): the legacy whole-plan flag OR any milestone LOCKED — a lock opens THAT
          // milestone for execution while others are still being planned (the flag alone stayed false
          // until no draft remained anywhere, so a locked M0 could never be run).
          finalized: isPlanRunnable(data),
          projectKind: String(data.projectKind || 'executable'),       // G0 — the composer's modes follow it
          currentMilestone: String(data.currentMilestone || ''),
          // Milestone roadmap (for the composer's deterministic next-prompt + inline approve).
          milestones: Array.isArray(data.milestones) ? data.milestones : [],
          planningCursor: String(data.planningCursor || ''),
          approvedMilestones: Array.isArray(data.approvedMilestones) ? data.approvedMilestones : [],
          allTasks: (Array.isArray(data.tasks) ? data.tasks : []).map((t) => ({
            id: t.id, milestone: t.milestone, status: t.status, wave: t.wave,
            origin: t.origin || '', authoredRunId: t.authoredRunId || '',   // journey default: worked vs template
          })),
          readyTasks: ready.map((t) => ({
            id: t.id, title: t.title, leadAgent: t.leadAgent, milestone: t.milestone,
            wave: t.wave,                      // Run ▾ prefill: "Run wave 2 of …" (MODES.md §2)
            // Unit D: carry the task's required connectors + live status so the composer can
            // SOFT-WARN (not block) when a run's connectors aren't all tested_ok.
            requiredConnectors: Array.isArray(t.requiredConnectors) ? t.requiredConnectors : [],
            // Dynamic roster: the exact agents this task assigned — used to focus the roster/tree
            // on the relevant agents (dim the rest) for a task-bound run.
            involvedAgents: Array.isArray(t.involvedAgents) ? t.involvedAgents : [],
          })),
        });
      })
      .catch(() => { setPlanState((prev) => ({ ...prev, loaded: true })); /* best-effort: keep the last-known plan, but KNOWN */ });
    return () => { cancelled = true; };
  }, [isProject, networkId, poolId, uiState, planStateBump]);

  // MILESTONE the plan looks READY TO APPROVE: the planning-cursor milestone (earliest not-yet-
  // approved) whose tasks are all detailed (none still 'draft') — i.e. planning is done, the human
  // just hasn't clicked approve. Null when nothing is approvable (no cursor / still has drafts /
  // already approved). Drives BOTH the inline "Approve {milestone}" band button and the approve
  // suggestion. Deterministic — pure derivation from the plan state we already fetched.
  const approvableMilestone = useMemo(() => {
    if (!isProject) return null;
    const cur = planState.planningCursor;                // an ID, e.g. "M1"
    if (!cur) return null;
    if ((planState.approvedMilestones || []).includes(cur)) return null;
    // Tasks store ``milestone`` by NAME (e.g. "ARCHITECTURE"); the cursor is an ID ("M1"). Match on
    // either so the filter isn't silently empty (the "no wave / not approvable" bug).
    const m = (planState.milestones || []).find((x) => x.id === cur);
    const curName = (m && m.name) || cur;
    const msTasks = (planState.allTasks || []).filter((t) => t.milestone === cur || t.milestone === curName);
    if (!msTasks.length) return null;                    // nothing planned yet → not approvable
    const hasDraft = msTasks.some((t) => String(t.status || '').toLowerCase() === 'draft');
    if (hasDraft) return null;                           // still being planned → not ready
    return { id: cur, name: curName };
  }, [isProject, planState.planningCursor, planState.approvedMilestones, planState.allTasks, planState.milestones]);

  // DETERMINISTIC NEXT-PROMPT proposal (shown as the composer placeholder; Tab accepts it). Built
  // from the plan roadmap we already have — NO LLM, instant. Priority:
  //   1) plan ready to approve → "Approve {milestone}"
  //   2) a milestone still to plan (cursor) → "Plan {milestone}" (WHOLE milestone — §2.5h)
  //   3) approved + finalized + ready tasks → "Run wave {n}" / "Work {task}"
  // Returns {text, groomScope}. PLAN IS WHOLE-MILESTONE (§2.5h): the Plan suggestion never wave-scopes
  // (that's Refine's job). groomScope pins only the milestone (no taskIds/waves) so the reducer
  // replans the entire milestone unbiased; auto-continue (§2.5h) handles a too-big one, not waves.
  const nextPromptProposal = useMemo(() => {
    if (!isProject) return { text: '', groomScope: null };
    const nameFor = (mid) => {
      const m = (planState.milestones || []).find((x) => x.id === mid);
      return (m && m.name) || mid;
    };
    // (1) approve — a decision, no run scope.
    if (approvableMilestone) return { text: `Approve ${approvableMilestone.name}`, groomScope: null };
    // (2) plan the cursor milestone — WHOLE, unbiased (no wave subsetting).
    const cur = planState.planningCursor;                // an ID, e.g. "M1"
    if (cur && !(planState.approvedMilestones || []).includes(cur)) {
      return { text: `Plan ${nameFor(cur)}`, groomScope: { milestone: cur, taskIds: [] } };
    }
    // (3) run the ready set (execution, not plan → no groomScope).
    if (planState.finalized && (planState.readyTasks || []).length) {
      const first = planState.readyTasks[0];
      const waves = new Set((planState.readyTasks || []).map((t) => {
        const full = (planState.allTasks || []).find((a) => a.id === t.id);
        return full ? (Number(full.wave) || 0) : 0;
      }));
      if (waves.size === 1) return { text: `Run wave ${[...waves][0]}`, groomScope: null };
      return { text: first?.title ? `Work: ${first.title}` : '', groomScope: null };
    }
    return { text: '', groomScope: null };
  }, [isProject, approvableMilestone, planState.planningCursor, planState.approvedMilestones,
      planState.allTasks, planState.milestones, planState.finalized, planState.readyTasks]);
  const suggestedNextPrompt = nextPromptProposal.text;
  const suggestedGroomScope = nextPromptProposal.groomScope;

  // GUIDED-JOURNEY stage (PROJECT-NETWORKS §8) — which pipeline step (Plan→Review→Finalize→Run) is
  // active for the cursor milestone, derived from the SAME ledger state as the next-step proposal so
  // the composer's pipeline stepper can't drift out of sync. Only meaningful for a project room.
  const projectStage = useMemo(
    () => (isProject ? deriveProjectStage(planState, approvableMilestone) : ''),
    [isProject, planState, approvableMilestone],
  );

  // Inline approve from the composer's status band (idea 2). Mirrors TasksTab.onApprovePlan:
  // approve → re-fetch plan state (the effect above keys on uiState; bump it so the band + suggestion
  // refresh immediately without waiting for a run to settle).
  // REVIEW JUMP (band "Review" link): switch to the Tasks tab + ask TasksTab to reveal this
  // milestone (expand + scroll). Nonce makes repeat jumps to the same milestone re-fire.
  const [tasksFocusRequest, setTasksFocusRequest] = useState(null);
  const handleReviewMilestone = useCallback((milestoneId) => {
    if (!milestoneId) return;
    setRoomTabIndex(tasksTabIndex);
    setTasksFocusRequest({ id: milestoneId, nonce: Date.now() });
  }, [tasksTabIndex]);

  const [bandApproveBusy, setBandApproveBusy] = useState(false);
  const handleApproveMilestone = useCallback(async (milestoneId) => {
    if (!milestoneId || bandApproveBusy) return;
    setBandApproveBusy(true);
    try {
      await approveMilestonePlan(networkId, milestoneId, { approved: true, pool: poolId });
      // Optimistic local reflect so the band clears at once; the /tasks refetch confirms.
      setPlanState((prev) => ({
        ...prev,
        approvedMilestones: Array.from(new Set([...(prev.approvedMilestones || []), milestoneId])),
      }));
      setPlanStateBump((n) => n + 1);   // re-fetch /tasks → cursor advances, band + suggestion refresh
    } catch (e) {
      pushLocalSystemEvent(e?.message || 'Approve milestone failed');
    } finally {
      setBandApproveBusy(false);
    }
  }, [networkId, bandApproveBusy, pushLocalSystemEvent]);

  const mcpGateReason = useMemo(() => {
    // READINESS (BAD credential) takes precedence in the message — it's a set-but-broken cred, a
    // more specific fix ("the token is invalid") than "configure it". Then fall back to needsConfig.
    if (mcpNotReady.length) {
      const nr = mcpNotReady[0];
      const nm = nr.name || nr.id;
      return `Fix ${nm} in Connections to start a run — ${nr.reason || 'credential failed verification.'}`;
    }
    if (!mcpNeedsConfig.length) return '';
    // needsConfig is now per-(agent, mcp): { agentId, agentName, id, name }.
    // Summarize by connection name, noting the agent when it's a single one.
    const names = Array.from(
      new Set(mcpNeedsConfig.map((m) => m.name || m.id).filter(Boolean)),
    );
    const agents = Array.from(
      new Set(mcpNeedsConfig.map((m) => m.agentName || m.agentId).filter(Boolean)),
    );
    const who = agents.length === 1 ? ` for ${agents[0]}` : '';
    if (names.length === 1) return `Configure ${names[0]}${who} in Connections to start a run.`;
    return `Configure ${names.join(', ')} in Connections to start a run.`;
  }, [mcpNeedsConfig, mcpNotReady]);

  // READINESS map for the Connections tab (fail-fast framework): {mcpId: {status:'BAD', reason}} so
  // SecretsCatalog highlights the offending connection row with its error. Built from ``notReady``
  // (server-verified BAD credentials on active agents) — the exact rows the user must fix + re-test.
  const readinessByMcp = useMemo(() => {
    const out = {};
    for (const nr of mcpNotReady) {
      if (nr?.id) out[nr.id] = { status: 'BAD', reason: nr.reason || 'Credential failed verification.' };
    }
    return out;
  }, [mcpNotReady]);

  // Top-of-room readiness CHIP content — surfaces BOTH gate buckets so the user always SEES why
  // the room is blocked, not just when a credential is set-but-broken. A never-configured tool
  // ("Needs credential") blocks the run exactly the same as an expired one, but previously only
  // ``notReady`` got a banner and ``needsConfig`` was buried in the composer placeholder. ``notReady``
  // still wins the message (a more specific fix). null → no gate → no chip. Click jumps to the row.
  const mcpGateChip = useMemo(() => {
    if (mcpNotReady.length > 0) {
      return {
        tool: mcpNotReady[0]?.id,
        message: mcpNotReady.length === 1
          ? `Connection expired or invalid: ${mcpNotReady[0].name || mcpNotReady[0].id} — fix to execute.`
          : `${mcpNotReady.length} connections expired or invalid — fix to execute.`,
      };
    }
    if (mcpNeedsConfig.length > 0) {
      const names = Array.from(
        new Set(mcpNeedsConfig.map((m) => m.name || m.id).filter(Boolean)),
      );
      return {
        tool: mcpNeedsConfig[0]?.id,
        message: names.length === 1
          ? `Needs credential: ${names[0]} — configure in Connections to execute.`
          : `${names.length} connections need credentials — configure to execute.`,
      };
    }
    return null;
  }, [mcpNotReady, mcpNeedsConfig]);

  // GROUP all turns into RUNS (one collapsible block per run). A run = all turns sharing a runId;
  // turns with no runId (legacy/non-generative) fall under a synthetic key so they still render.
  // Runs ordered NEWEST-FIRST (latest run on top); turn order within a run preserved. Built from ALL
  // turnGroups — the cap below is applied on RUNS, not turns (a generative run has many turns; the
  // old per-turn cap could truncate the middle of one run and mis-count "older runs").
  const allRunBlocks = useMemo(() => {
    const order = [];
    const byRun = new Map();
    for (const t of turnGroups) {
      // Group by the run id. Prefer ``turn.runId`` (stamped when the run's top-level
      // EXECUTION_STARTED was seen), but FALL BACK to ``executionId``/``correlationId`` — for a
      // HISTORICAL run whose EXECUTION_STARTED fell outside the loaded window, ``runId`` is unset,
      // yet its agent events (AGENT_FINAL_RESPONSE/LOG) carry the top-level run id as
      // executionId/correlationId. Without this fallback those turns scatter into per-agent
      // singletons (the "3 individual GOOG blocks" bug). Only a truly id-less turn is a singleton.
      const rid = t.runId || t.executionId || t.correlationId || `__turn__${t.id}`;
      if (!byRun.has(rid)) { byRun.set(rid, []); order.push(rid); }
      byRun.get(rid).push(t);
    }
    // CHAT ORDER: turns arrive chronologically, so ``order`` is already OLDEST-first. Keep it —
    // oldest run on top, newest run at the bottom (like a chat log). Run 1 = oldest.
    return order.map((rid, i) => ({
      runId: rid,
      turns: byRun.get(rid),
      index: i + 1,                                 // Run 1 = oldest (top), highest = newest (bottom)
      isLiveRun: rid === currentRunId,
      releaseVersion: (runReleaseVersions && runReleaseVersions[rid]) || null,
      liveRoster: (agentRostersByRun && agentRostersByRun[rid]) || (rid === currentRunId ? mergedRoster : []),
    }));
  }, [turnGroups, currentRunId, runReleaseVersions, agentRostersByRun, mergedRoster]);

  // SINGLE-RUN VIEW (like the empty room): show ONLY the current/newest run, streaming at the top.
  // Every prior run is folded AWAY entirely (not rendered) — a new prompt is treated exactly like
  // a fresh room. Older runs reveal only when the user asks, via the "Load previous" button at the
  // top (``showPrevious``). Each new prompt RESETS this to the single-run view (see the effect that
  // watches ``currentRunId`` below), so sending always snaps back to "just the current one on top".
  const [showPrevious, setShowPrevious] = useState(false);
  // Newest run = last in the oldest-first ``allRunBlocks``. Collapse to it unless the user expanded.
  // FRESH-RUN CLEAR (render layer): while THIS send is booting, ``visibleRunBlocks`` returns [] so the
  // boot spinner + fresh graph sit on top on the USER event — not after the new run's AppSync events
  // land (the "old block lingers below the spinner" bug). Done HERE (render only), never on
  // ``combinedEnvelopes`` — that buffer must keep feeding ``uiState``/``currentRunId`` so the composer
  // stays frozen mid-run. The new run's first live event clears ``sending`` → its block renders.
  const runBlocks = useMemo(
    () => visibleRunBlocks(allRunBlocks, { sending, showPrevious }),
    [sending, showPrevious, allRunBlocks],
  );
  // Snap back to the single-run view (fold prior runs away) the MOMENT the user sends — do NOT
  // wait for the backend/AppSync pre-seed event to land. On an empty room the fold is invisible
  // (nothing to fold); on a room WITH history, waiting for ``currentRunId`` to change meant the
  // old runs stayed expanded until the pre-seed roster arrived (~seconds), so the new prompt only
  // jumped to the top late. ``sending`` flips true on Enter (handleSendPrompt), so folding here
  // moves the prompt to the top instantly. ``currentRunId`` stays in the deps so a run started by
  // a PEER (no local ``sending``) still folds when its run id arrives.
  useEffect(() => {
    setShowPrevious(false);
  }, [currentRunId, sending]);
  // Are there older runs hidden behind the fold? (drives the top "Load previous" button)
  const hasFoldedRuns = !showPrevious && allRunBlocks.length > 1;

  // Focus only matters when MORE THAN ONE run is on screen (a lone run is unambiguous). The
  // effective focus = the user's pick if it's still present, else the NEWEST run (last, since
  // ``runBlocks`` is oldest-first) — so on load the latest execution's graph is the highlighted one.
  const focusManaged = runBlocks.length > 1;
  const effectiveFocusedRunId = useMemo(() => {
    if (!focusManaged) return null;
    if (focusedRunId && runBlocks.some((rb) => rb.runId === focusedRunId)) return focusedRunId;
    return runBlocks[runBlocks.length - 1].runId;   // newest
  }, [focusManaged, focusedRunId, runBlocks]);

  // Render the CURRENT run's tree in the DOCKED panel when it has a roster + is the visible live run.
  const showRunHierarchy = Array.isArray(mergedRoster) && mergedRoster.length > 0
    && !!currentRunId
    && runBlocks.some((rb) => rb.runId === currentRunId);

  // Clear "Processing…" indicator once stream events start arriving
  useEffect(() => {
    if (sending && turnGroups.length > 0) {
      const latest = turnGroups[turnGroups.length - 1];
      if (latest.status === 'live') setSending(false);
    }
  }, [turnGroups, sending]);

  // BOOT TIMER: while a run is booting, tick once a second so the "0:07" elapsed clock on the
  // booting card visibly advances (proving the room is alive, not frozen). Stops the moment
  // ``sending`` clears (first live event or the 30s safety timeout) — no idle interval.
  useEffect(() => {
    if (!sending) return undefined;
    const id = setInterval(() => setBootSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [sending]);

  const resolvedBuilderCliWait = useMemo(() => {
    if (activeBuilderCliWait) return activeBuilderCliWait;
    const h = hydratedBuilderCliWait;
    if (
      h?.runId
      && typeof h.syntheticEventId === 'string'
      && h.syntheticEventId.length > 0
    ) {
      return {
        eventId: h.syntheticEventId,
        runId: h.runId,
        prompt: typeof h.prompt === 'string' ? h.prompt : '',
        interaction: h.interaction && typeof h.interaction === 'object' ? h.interaction : null,
      };
    }
    return null;
  }, [activeBuilderCliWait, hydratedBuilderCliWait]);

  // STOP SWITCH — BUILDER-ONLY. Cancel targets the builder's CodeBuild run, so
  // the band must appear ONLY while the BUILDER (claude-code) is running — NOT
  // during TeamLead/Test/other worker agents (generic uiState===RUNNING is the
  // whole network and is wrong here). Builder-specific signals:
  //   1. a live builder CLI wait (interactive approval) → definitely the builder
  //   2. the latest turn has BUILDER_PHASE events and the last one is NOT a
  //      terminal phase (COMPLETED) → builder still running
  const activeBuilderRunId = useMemo(() => {
    if (resolvedBuilderCliWait?.runId) return resolvedBuilderCliWait.runId;
    const latestTurn = Array.isArray(turnGroups) && turnGroups.length > 0
      ? turnGroups[turnGroups.length - 1] : null;
    const phases = latestTurn && Array.isArray(latestTurn.builderPhases)
      ? latestTurn.builderPhases : [];
    // Bind Stop to the explicit claude-code lifecycle the runner emits:
    // CLAUDE_CODE/started (eager, the instant the SDK launches) → COMPLETED at
    // end. Builder is "running" iff we've seen CLAUDE_CODE and NOT a terminal
    // COMPLETED after it. (Provisioning/clone phases alone don't show Stop —
    // claude-code isn't up yet; other agents never emit builder phases at all.)
    let claudeRunning = false;
    for (const ph of phases) {
      const phase = String(ph.phase || '').toUpperCase();
      const status = String(ph.status || '').toLowerCase();
      if (phase === 'CLAUDE_CODE') claudeRunning = true;
      if (phase === 'COMPLETED' || status === 'completed'
          || status === 'failed' || status === 'skipped') {
        claudeRunning = false;  // terminal — claude-code ended
      }
    }
    if (!claudeRunning) return null;
    // PREFER the builder's OWN runId (captured from BUILDER_PHASE payload.runId
    // == the runner's execution-name). This is the id the builder keys its
    // cancel row + kickoff on; Stop MUST send it. The turn/last-execution
    // ``executionId`` is a fallback only — it can be a runtime UUID that does
    // NOT match the builder's key, which silently no-ops the Stop (the poller
    // watches RUN#<execution-name> while the cancel API would write RUN#<uuid>).
    const rid = (typeof latestTurn.builderRunId === 'string' && latestTurn.builderRunId.trim())
      || (typeof latestTurn.executionId === 'string' && latestTurn.executionId.trim())
      || (typeof lastExecution?.executionId === 'string' && lastExecution.executionId.trim())
      || '';
    // [stop-diag] When claude-code is running, log the resolved target so we can
    // see in the console whether Stop has a runId to act on (and that the
    // CLAUDE_CODE phase actually arrived).
    if (rid) console.debug('[stop-diag] activeBuilderRunId =', rid);
    else console.warn('[stop-diag] claude running but NO builderRunId/executionId on turn — Stop would no-op');
    return rid || null;
  }, [resolvedBuilderCliWait, turnGroups, lastExecution?.executionId]);

  const [stopping, setStopping] = useState(false);
  const handleStopRun = useCallback(async () => {
    // [stop-diag] Trace every Stop click so we can see in the console whether
    // the handler fired, what runId it targeted, and the API outcome.
    console.info('[stop-diag] Stop clicked', {
      activeBuilderRunId, stopping, networkId, poolId,
    });
    if (!activeBuilderRunId || stopping) {
      console.warn('[stop-diag] Stop NO-OP — no activeBuilderRunId or already stopping', {
        activeBuilderRunId, stopping,
      });
      return;
    }
    setStopping(true);
    try {
      const resp = await cancelUniversalBuilderRun(networkId, { runId: activeBuilderRunId, poolId });
      console.info('[stop-diag] cancel API returned', resp);
    } catch (e) {
      // Non-fatal: surface but don't block — the CodeBuild hard-fallback may
      // still have fired server-side.
      console.error('[stop-diag] Stop run failed:', e);
    } finally {
      setStopping(false);
    }
  }, [activeBuilderRunId, stopping, networkId, poolId]);

  const builderCliPromptOpen = !!(
    resolvedBuilderCliWait
    && networkId
    && !handledBuilderWaitSet.has(resolvedBuilderCliWait.eventId)
  );

  // ── Composer builder band + Stop, hierarchy-aware ──────────────────────────
  // When the current run renders as a hierarchy tree, the builder controls come from the
  // multi-wait model: the interaction band shows the wait the user SELECTED on a roster row (one at
  // a time), and Stop is per-row inside the tree — NOT above the prompt. Otherwise (flat/legacy or
  // reconnect-hydrated), keep the single-wait path (``resolvedBuilderCliWait`` + above-prompt Stop).
  const selectedTreeWait = builderControls.selectedWait;
  const composerBuilderCliWait = showRunHierarchy
    ? (selectedTreeWait
      ? {
        eventId: selectedTreeWait.waitEventId,
        runId: selectedTreeWait.runId,
        prompt: selectedTreeWait.prompt || '',
        interaction: selectedTreeWait.interaction || null,
      }
      : null)
    : (builderCliPromptOpen ? resolvedBuilderCliWait : null);
  const composerOnBuilderCliHandled = showRunHierarchy
    ? builderControls.onSelectedWaitHandled
    : () => (resolvedBuilderCliWait ? markBuilderWaitHandled(resolvedBuilderCliWait.eventId) : undefined);
  // Above-prompt Stop only in the flat path; the tree carries per-row Stop.
  const composerActiveBuilderRunId = showRunHierarchy ? null : activeBuilderRunId;

  useEffect(() => {
    if (!networkId || activeBuilderCliWait) {
      setHydratedBuilderCliWait(null);
      return undefined;
    }

    // Only hydrate from Dynamo on initial mount / reconnect — not on a poll loop.
    // The live path is fully event-driven via AppSync BUILDER_INPUT_WAIT.
    // This one-shot check covers the case where the page refreshed while an
    // approval prompt was active and the AppSync event was missed.
    const ls = typeof lastExecution?.status === 'string' ? lastExecution.status.toUpperCase() : '';
    const us = String(uiState || '').toUpperCase();
    const execLooksLive = ls === 'RUNNING' || us === 'RUNNING';
    if (!execLooksLive) {
      setHydratedBuilderCliWait(null);
      return undefined;
    }

    // Only check if we have builder terminal lines in the current turn —
    // this means a builder is actually running (not just a Think agent).
    const latestTurn = Array.isArray(turnGroups) && turnGroups.length > 0
      ? turnGroups[turnGroups.length - 1]
      : null;
    const hasBuilderActivity = latestTurn && (
      (Array.isArray(latestTurn.builderTerminalLines) && latestTurn.builderTerminalLines.length > 0)
      || (Array.isArray(latestTurn.builderPhases) && latestTurn.builderPhases.length > 0)
    );
    if (!hasBuilderActivity) {
      setHydratedBuilderCliWait(null);
      return undefined;
    }

    let runIdRaw = typeof lastExecution?.executionId === 'string' ? lastExecution.executionId.trim() : '';
    if (!runIdRaw && latestTurn) {
      runIdRaw = typeof latestTurn.executionId === 'string' ? latestTurn.executionId.trim() : '';
    }
    if (!runIdRaw) {
      setHydratedBuilderCliWait(null);
      return undefined;
    }

    let cancelled = false;

    async function hydrateOnce() {
      try {
        const slot = await fetchBuilderCliSlot(networkId, runIdRaw, poolId);
        if (cancelled) return;
        const awaiting =
          Boolean(slot?.inputWaitActive) && slot?.hasLine !== true;
        if (awaiting) {
          const wo = Number(slot?.waitOpenedAt ?? 0) || 0;
          const ps = typeof slot?.promptSnippet === 'string' ? slot.promptSnippet : '';
          const ix = slot?.interaction && typeof slot.interaction === 'object' ? slot.interaction : null;
          setHydratedBuilderCliWait({
            syntheticEventId: `hydrated-builder-cli:${runIdRaw}:${wo}`,
            runId: runIdRaw,
            prompt: ps,
            interaction: ix,
          });
        } else {
          setHydratedBuilderCliWait(null);
        }
      } catch {
        if (!cancelled) setHydratedBuilderCliWait(null);
      }
    }

    hydrateOnce();
    return () => { cancelled = true; };
  }, [activeBuilderCliWait, networkId, poolId, lastExecution?.executionId, lastExecution?.status, turnGroups.length, uiState]);

  /** Suppress the reducer's pendingApproval until the matching
   *  APPROVAL_RESOLVED has propagated; otherwise the UI flickers back to
   *  AWAITING_APPROVAL between click and echo. */
  const pendingApproval = useMemo(() => {
    const p = room.pendingApproval;
    if (!p) return null;
    if (resolvedTaskIds.includes(p.taskId)) return null;
    return p;
  }, [room.pendingApproval, resolvedTaskIds]);

  /** Drop optimistic bubbles whose correlationId now appears in the
   *  reduced feed (the backend echoed our prompt). */
  useEffect(() => {
    if (optimisticBubbles.length === 0) return;
    const confirmedCorrelations = new Set(
      room.feed
        .filter((e) => e.kind === 'user' && e.eventId && e.correlationId)
        .map((e) => e.correlationId),
    );
    if (confirmedCorrelations.size === 0) return;
    setOptimisticBubbles((prev) => prev.filter((b) => !confirmedCorrelations.has(b.correlationId)));
  }, [room.feed, optimisticBubbles.length]);

  /** Once APPROVAL_RESOLVED has been processed by the reducer the row
   *  drops from `room.pendingApproval` — clear our local override so a
   *  subsequent fresh approval can show. */
  useEffect(() => {
    if (resolvedTaskIds.length === 0) return;
    if (room.pendingApproval) return;
    setResolvedTaskIds([]);
  }, [room.pendingApproval, resolvedTaskIds.length]);

  /** Pin the bottom: as content arrives (new turn, builder terminal lines,
   *  agent text, thinking lines) keep the latest message just above the
   *  composer so nothing gets hidden behind it. The live-content signal
   *  bumps whenever the active turn grows, not just on new-turn opens. */
  const liveTurn = turnGroups.length > 0 ? turnGroups[turnGroups.length - 1] : null;
  // FINAL SUMMARY autoscroll: the synthesis streams token-by-token into the Summary roster row's
  // ``streamText`` (SUMMARY_DELTA), NOT into agentEntries — so the counts below never move while
  // the answer composes, and the growing text slid UNDER the composer with no scroll. Fold the
  // summary's LENGTH into the signal so each streamed chunk re-pins the bottom (the block sits just
  // above the prompt box as it writes). Length (not identity) is the signal: it bumps per chunk.
  const _summaryStreamLen = (() => {
    const rows = Array.isArray(agentRoster) ? agentRoster : [];
    const s = rows.find((r) => r && r.agentId === 'Summary');
    return (s && typeof s.streamText === 'string') ? s.streamText.length : 0;
  })();
  // STRUCTURAL signal — new turn / new agent / thinking. These are discrete events; a SMOOTH
  // scroll to them reads well. Two per-stream growths are DELIBERATELY excluded because each streams
  // inside its OWN fixed-height scroll window (see AgentHierarchyPanel / BuilderStreamTerminal), so
  // the page does not grow as they emit and needs no per-chunk page re-scroll:
  //   * the summary's per-character growth, and
  //   * the BUILDER terminal's per-line growth (``builderTerminalLines``). Including the latter made
  //     every builder cc-line re-pin the whole conversation to the bottom — dragging the network
  //     block down so the user had to scroll back up to watch the builder stream. The builder's own
  //     200px ``overflow-y-auto`` container auto-scrolls internally; the page must stay put.
  const liveContentSignal =
    (liveTurn?.agentEntries?.length || 0) * 100
    + (liveTurn?.thinking?.length || 0) * 13;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turnGroups.length, liveContentSignal, pendingApproval, uiState]);
  void _summaryStreamLen;   // retained for potential future use; no longer drives page scroll
  // OPEN AT THE NEWEST (Slack): when a room's first discussion page lands, land at the bottom once — scroll-back
  // paging then loads older pages only as the user scrolls up.
  const _openedAtBottom = useRef('');
  useEffect(() => {
    const key = `${networkId}:${poolId || ''}`;
    if (discussion.loading || !(discussion.messages || []).length || _openedAtBottom.current === key) return;
    _openedAtBottom.current = key;
    const root = streamScrollRef.current;
    if (root) root.scrollTop = root.scrollHeight;
  }, [networkId, poolId, discussion.loading, discussion.messages]);

  // NO BLACK BOX (L60): the Tasks-tab "plan updating…" banner carries a "Planner agent" chip —
  // clicking it jumps to the Activities/stream lane and scrolls to the live bottom, where the async
  // planner streams as the "Planner" builder card. Same affordance as watching any builder agent.
  const openPlannerStream = useCallback(() => {
    setRoomTabIndex(0);   // Activities (the stream lane)
    // Scroll AFTER the tab switch renders — the stream lane must be mounted/visible first.
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 350);
  }, []);

  // Approval claim derivations -----------------------------------------------
  const pendingTaskId = pendingApproval?.taskId || '';
  // STAND-IN GATES (STAND-IN.md §7): the supervisors' stand-ins pre-review the open Run gate + the Plan milestone gate
  const runGateReviews = useGateAdvice(networkId, poolId, 'run', pendingTaskId, messages);
  const planGateReviews = useGateAdvice(networkId, poolId, 'plan', (approvableMilestone && approvableMilestone.id) || '', messages);
  const currentLease = pendingTaskId ? getApprovalLease(pendingTaskId) : null;
  const iHoldClaim = pendingTaskId ? isSelfClaimer(pendingTaskId) : false;
  const peerHoldsClaim = !!(
    currentLease && currentLease.live && currentLease.claimerSub && !iHoldClaim
  );
  const peerLabel = peerHoldsClaim
    ? currentLease.claimerEmail || currentLease.claimerSub?.slice(0, 8) || 'a peer'
    : '';

  // Composer callbacks -------------------------------------------------------
  const handleSendPrompt = useCallback(
    async (text, { stagingId, attachments, correlationId, planMode, taskBinding, taskId, taskIds, groomScope, planTier, mentions } = {}) => {
      if (!text) return;
      // RE-ENTRY GUARD: a room runs ONE task at a time. Block a second Send while THIS send is still
      // booting (``sending``) or a run is live (``uiState==='RUNNING'``) — belt-and-suspenders with
      // the composer's disabled Send, and it stops the "spinner not seen → user hits Send again →
      // server 409 'a run is already in progress'" loop by rejecting locally with a clear message.
      if (shouldBlockSend({ sending, uiState })) {
        pushLocalSystemEvent('A run is already in progress in this room — rooms run one task at a time. Wait for it to finish.');
        return;
      }
      // AUTO-NAV (UX): a Send from the Tasks tab (or anywhere but Activities) jumps to Activities so
      // the user SEES the run stream — the payoff moment. The shared composer stays put (footer).
      setRoomTabIndex(0);
      setSending(true);
      // BOOTING CARD: reset the elapsed clock, count artifacts, reset "accepted" — the card shows a
      // moving elapsed timer + staged copy until the first live stream event clears ``sending``.
      setBootSecs(0);
      setBootAccepted(false);
      setBootArtifacts(Array.isArray(attachments) ? attachments.length : 0);
      // Optimistic bubble — appears in the feed instantly so the user sees
      // their turn. Reconciled by `correlationId` once the backend lands a
      // ``publish_user_prompt`` echo (still pending in some paths; the
      // reducer carries the bubble forward gracefully until then).
      const bubble = freshOptimisticBubble({
        text,
        actorName: user?.email || user?.name || 'You',
        correlationId,
      });
      setOptimisticBubbles((prev) => [...prev, bubble]);

      // Mirror the Network Workbench ``Run`` tab shape: ``input.tenantId`` is
      // the knowledge / storage tenant. For agentic rooms that is the pool id
      // (e.g. ``milo``) — the same value the server injects on
      // ``/pools/{poolId}/execute``; including it here keeps DevTools and
      // tracing aligned with a direct ``POST /networks/{id}/execute`` run
      // where you typed a tenant in the workbench.
      // ``source: 'ui'`` tags the run's Trigger provenance as UI (vs API for
      // direct/SDK callers, JOB for the scheduler). Attribution for the
      // Executions tab — not a security boundary.
      const body = { input: { prompt: text, tenantId: poolId }, logLevel: 'INFO', source: 'ui' };
      if (Array.isArray(mentions) && mentions.length) body.mentions = mentions;   // people tagged — notified (MENTIONS.md)
      const _hasAttachments = Array.isArray(attachments) && attachments.length > 0;
      let _ctxLabel = '';
      if (_hasAttachments) {
        body.attachmentStagingId = stagingId;
        body.attachmentInput = buildAttachmentInputBody(attachments);
        const _first = attachments[0] && (attachments[0].filename || attachments[0].fileName);
        _ctxLabel = attachments.length === 1
          ? (_first || 'your upload')
          : `${attachments.length} uploads`;
      }
      // PLAN MODE (§planner): "Run Planner" fires the network in plan mode — the leads run as
      // claude-code planners (write nothing) and detail/add DRAFT tasks into the canonical plan the
      // Tasks tab shows. The backend routes the plan to the __planner__ ledger regardless of poolId.
      if (planMode) body.planMode = true;
      // GROOM SCOPE (§2.6): a surgical grooming run — plan mode PLUS a specific milestone + task ids
      // the user selected in the Tasks tab. The reducer confines the plan to these (write-gate +
      // scopeAssertion); the leads' brief scopes to exactly these tasks. Only meaningful with planMode.
      if (planMode && groomScope && groomScope.milestone) {
        body.groomScope = {
          milestone: String(groomScope.milestone),
          taskIds: Array.isArray(groomScope.taskIds) ? groomScope.taskIds.map(String) : [],
          // WAVE PARTITION (§2.8): the runner expands waves → task ids deterministically.
          ...(Array.isArray(groomScope.waves) && groomScope.waves.length
            ? { waves: groomScope.waves.map((w) => Number(w)).filter((w) => Number.isFinite(w)) }
            : {}),
          // EPIC PARTITION (§0.1): a selected epic (story) — the runner expands it → its task ids.
          ...(groomScope.epic ? { epic: String(groomScope.epic) } : {}),
          // EPIC EXPANSION (§0.1): author leaf children under this epic (id) — only leaves execute.
          ...(groomScope.epicParent ? { epicParent: String(groomScope.epicParent) } : {}),
        };
      }
      // PLAN TIER (PLAN-INTEGRATION.md §2.5): the user's per-milestone tier choice ('standard' |
      // 'verified'); 'auto'/absent → omit (runner uses the architect hint + backstop). Only in plan mode.
      if (planMode && (planTier === 'standard' || planTier === 'verified')) {
        body.planTier = planTier;
      }
      // TASK BINDING (Unit F): a PROJECT run targets a single task ('task' + optional taskId;
      // '' id = auto next-ready) or the whole ready set ('wave'). The runner reads these; a
      // non-project run omits them (legacy). Never sent alongside planMode (planner runs the leads).
      Object.assign(body, taskBindingFields(planMode, taskBinding, taskId, taskIds));
      // §26.4 SERIAL HANDSHAKE: hold the run until the room-uploads context (kicked at upload time)
      // is ready — the worker's CONTEXT_READY beacon resolves this; a 25s safety timeout FAILS OPEN
      // so a missed/slow beacon never wedges Send (context then lands next turn, the pre-§26 path).
      //   #1 beacon NOT yet arrived → show the "preparing context" spinner, release the instant it
      //      arrives (or on the safety timeout).
      //   #2 beacon already arrived → NO spinner, trigger immediately (awaitContextReady resolves sync).
      if (_hasAttachments && stagingId) {
        const _alreadyReady = contextReadyRef.current.has(stagingId);
        if (!_alreadyReady) setContextBuilding({ label: _ctxLabel });
        await awaitContextReady(stagingId, 25000);
        if (!_alreadyReady) setContextBuilding(null);
      }
      try {
        const _kick = await poolExecute(networkId, poolId, body);
        // SEED THE RUN ID FROM THE KICK (ordering-proof): the execute response carries the run's
        // ``executionName`` (= the runId all its events use — engine_agent §runId / USER_PROMPT
        // correlation). Hand it to the reducer as ``pendingRunId`` so the run's FIRST event
        // force-starts its block regardless of AppSync delivery order — the artifact-run standby fix.
        const _runId = String(
          (_kick && (_kick.executionName || _kick.taskId)) ||
          (_kick && typeof _kick.executionArn === 'string' ? _kick.executionArn.split(':').pop() : '') || '',
        ).trim();
        if (_runId) setKickedRunId(_runId);
        // StartExecution accepted — the run exists server-side even before the first event streams.
        // Flip the card to "waking agents" so the user sees a concrete milestone, not a static wait.
        setBootAccepted(true);
        // Don't clear sending here — let the first stream event clear it.
        // Safety timeout: clear after 30s if no events arrive.
        setTimeout(() => setSending(false), 30000);
      } catch (e) {
        console.error(e);
        setSending(false);
        setOptimisticBubbles((prev) => prev.filter((entry) => entry.id !== bubble.id));
        if (e instanceof IncludedTokensExceededError) {
          pushLocalSystemEvent(
            'Run blocked: this organization has used all included tokens for this billing period. An admin can upgrade the plan to keep running.',
          );
        } else {
          pushLocalSystemEvent(e.message || 'Execution failed');
          // MCP_NOT_READY / TOOL_NOT_CONFIGURED: a stale-cache race let Send through but the trigger
          // gate rejected it. Re-pull the readiness status so the composer shows the gate + the fix.
          if (e && (e.code === 'MCP_NOT_READY' || e.code === 'TOOL_NOT_CONFIGURED')) {
            try { refreshMcpGate(); } catch { /* best-effort */ }
          }
        }
      }
    },
    [networkId, poolId, user?.email, user?.name, pushLocalSystemEvent, refreshMcpGate, awaitContextReady,
     sending, uiState],
  );

  // WAVE AUTO-SEQUENCE (MILESTONE-SCOPED-PLANNING §wave-auto): "Plan all waves" fires a plan run for
  // wave 0 with ``autoWaveSeq:true`` — the runner seeds the AUTOWAVE marker and the final reducer
  // auto-fires the remaining waves server-side (survives tab close). A dedicated fire (not a composer
  // Send) since there's no prose to type; a synthetic prompt labels the run.
  const handlePlanAllWaves = useCallback(async (milestone) => {
    if (!milestone) return;
    setRoomTabIndex(0);   // AUTO-NAV: fired from Tasks → jump to Activities to watch the wave stream
    setSending(true);
    setBootSecs(0);
    setBootAccepted(false);
    setBootArtifacts(0);
    try {
      await poolExecute(networkId, poolId, {
        input: { prompt: `Plan all waves of ${milestone}`, tenantId: poolId },
        logLevel: 'INFO', source: 'ui',
        planMode: true,
        autoWaveSeq: true,
        // (L122) no wave number here: the RUNNER resolves the milestone's real (1-based) waves, plans the
        // first, and seeds the chain — the old ``waves:[0]`` matched no task and replanned the whole milestone.
        groomScope: { milestone: String(milestone), taskIds: [] },
      });
      setBootAccepted(true);
      setTimeout(() => setSending(false), 30000);
    } catch (e) {
      setSending(false);
      pushLocalSystemEvent(e.message || 'Plan-all-waves failed');
    }
  }, [networkId, poolId, pushLocalSystemEvent]);

  // Stop a running auto-wave sequence (clears the marker → the chain dies at the next boundary).
  const handleStopAutoWave = useCallback(async (milestone) => {
    if (!milestone) return;
    try {
      await stopAutoWavePlan(networkId, milestone, poolId ? { pool: poolId } : {});
    } catch (e) {
      pushLocalSystemEvent(e.message || 'Stop auto-plan failed');
    }
  }, [networkId, poolId, pushLocalSystemEvent]);

  const handleClaim = useCallback(async () => {
    if (!pendingTaskId) return;
    setApprovalBusy(true);
    try {
      await claimApprovalLease(pendingTaskId);
    } catch (e) {
      pushLocalSystemEvent(e?.data?.message || e.message || 'Could not claim approval');
    } finally {
      setApprovalBusy(false);
    }
  }, [pendingTaskId, claimApprovalLease, pushLocalSystemEvent]);

  const handleEscalate = useCallback(async () => {
    if (!pendingTaskId) return;
    setApprovalBusy(true);
    try {
      await escalateApprovalLease(pendingTaskId, {
        reason: 'Escalated from the room',
      });
      pushLocalSystemEvent('Approval escalated. Workspace admins will see it in Overwatch.');
    } catch (e) {
      pushLocalSystemEvent(e.message || 'Escalation failed');
    } finally {
      setApprovalBusy(false);
    }
  }, [pendingTaskId, escalateApprovalLease, pushLocalSystemEvent]);

  const handleDelegate = useCallback(() => {
    if (!pendingTaskId || !iHoldClaim) return;
    setDelegateDialogOpen(true);
  }, [pendingTaskId, iHoldClaim]);

  const handleDelegateSubmit = useCallback(
    async ({ toSub, toEmail, reason }) => {
      if (!pendingTaskId || !iHoldClaim) {
        throw new Error(
          iHoldClaim
            ? 'No pending approval to delegate.'
            : 'Claim has been reassigned — reclaim first.',
        );
      }
      setApprovalBusy(true);
      try {
        await delegateApprovalLease(pendingTaskId, { toSub, toEmail, reason });
        pushLocalSystemEvent(`Delegated to ${toEmail || toSub}.`);
      } finally {
        setApprovalBusy(false);
      }
    },
    [pendingTaskId, iHoldClaim, delegateApprovalLease, pushLocalSystemEvent],
  );

  const handleApprovalDecision = useCallback(
    async (decision) => {
      if (!pendingApproval?.taskToken) return;
      if (!iHoldClaim) {
        pushLocalSystemEvent('Claim the approval before resolving it.');
        return;
      }
      const taskId = pendingApproval.taskId;
      setApprovalBusy(true);
      try {
        await poolApproval(networkId, poolId, {
          taskToken: pendingApproval.taskToken,
          taskId: taskId || undefined,
          decision,
        });
        if (taskId) setResolvedTaskIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]));
      } catch (e) {
        pushLocalSystemEvent(e.message || 'Approval failed');
      } finally {
        setApprovalBusy(false);
      }
    },
    [networkId, poolId, pendingApproval, iHoldClaim, pushLocalSystemEvent],
  );

  const handleSendModify = useCallback(
    async (text /* , { stagingId, attachments } — see Composer comment */) => {
      if (!pendingApproval?.taskToken || !text) return;
      if (!iHoldClaim) {
        pushLocalSystemEvent('Claim the approval before sending feedback.');
        return;
      }
      const taskId = pendingApproval.taskId;
      setApprovalBusy(true);
      try {
        await poolApproval(networkId, poolId, {
          taskToken: pendingApproval.taskToken,
          taskId: taskId || undefined,
          decision: 'MODIFY',
          feedback: text,
        });
        if (taskId) setResolvedTaskIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]));
      } catch (e) {
        pushLocalSystemEvent(e.message || 'Modify failed');
      } finally {
        setApprovalBusy(false);
      }
    },
    [networkId, poolId, pendingApproval, iHoldClaim, pushLocalSystemEvent],
  );

  const canManagePoolSettings = useMemo(
    () =>
      (poolRole || '').toUpperCase() === 'CREATOR'
      || ['OWNER', 'ADMIN'].includes(String(workspaceRole || '').toUpperCase()),
    [poolRole, workspaceRole],
  );

  // ROOM-SCOPES Phase 1 — tree context-menu actions (disable-branch / disable-self / enable).
  // Optimistic: patch the local map immediately (the tree dims on click), call the API, and roll
  // back on failure. Peers converge via POOL_AGENT_DISABLE_CHANGED (see the envelope watcher
  // above). Only wired into the tree when ``canManagePoolSettings`` — same authority as the
  // Settings drawer (the backend enforces it regardless).
  const handleAgentDisableAction = useCallback(async (agentId, action) => {
    if (!agentId) return;
    // TWO-TIER guard: a 'frozen' disable is the room's immutable carve — the owner can't
    // enable it here (the backend also 409s). Block the enable action + tell the user.
    if (action === 'enable' && agentDisableSources[agentId] === 'frozen') {
      pushLocalSystemEvent(`${agentId} is disabled by the room's frozen carve — re-release the room to change it.`);
      return;
    }
    const prevMode = agentDisables[agentId] || null;
    const nextMode = action === 'disable-branch' ? 'branch'
      : action === 'disable-self' ? 'self' : null;
    // Optimistic update — the node dims (or un-dims) the instant the user picks the action.
    // Owner actions are always the toggleable 'room' tier.
    setAgentDisables((prev) => {
      const next = { ...prev };
      if (nextMode) next[agentId] = nextMode;
      else delete next[agentId];
      return next;
    });
    setAgentDisableSources((prev) => {
      const next = { ...prev };
      if (nextMode) next[agentId] = 'room';
      else delete next[agentId];
      return next;
    });
    try {
      if (nextMode) {
        const res = await setPoolAgentDisable(networkId, poolId, agentId, nextMode);
        // The server may COERCE the mode (e.g. 'self' on a leaf → 'branch') — reflect what stuck.
        if (res?.mode && res.mode !== nextMode) {
          setAgentDisables((prev) => ({ ...prev, [agentId]: res.mode }));
        }
        pushLocalSystemEvent(`${agentId} disabled for this room — takes effect on the next run.`, { ttlMs: 4000 });
      } else {
        await clearPoolAgentDisable(networkId, poolId, agentId);
        pushLocalSystemEvent(`${agentId} enabled — takes effect on the next run.`, { ttlMs: 4000 });
      }
      // Recompute the credential run-gate NOW — a disabled agent's unconfigured tool must stop
      // blocking (and re-enabling one must re-block) on the fly, not "next run". The backend gate
      // reads LIVE disable rows (``_disabled_agent_ids_for_room``), so we refetch only AFTER the
      // write lands — an earlier refetch would race the server and read the pre-toggle state.
      refreshMcpGate();
    } catch (e) {
      // Roll the optimistic patch back so the tree matches the server again.
      // eslint-disable-next-line no-console
      console.warn('[room] agent disable action failed:', e);
      setAgentDisables((prev) => {
        const next = { ...prev };
        if (prevMode) next[agentId] = prevMode;
        else delete next[agentId];
        return next;
      });
      refreshAgentDisables();   // re-sync sources from the server (e.g. a rejected frozen clear)
    }
  }, [agentDisables, agentDisableSources, networkId, poolId, pushLocalSystemEvent, refreshAgentDisables, refreshMcpGate]);

  // RESULT REUSE (RESULT-REUSE.md) — tree context-menu actions ('reuse' | 'stop-reuse'). Optimistic
  // patch + API + rollback, mirroring the disable handler. Peers converge via the shared
  // POOL_AGENT_DISABLE_CHANGED beacon (mode='reuse') watched above. Project/phased networks only.
  const handleAgentReuseAction = useCallback(async (agentId, action) => {
    if (!agentId) return;
    const prev = agentReuses[agentId] || null;
    if (action === 'reuse') {
      // Optimistic: ring it now (runId filled by the server response / next refresh).
      setAgentReuses((m) => ({ ...m, [agentId]: { capturedMilestone: '', capturedRunId: '' } }));
      try {
        const res = await setPoolAgentReuse(networkId, poolId, agentId);
        setAgentReuses((m) => ({ ...m, [agentId]: {
          capturedMilestone: res?.capturedMilestone || '',
          capturedRunId: res?.capturedRunId || '',
        } }));
        pushLocalSystemEvent(`${agentId} will reuse its last result next run.`, { ttlMs: 4000 });
      } catch (e) {
        setAgentReuses((m) => { const n = { ...m }; if (prev) n[agentId] = prev; else delete n[agentId]; return n; });
        const msg = String(e?.message || '');
        pushLocalSystemEvent(
          msg.includes('NotAProjectNetwork')
            ? 'Reuse last result applies only to project (phased) networks.'
            : `Couldn't set reuse for ${agentId}.`,
          { ttlMs: 5000 });
      }
    } else {  // 'stop-reuse'
      setAgentReuses((m) => { const n = { ...m }; delete n[agentId]; return n; });
      try {
        await clearPoolAgentReuse(networkId, poolId, agentId);
        pushLocalSystemEvent(`${agentId} will run fresh next run.`, { ttlMs: 4000 });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[room] stop-reuse failed:', e);
        if (prev) setAgentReuses((m) => ({ ...m, [agentId]: prev }));
        refreshAgentReuses();
      }
    }
  }, [agentReuses, networkId, poolId, pushLocalSystemEvent, refreshAgentReuses]);

  // Connector management is wider than roster management: pool BUILDER also
  // qualifies, since they're the role that responds to Universal Builder
  // approvals at runtime and may need to swap credentials mid-flight.
  const canManageConnectors = useMemo(
    () => canManageRoomConnectors(poolRoles, workspaceRole),
    [poolRoles, workspaceRole],
  );

  const canExecute = useMemo(() => {
    // Two-axis gate (Phase B): identity × trigger mode.
    //
    // 1. Identity (Phase A): only the pool CREATOR can start a run from
    //    the prompt box. Pool SUPERVISORs see the box at IDLE in
    //    disabled / read-only mode; they only get an editable prompt
    //    during an AWAITING_APPROVAL + Modify turn (gated independently
    //    in `composerState.js`).
    // 2. Trigger mode (Phase B): pools released as HEADLESS reject UI-
    //    driven starts entirely — the prompt box is read-only for every
    //    member at IDLE, regardless of role. Approvals still work
    //    (mirrors the backend's intent: "humans approve, robots start").
    //    HYBRID and MANUAL both allow CREATOR-driven manual starts.
    //
    // Workspace OWNER / ADMIN bypass the pool-role check via Overwatch
    // for break-glass reasons, but the trigger-mode gate still applies
    // to them — admins flipping a pool to HEADLESS are explicitly opting
    // every human (including themselves) out of the manual path.
    //
    // The UI gate is purely for clarity — the backend's
    // `pool_execute_handler` is the security boundary.
    if (triggerMode === TRIGGER_HEADLESS) return false;
    const wr = String(workspaceRole || '').toUpperCase();
    if (wr === 'VIEWER') return false;
    if (wr === 'OWNER' || wr === 'ADMIN') return true;
    const pr = String(poolRole || '').toUpperCase();
    return pr === 'CREATOR';
  }, [triggerMode, workspaceRole, poolRole]);

  // Lifecycle freeze + banner (M5).
  // ``isAdmin`` only matters for the "should this row be hidden from
  // the sidebar?" decision — the chat surface here is post-routing,
  // so we always render the banner / freeze for the explicit status.
  const lifecycleUi = useMemo(
    () => projectRoomLifecycleUiState(
      { lifecycleStatus, inactivityWarning },
      { isAdmin: ['OWNER', 'ADMIN'].includes(String(workspaceRole || '').toUpperCase()) },
    ),
    [lifecycleStatus, inactivityWarning, workspaceRole],
  );
  const lifecycleFrozen = lifecycleUi.frozen;

  const roomTitle = displayName || `${networkId} · ${poolId}`;

  // ``networkAsl`` (topology) is fetched above (near the seeded roster) and reused here for
  // ``networkAsl`` (topology) is fetched above (near the seeded roster) and feeds the docked
  // live agent-tree; the tree's per-node status comes from ``treeStatusByAgentId`` (merged roster).

  // ROOM HEADER + TABS — rendered in place (full) or into the shell's compact dock row (ROOMS.md §2.1).
  const roomTabClass = (sel) => (compact
    ? `shrink-0 inline-flex items-center rounded px-1.5 py-1 transition-colors ${sel ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`
    : `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
      sel ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'}`);
  const tabLabel = (text) => (compact ? <span className="sr-only">{text}</span> : text);
  const roomHeaderRow = (
        <div className={compact ? 'flex min-w-0 items-center gap-2' : 'flex min-h-[36px] items-center justify-between gap-2'}>
          <div className="flex min-w-0 items-center gap-2">
            {/* Room's universal avatar — a circle in its NETWORK's hue family
                (same identity as the dashboard room row), so a room looks the
                same everywhere and visibly belongs to its network. */}
            {networkId && poolId ? (
              <RoomAvatar networkId={networkId} poolId={poolId} name={displayName} size={compact ? 18 : 24} />
            ) : null}
            <h1 className={`truncate font-semibold leading-tight text-gray-900 ${compact ? 'max-w-[200px] text-sm' : 'text-base'}`} title={roomTitle}>
              {roomTitle}
            </h1>
            {/* Version label — sits next to the room name so users always
                see which slice of the network this room is talking to. */}
            {versionLabel ? (
              <span
                className={`shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium ring-1 ${
                  connected
                    // Green when subscribed to AppSync — the version chip IS the
                    // room's live-connection indicator (no separate dot here).
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                    : 'bg-blue-50 text-blue-700 ring-blue-200'
                }`}
                title={
                  connected
                    ? `Release version this room is pinned to · AppSync subscribed`
                    : `Release version this room is pinned to · AppSync connecting…`
                }
              >
                v{versionLabel}
              </span>
            ) : null}
            {/* Room UI-state chip — sits right of the Version chip. Friendly label, never the
                raw engine enum (e.g. "AWAITING_INITIAL_INPUT" → "Ready"). */}
            {uiState ? (
              <span
                className="shrink-0 rounded-full bg-slate-200/80 px-1.5 py-px text-[10px] font-medium text-slate-800"
                title={`Room state: ${friendlyUiState(uiState)}`}
              >
                {friendlyUiState(uiState)}
              </span>
            ) : null}
            {/* Economy/Test mode chip — shown only when this room runs cheap (the frozen
                Shared setting). Amber so it reads as an active cost-saving mode, not an error. */}
            {economyMode ? (
              <span
                className="shrink-0 rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-medium text-amber-800 ring-1 ring-amber-200"
                title="Economy mode: this room runs on the fastest, lowest-cost model with concise answers. Turn off in the workbench Shared settings for full-quality runs."
                data-testid="room-economy-chip"
              >
                {compact ? 'Eco' : 'Economy'}
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* DYNAMIC NETWORK VIEW (DYNAMIC-NETWORK-VIEW) — icon toggle beside Settings: "Engaged" shows just the
                agents the Lead engaged (+ancestors+root leads), as they engage; "Full" the whole roster. Controls the
                MAIN-WINDOW roster only; the docked diagram always renders the full topology. */}
            {isManifest(networkAsl) ? (
              <div role="radiogroup" aria-label="Roster view"
                className="inline-flex items-center rounded-md border border-gray-300 bg-white p-0.5">
                <button
                  type="button"
                  role="radio"
                  aria-checked={dynamicView}
                  aria-label="Engaged agents"
                  data-testid="netview-dynamic"
                  onClick={() => setDynamicView(true)}
                  className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 transition-colors ${dynamicView ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Engaged — the roster shows only the agents engaged this run, as they engage"
                >
                  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
                    <circle cx="8" cy="3.5" r="1.75" />
                    <path d="M8 5.25v3.5" />
                    <circle cx="8" cy="11" r="1.75" fill="currentColor" />
                  </svg>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!dynamicView}
                  aria-label="Full team"
                  data-testid="netview-full"
                  onClick={() => setDynamicView(false)}
                  className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 transition-colors ${!dynamicView ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Full — the roster shows every agent (the whole team)"
                >
                  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
                    <circle cx="8" cy="3" r="1.5" />
                    <path d="M8 4.5V7M4 9V7h8v2" />
                    <circle cx="4" cy="11" r="1.5" />
                    <circle cx="8" cy="11" r="1.5" />
                    <circle cx="12" cy="11" r="1.5" />
                  </svg>
                </button>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => setMembersDrawerOpen(true)}
              aria-label={compact ? 'Settings' : undefined}
              className={`inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 ${compact ? 'px-1.5 py-1' : 'px-2.5 py-1'}`}
              title="Members, trigger mode, agent supervisors, and connector overrides"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5 opacity-70"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h.01a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
              {compact ? <span className="sr-only">Settings</span> : 'Settings'}
              {typeof memberCount === 'number' ? (
                <span
                  className="rounded-full bg-gray-100 px-1.5 text-[10px] font-semibold text-gray-600"
                  title={`${memberCount} active member${memberCount === 1 ? '' : 's'}`}
                >
                  {memberCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => setContextDrawerOpen(true)}
              aria-label={compact ? 'View context' : undefined}
              className={`inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 ${compact ? 'px-1.5 py-1' : 'px-2.5 py-1'}`}
              title="View context — exactly as the agent receives it (RULES verbatim, REFERENCE retrieved at runtime)"
            >
              {compact ? <><FileSearch className="h-3.5 w-3.5 opacity-70" aria-hidden /><span className="sr-only">View context</span></> : 'View context'}
            </button>
          </div>
        </div>
  );
  const roomTabList = (
<Tab.List className={compact ? 'flex min-w-0 items-center gap-0.5' : '-mb-px flex w-full min-w-0 flex-wrap items-center gap-x-1 overflow-x-auto pb-px sm:gap-x-2'}>
            <Tab
              title={compact ? 'Activities' : undefined}
              className={({ selected: sel }) => roomTabClass(sel)}
            >
              <List className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              {tabLabel('Activities')}
            </Tab>
            <Tab
              title={compact ? 'Context' : undefined}
              className={({ selected: sel }) => roomTabClass(sel)}
            >
              <Database className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              {tabLabel('Context')}
            </Tab>
            <Tab
              title={compact ? 'Connections' : undefined}
              className={({ selected: sel }) => roomTabClass(sel)}
            >
              <Plug className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              {tabLabel('Connections')}
            </Tab>
            <Tab
              title={compact ? 'Workspace' : undefined}
              className={({ selected: sel }) => roomTabClass(sel)}
            >
              <FolderTree className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              {tabLabel('Workspace')}
            </Tab>
            <Tab
              title={compact ? 'Executions' : undefined}
              className={({ selected: sel }) => roomTabClass(sel)}
            >
              <History className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              {tabLabel('Executions')}
            </Tab>
            <Tab
              title={compact ? 'Scheduler' : undefined}
              className={({ selected: sel }) => roomTabClass(sel)}
            >
              <CalendarClock className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              {tabLabel('Scheduler')}
            </Tab>
            {/* TASKS (§planner) — only for PROJECT (phased) rooms. Appended LAST so the existing
                ROOM_*_TAB_INDEX constants (Connections=2, Executions=4) stay correct. Only once the room is in
                Plan / Run / Verify (MODES.md §7). */}
            {isProject && planTabsOn ? (
              <Tab
                title={compact ? 'Tasks' : undefined}
                className={({ selected: sel }) => roomTabClass(sel)}
              >
                <ListChecks className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                {tabLabel('Tasks')}
              </Tab>
            ) : null}
            {/* PROJECT MAP (PROJECT-MAP.md) — appended LAST after Tasks, same isProject gate so tab
                index parity holds (the pre-Tasks constants Connections=2/Executions=4 are untouched). */}
            {isProject && planTabsOn ? (
              <Tab
                title={compact ? 'Project map' : undefined}
                className={({ selected: sel }) => roomTabClass(sel)}
              >
                <ProjectMapIcon className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                {tabLabel('Project map')}
              </Tab>
            ) : null}
            {/* ROOM ALARMS (RELEASE-ALARMS.md §Rooms) — dynamic, RIGHT-ALIGNED (``ml-auto``), shown
                ONLY when something is broken (tripped > 0). Appended LAST so the existing tab-index
                constants stay correct (its panel is appended last to match). */}
            {roomAlarmCount > 0 ? (
              <Tab
                className={({ selected: sel }) =>
                  `${compact ? 'rounded px-1.5 py-1' : 'ml-auto border-b-2 px-2 py-1.5'} shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold transition-colors ${
                    sel
                      ? (compact ? 'bg-red-50 text-red-600' : 'border-red-500 text-red-600')
                      : (compact ? 'text-red-500 hover:bg-red-50' : 'border-transparent text-red-500 hover:border-red-300 hover:text-red-600')
                  }`
                }
                data-testid="room-alarm-tab"
                title={compact ? `Alarm (${roomAlarmCount})` : undefined}
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {compact ? <span className="text-[11px]">{roomAlarmCount}<span className="sr-only"> room alarms</span></span> : `Alarm (${roomAlarmCount})`}
              </Tab>
            ) : null}
          </Tab.List>
  );

  return (
    <PlanWordsContext.Provider value={planWordsValue}>
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
      {/* (Global search lives in WorkbenchShell's top bar — not duplicated here.
          Room uiState + AppSync-connection show as chips beside the room title.) */}
      {compact
        ? createPortal(roomHeaderRow, dockSlots.room)
        : (
          <div className="relative shrink-0 border-b border-gray-200 bg-white px-3 py-2 sm:px-4">
            {roomHeaderRow}
          {/* Room budget (USAGE-AND-BUDGET.md §5b) as the header's underline — a
              thin bar on the bottom border, filled to consumed %, transitioning
              green → amber (80%) → red (95%+). Self-gates: nothing when the room
              has no allocation. */}
          <RoomBudgetGauge networkId={networkId} poolId={poolId} variant="border" />
          </div>
        )}
      {compact ? (
        <div className="relative shrink-0"><RoomBudgetGauge networkId={networkId} poolId={poolId} variant="border" /></div>
      ) : null}


      <Tab.Group
        as="div"
        selectedIndex={roomTabIndex}
        onChange={setRoomTabIndex}
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        {compact
          ? createPortal(roomTabList, dockSlots.tabs)
          : (
            <div className="min-w-0 shrink-0 border-b border-gray-200 bg-white px-3 sm:px-4">
              {roomTabList}
            </div>
          )}

        {/* CONNECTION-READINESS chip (fail-fast framework): hangs at the top across all tabs whenever
            an ACTIVE agent's assigned MCP/connector credential blocks the run — either a never-configured
            "Needs credential" shell OR a set-but-broken (server-verified BAD) credential. This mirrors the
            run gate exactly (both buckets block Send + 409 the trigger), so the user always SEES why the
            room won't run. One click jumps to the Connections tab (offending row highlighted with its
            error) so they configure/fix + re-test; the cache unblocks execution in real time. */}
        {mcpGateChip ? (
          <button
            type="button"
            onClick={() => navigateRoomToConnections({ tool: mcpGateChip.tool })}
            data-testid="room-connection-readiness-chip"
            className="flex w-full items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-left text-[11px] text-amber-800 hover:bg-amber-100 transition-colors"
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{mcpGateChip.message}</span>
            <span className="shrink-0 font-medium underline">Open Connections</span>
          </button>
        ) : null}

        <Tab.Panels className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {/* NETWORK panel — ``unmount={false}`` keeps it mounted (just hidden) when the user
              switches tabs, so the COMPOSER's in-progress work survives: the typed draft + staged
              uploads (both Composer-local state) are no longer destroyed on a tab switch. It also
              keeps the live stream/diagram current in the background. Heavy read-only tabs below
              stay on the default unmount so we don't keep everything alive. */}
          <Tab.Panel unmount={false} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {/* ONE WHITE ROOM SURFACE. This row holds the stream (left) and the docked agent-tree
                panel (right, ``bg-white``). It used to be ``bg-gradient-to-br from-gray-50
                to-blue-50``, which is what made every card in the stream read as a separate window
                floating on tinted paper — a white run block on a gray-blue wash is a visible tile
                no matter how its own borders are styled. Flat white here means the stream, the
                cards and the ReactFlow tree panel are all the same sheet, and the only visual
                separation left is the panels' own hairline borders. */}
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-row bg-white">
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                {lifecycleUi.bannerText ? (
                  <LifecycleBanner
                    text={lifecycleUi.bannerText}
                    tone={lifecycleUi.bannerTone}
                  />
                ) : null}
                <div ref={streamScrollRef} className="sf-room-surface min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-6">
                  <div className="w-full">
                    {/* scroll-back sentinel: nearing it loads the next older page of the discussion */}
                    <div ref={olderSentinelRef} aria-hidden data-testid="stream-older-sentinel" />
                    {discussionHasMore ? (
                      <div className="flex justify-center pb-2 text-[11px] text-gray-400" data-testid="stream-older-loading">
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> Loading earlier messages…
                      </div>
                    ) : null}
                    {/* SLACK DENSITY (MODES.md §3f): no blanket gap — messages space by their own padding; run blocks
                        and cards carry their own margin */}
                    <div className={WORKBENCH_ACTIVITIES_STREAM_OUTER_PADDING}>
                    {/* ``!sending``: while a send is booting we show the boot spinner (below), NOT the
                        "No activity yet" empty-state — otherwise a Send looked like nothing happened
                        (the spinner-gone regression). runBlocks are hidden during boot, so turnGroups
                        being non-empty here just means prior/other content; the empty-state is only
                        for a genuinely idle, not-booting room. */}
                    {turnGroups.length === 0 && localSystemEvents.length === 0 && !sending
                      && !discussion.messages.length && !directRuns.length ? (
                      !historyLoaded ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        </div>
                      ) : composerMode === 'context' ? (
                        <ContextStartHint />
                      ) : (
                        <div className="flex items-center justify-center py-12">
                          <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-500 shadow-sm">
                            No activity yet. Send a prompt to start a run.
                          </span>
                        </div>
                      )
                    ) : null}
                    {/* LOAD PREVIOUS — the room shows ONLY the current run (like a fresh room); prior
                        runs are folded away. This top button reveals them on demand. Two sources of
                        "more above": (a) runs already loaded in memory but folded (``hasFoldedRuns``)
                        → just expand them (no fetch); (b) older runs still on the channel
                        (``historyHasMore``) → page one in. Show the button if EITHER exists; a click
                        expands the fold first, else pages the channel. */}
                    {(hasFoldedRuns || (historyLoaded && historyHasMore)) ? (
                      <div className="flex justify-center pb-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (hasFoldedRuns) setShowPrevious(true);   // reveal in-memory folded runs
                            else loadMoreHistory();                     // else fetch older from the channel
                          }}
                          disabled={historyPaging}
                          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-600 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                          data-testid="load-previous-run"
                        >
                          {historyPaging ? (
                            <><Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading…</>
                          ) : (
                            'Load previous'
                          )}
                        </button>
                      </div>
                    ) : null}
                    {/* PER-RUN BLOCKS — each run is a collapsible block (newest on top). Header
                        click loads that run; each renders its OWN turns + (for historical runs) its
                        own tree on ITS topology. The LIVE run's tree is in the docked right panel. */}
                    {/* THE ROOM'S ACTIVITY (owner 2026-10-03): run blocks, direct-run cards and discussion messages
                        in ONE chronological stream — Slack with agents as members. */}
                    {((stream) => {
                      // SLACK DAY PILLS (MODES.md §3a-groups): each day's pill opens "Jump to…" — Most recent · a loaded
                      // day · The very beginning (scrolling up keeps paging older days in)
                      const days = stream.filter((x) => x.kind === 'divider').map((x) => ({ key: x.key, label: x.label }));
                      const onJump = (k) => {
                        const root = streamScrollRef.current;
                        if (k === 'latest') { stick.jumpToBottom(); return; }
                        if (k === 'earliest') { if (root) root.scrollTo({ top: 0, behavior: 'smooth' }); return; }
                        const el = root && root.querySelector(`[data-day="${CSS.escape(k)}"]`);
                        if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
                      };
                      const renderIt = (it) => (it.kind === 'divider' ? (
                      <StreamDayDivider key={it.key} label={it.label} dayKey={it.key} days={days} onJump={onJump} />
                    ) : it.kind === 'direct' ? (
                      <div key={it.item.runId} className="my-3"><DirectRunCard run={it.item} mode={directModes[it.item.runId] || ''}
                        onWatch={(r) => openAgentInDock(`${r.agentId}@${r.runId}`)}
                        onStop={(r) => builderControls.stopBuilder(r.runId)}
                        stopping={builderControls.stoppingSet.has(it.item.runId)}
                        renderOutputs={(run) => (
                          <WorkOutputs networkId={networkId} poolId={poolId} run={run} workspace={roomWs}
                            planPicks={(ctxBuild.plan || {}).picks} canShare={canExecute}
                            onOpen={(f) => openDocFor({ id: `ws-${run.runId}`, agentName: run.agentName || run.agentId },
                              { index: 0, name: f.name, size: f.size, type: f.contentType || '',
                                workspace: { path: f.path, workspaceId: roomWs.workspaceId } })} />
                        )} /></div>
                    ) : it.kind === 'message' ? (
                      it.item.kind === 'context' ? (
                        <div key={it.item.id} className="my-1.5"><ContextCard m={it.item} onOpen={openContextFor}
                          active={!!openContext && openContext.id === it.item.id} /></div>
                      ) : <React.Fragment key={it.item.id}>
                        <DiscussionMessage me={user?.sub || ''} m={it.item} compact={!!it.compact} timeOnly={isSingleDayGroup(it.group)} onAdopt={canExecute ? handleAdopt : null}
                          questionOf={questionOf} onJumpTo={jumpToMessage}
                          canEdit={_canEditMsg(it.item)}
                          canDelete={canModerate || (it.item.kind === 'user' && !!user?.sub && it.item.authorSub === user.sub)}
                          onEdit={handleEditAny} onDelete={setDeleteAsk} onConfirm={handleConfirm}
                          onSignal={handleSignal} nameOf={signalNameOf} personOf={personOf} renderFiles={renderFiles} renderLinks={renderLinks} onReplyInThread={handleReplyInThread}
                          onKeep={canExecute ? handleKeep : null} keepKinds={keepKinds} />
                        {/* STAND-IN (STAND-IN.md): an agent answering for someone mentioned here; MY drafts (only mine) */}
                        {standin.thinking[it.item.id] ? (
                          <div data-testid={`standin-thinking-${it.item.id}`} className="pl-11 text-[0.75rem] italic text-indigo-500">
                            {(discussion.agents || []).find((a) => a.id === standin.thinking[it.item.id].agentId)?.name || 'A stand-in'} is answering for {personOf(standin.thinking[it.item.id].forSub)?.name || 'them'}…
                          </div>
                        ) : null}

                        <ReplyBar root={it.item} nameOf={signalNameOf} personOf={personOf} renderFiles={renderFiles} onOpen={openThreadFor}
                          active={!!threadRoot && threadRoot.id === it.item.id} />
                        {/* ASK PROGRESS (MODES.md §10.2a): the agents answering THIS question, live for everyone */}
                        {(askProgressByQ[it.item.id] || []).map((p) => (
                          <AgentAnswering key={p.key} p={p} onGrow={stick.follow}
                        askerName={questionOf(p.questionId) ? authorLabel(questionOf(p.questionId), personOf, '') : ''} />
                        ))}
                      </React.Fragment>
                    ) : ((rb) => (
                      // Each block in its OWN error boundary — a single bad run (malformed manifest,
                      // etc.) shows an inline error with the real message instead of blanking the
                      // WHOLE stream (which is what "my blocks disappeared" was).
                      <div key={rb.runId} className="my-3"><RouteErrorBoundary routeName={`run:${rb.index}`}>
                        <RunBlock
                          turns={rb.turns}
                          liveRoster={rb.liveRoster}
                          releaseVersion={rb.releaseVersion}
                          isLiveRun={rb.isLiveRun}
                          networkId={networkId}
                          poolId={poolId}
                          stageDepthCap={stageDepthCap}
                          disabledByAgentId={agentDisables}
                          reuseByAgentId={agentReuses}
                          stageCost={(stageCostByRun && stageCostByRun[rb.runId]) || null}
                          verbose={verboseMode}
                          dynamicView={dynamicView}
                          builderControls={builderControls}
                          handledBuilderWaitSet={handledBuilderWaitSet}
                          resolvedWaitEventIds={resolvedWaitEventIds}
                          renderRowAction={renderBuilderRowAction}
                          // FOCUS: highlight the run whose graph is in view; click a dimmed block to
                          // focus it. Only wired when >1 run is on screen (a lone run needs no anchor).
                          runId={rb.runId}
                          {...(focusManaged
                            ? { focused: rb.runId === effectiveFocusedRunId, onFocus: setFocusedRunId }
                            : {})}
                          // The DOCK is the ONE AND ONLY agent-stream surface — ALWAYS. ``hideReader``
                          // is unconditionally true so the obsolete inline ``#0d1117`` reader inside
                          // AgentHierarchyPanel NEVER renders (it used to reappear whenever the dock
                          // was collapsed → the "builder pops above the agents" bug). Not gated on
                          // ``dockEnabled`` (collapse just hides the dock panel; it must NOT resurrect
                          // the inline reader) nor on ``rb.isLiveRun`` (every block, live or settled).
                          hideReader
                          // Every block's tree (live OR settled/historical) is a pure launcher: a
                          // click fires ``onSelectAgent`` → opens/focuses a DOCK tab (expanding the
                          // dock if collapsed). No inline selection (``selectedAgentId`` null — there
                          // is no inline pane). Wiring it for settled blocks too fixes clicking an
                          // agent in an executed/past run (e.g. the CmmcCompliance plan run) rendering
                          // the obsolete inline builder instead of opening the dock.
                          selectedAgentId={null}
                          onSelectAgent={onAgentActivate}
                        />
                      </RouteErrorBoundary></div>
                    ))(it.item));
                      // ONE DAY = ONE SECTION (Slack): its pill sticks only while ITS messages are on screen, then
                      // scrolls away with them — the next day's pill takes over. All pills in one parent stacked up.
                      return streamDaySections(stream).map((g) => (
                        <section key={g.key} data-testid="stream-day-section" data-day-section={g.key}>
                          {g.items.map(renderIt)}
                        </section>
                      ));
                    })(withContinuations(withRosterRollups(withStreamGroups(streamTimeline(runBlocks, directRuns, threads.top))), user?.sub || ''))}
                    {/* an agent answering a question that isn't on screen yet (an older page / still arriving) */}
                    {askProgress.filter((p) => !threads.top.some((m) => m.id === p.questionId)).map((p) => (
                      <AgentAnswering key={p.key} p={p} onGrow={stick.follow}
                        askerName={questionOf(p.questionId) ? authorLabel(questionOf(p.questionId), personOf, '') : ''} />
                    ))}
                    {localSystemEvents.length > 0 ? (
                      <div className="space-y-2">
                        {localSystemEvents.map((sys) => (
                          <div
                            key={sys.id}
                            className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"
                          >
                            <div className="whitespace-pre-wrap">{sys.text}</div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {sending && (() => {
                      // BOOTING CARD — fills the "void" between Send and the first stream event with a
                      // live, honest status: a moving elapsed clock (``bootSecs`` ticks each second) +
                      // a staged headline tied to the one real milestone we can observe (StartExecution
                      // accepted). No fake progress — just the true stage + how long it's taken.
                      const clock = `${Math.floor(bootSecs / 60)}:${String(bootSecs % 60).padStart(2, '0')}`;
                      const headline = !bootAccepted
                        ? 'Sending run…'
                        : (bootSecs >= 10
                            ? 'Agents are starting up — this can take a moment.'
                            : 'Run accepted — waking agents…');
                      return (
                        <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <Loader2 className="h-4 w-4 animate-spin text-blue-500" aria-hidden />
                            <span className="text-sm font-medium text-slate-700">{headline}</span>
                            <span className="ml-auto font-mono text-xs tabular-nums text-slate-400">{clock}</span>
                          </div>
                          {bootArtifacts > 0 ? (
                            <div className="mt-1.5 pl-[26px] text-xs text-slate-500">
                              Preparing {bootArtifacts} artifact{bootArtifacts === 1 ? '' : 's'}…
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}
                    {contextBuilding && (
                      <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-4 py-2.5"
                           data-testid="room-context-building">
                        <div className="flex items-center gap-2.5">
                          <Loader2 className="h-4 w-4 animate-spin text-sky-500" aria-hidden />
                          <span className="text-sm text-slate-700">
                            Preparing context from <span className="font-medium">{contextBuilding.label}</span> — the run
                            will start as soon as it’s ready.
                          </span>
                          <button
                            type="button"
                            onClick={() => setContextBuilding(null)}
                            className="ml-auto text-xs text-sky-500 hover:text-sky-700"
                          >
                            dismiss
                          </button>
                        </div>
                      </div>
                    )}
                    <div ref={bottomRef} />
                    </div>
                  </div>
                  {/* NEW MESSAGES ↓ (Slack): scrolled up while people post → a pill pinned to the bottom of the stream */}
                  {stick.newCount > 0 ? (
                    <div className="pointer-events-none sticky bottom-2 flex justify-center">
                      <button type="button" data-testid="stream-new-messages" onClick={stick.jumpToBottom}
                        className="pointer-events-auto inline-flex items-center gap-1 rounded-full bg-indigo-600 px-3 py-1 text-[11px] font-medium text-white shadow-md transition-colors hover:bg-indigo-700">
                        {stick.newCount} new message{stick.newCount === 1 ? '' : 's'}
                        <ChevronDown className="h-3 w-3" aria-hidden />
                      </button>
                    </div>
                  ) : null}
                </div>

                {/* The composer is NOT here anymore — it's a SHARED room-level footer (below the
                    Tab.Group) so the SAME instance (draft + uploads + plan-mode/groomScope state)
                    serves BOTH the Activities and Tasks tabs. One instance = inherently shared, no
                    state-lifting, no desync. See the SHARED COMPOSER block after the Tab.Group close. */}
              </div>

              {openContext ? (
                <ContextPane networkId={networkId} poolId={poolId} message={openContext} onClose={() => setOpenContext(null)}
                  canApprove={canExecute} onApproved={() => loadCtxBuild()} />
              ) : null}
              {/* THE DOC PANE (CONTEXT-DOCS.md §P1) — the same column as the Thread pane, one at a time */}
              {docLive ? (
                <DocPane networkId={networkId} poolId={poolId} message={docLive.message} att={docLive.att}
                  onClose={() => setOpenDoc(null)} />
              ) : null}
              {/* THE THREAD PANE (MODES.md §3b) — its OWN column between the stream and the network diagram */}
              {threadRoot ? (
                <ThreadPane networkId={networkId} poolId={poolId} root={threadRoot} liveReplies={liveReplies}
                  focus={!!openThread?.focus} onClose={() => setOpenThread(null)} onSend={handleThreadSend}
                  onContinue={handleThreadContinue}
                  roomLabel={displayName || ''} me={user?.sub || ''}
                  renderMessage={(x, { isRoot, compact }) => (
                    <DiscussionMessage key={x.id} isReply={!isRoot} compact={!!compact} me={user?.sub || ''} m={x}
                      canEdit={_canEditMsg(x)}
                      canDelete={canModerate || (x.kind === 'user' && !!user?.sub && x.authorSub === user.sub)}
                      onEdit={handleEditAny} onDelete={setDeleteAsk} onConfirm={handleConfirm} onSignal={handleSignal} nameOf={signalNameOf} personOf={personOf} renderFiles={renderFiles} renderLinks={renderLinks}
                      onKeep={canExecute ? handleKeep : null} keepKinds={keepKinds} />
                  )} />
              ) : null}

              {/* Docked agent-tree panel — replaces the old floating MiniFlow overlay. Uses the
                  side-space freed by the centered stream to show the WHOLE topology as a live tree
                  (ManifestTreeFlow, same class as the workbench/agent-config). Nodes color by the
                  AppSync stream in real time (running/awaiting/done/failed/skipped). Clicking a node
                  loads that agent into the shared container. Shown when centered + a topology exists
                  + the user hasn't hidden it. */}
              {/* The panel FRAME always shows beside the wide stream (2026-09-22: the network diagram must
                  always be available). Frame is decoupled from manifest availability so it's never a phantom (blank frame >
                  nothing); ManifestTreeFlow renders "No diagram available yet" until the manifest loads. */}
              {!treeOpen ? (
                /* DOCKED (collapsed): a slim rail — the arrow opens the network diagram. */
                <div className="hidden md:flex shrink-0 flex-col items-center border-l border-slate-200 bg-white pt-2"
                  data-testid="tree-dock-rail">
                  <button
                    type="button"
                    onClick={() => setTreeOpen(true)}
                    data-testid="tree-dock-open"
                    aria-label="Show the network diagram"
                    aria-expanded="false"
                    title="Show the network diagram"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M9.5 4 5.5 8l4 4" /><path d="M13 4v8" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div
                  className="relative hidden md:flex shrink-0 flex-col border-l border-slate-200 bg-white min-h-0"
                  style={{ width: `${treePanelWidth}px` }}
                  data-testid="tree-dock-panel"
                >
                  {/* dock it back */}
                  <button
                    type="button"
                    onClick={() => setTreeOpen(false)}
                    data-testid="tree-dock-close"
                    aria-label="Hide the network diagram"
                    aria-expanded="true"
                    title="Hide the network diagram"
                    className="absolute right-2 top-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white/90 text-slate-500 shadow-sm hover:bg-slate-50 hover:text-slate-800"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M6.5 4l4 4-4 4" /><path d="M3 4v8" />
                    </svg>
                  </button>
                  {/* Drag-to-resize divider on the panel's left edge. */}
                  <div
                    onMouseDown={onTreeDividerDown}
                    className="absolute left-0 top-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-blue-300/50"
                    title="Drag to resize"
                    role="separator"
                    aria-orientation="vertical"
                  />
                  <div className="relative min-h-0 flex-1">
                    {isManifest(networkAsl) ? (
                      <ManifestTreeFlow
                        data={networkAslGraph}
                        // FRESH-RUN CLEAR (render layer): while THIS send is booting, drop the prior
                        // run's node colouring so the docked graph reads as a fresh (all-queued) tree
                        // on the USER event; the new run's live statuses repaint it once it streams.
                        statusByAgentId={sending ? {} : treeStatusByAgentId}
                        highlightLogicalId={selectedTreeAgentId}
                        onAgentClick={onAgentActivate}
                        dimmedAgentIds={roomDimmedAgentIds}
                        // Per-room agent disables: 'branch' dims the whole subtree. TWO TIERS,
                        // colored differently: 'frozen' (room's immutable carve — slate/locked,
                        // no enable action) vs 'room' (owner's own — red-dashed, toggleable).
                        // Right-click menu only for pool managers — same gate as Settings.
                        disabledByAgentId={agentDisables}
                        disabledSourceByAgentId={agentDisableSources}
                        onAgentDisableAction={canManagePoolSettings ? handleAgentDisableAction : null}
                        // RESULT REUSE (RESULT-REUSE.md): only offered on project/phased networks
                        // (the backend gates too) + to pool managers, same authority as disable.
                        reuseByAgentId={agentReuses}
                        onAgentReuseAction={(canManagePoolSettings && isProject) ? handleAgentReuseAction : null}
                        canvasClassName="bg-white"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center px-4 text-center text-[11px] text-slate-400">
                        Agent tree loads when the network topology is available.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* The AGENT STREAM DOCK is NOT here anymore — it moved into the SHARED footer (below the
                Tab.Group), rendered BELOW the composer, so the prompt stays sticky ABOVE the dock the
                way it used to be, and the dock + prompt travel together to the Tasks tab. See the
                SHARED FOOTER block after the Tab.Group close. */}
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <AgenticDataPlaneTab
              networkId={networkId}
              poolId={poolId}
              viewerEmail={user?.email}
              poolRole={poolRole}
              workspaceRole={workspaceRole}
              lifecycleFrozen={lifecycleFrozen}
            />
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white outline-none">
            {activeTenantId ? (
              secretsRegistryV1Enabled() ? (
                <SecretsCatalog
                  tenantId={activeTenantId}
                  catalogNetworkId={networkId}
                  catalogPoolId={poolId}
                  leadPoolId={leadPoolId}
                  allowCreate={canManagePoolSettings}
                  workspaceRole={workspaceRole}
                  poolRole={poolRole}
                  viewerUserSub={user?.sub}
                  networkOwnerUserId={networkOwnerUserId}
                  autoOpenTool={connectionsAutoOpenTool}
                  onAutoOpenConsumed={() => setConnectionsAutoOpenTool(null)}
                  readinessByMcp={readinessByMcp}
                  declaredConnectors={declaredConnectors}
                  declaredMcps={declaredMcps}
                  connectorAssignments={connectorAssignments}
                  onConnectionsChanged={refreshMcpGate}
                  roomAgents={seededRoster
                    .filter((r) => r.agentId && !String(r.agentId).startsWith('__'))
                    .map((r) => ({ id: r.agentId, label: r.label || r.agentId }))}
                />
              ) : (
                <div className="p-6 text-sm text-gray-600">
                  Connections need the secrets registry in this deployment (
                  <code className="rounded bg-gray-100 px-1 text-xs">VITE_STEPFLOW_SECRETS_REGISTRY_V1</code>
                  ).
                </div>
              )
            ) : (
              <div className="p-6 text-sm text-gray-600">
                Sign in with an organization tenant to view and manage connections.
              </div>
            )}
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
            <AgentWorkspaceBrowser networkId={networkId} poolId={poolId}
              networkWorkspaceId={networkWorkspaceId}
              canManageHistory={!!roomModeState.canSwitch}
              publishTarget={roomModeState.publishTarget || null}
              onPublishTargetChange={(t) => setRoomModeState((st) => ({ ...st, publishTarget: t }))} />
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <ExecutionsTab
              networkId={networkId}
              poolId={poolId}
              messages={messages}
              onOpenLog={openLogInDock}
            />
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <SchedulerTab
              networkId={networkId}
              poolId={poolId}
              canManage={canManagePoolSettings}
            />
          </Tab.Panel>

          {/* TASKS (§planner) — the project plan the user reviews + finalizes. Panel order matches
              the Tab.List (appended after Scheduler), gated on isProject so index parity holds. */}
          {isProject && planTabsOn ? (
            /* TASKS panel — ``unmount={false}`` so the user's groom SELECTION (checkboxes / wave
               chips) + tier choice + collapsed-milestone state survive a tab switch. Without it the
               panel remounted → ``load()`` re-ran → selection wiped. ``active`` lets TasksTab refresh
               its DATA on return WITHOUT clobbering that selection (see there). */
            <Tab.Panel unmount={false} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <TasksTab
                networkId={networkId}
                poolId={poolId}
                teamFocus={roomModeState.expertPart?.role === 'department' ? (roomModeState.expertPart.team || '') : ''}
                canManage={canManagePoolSettings}
                onGroom={setGroomScope}
                planTier={planTier}
                onPlanTier={setPlanTier}
                active={roomTabIndex === tasksTabIndex}
                onPlanAllWaves={handlePlanAllWaves}
                onStopAutoWave={handleStopAutoWave}
                focusRequest={tasksFocusRequest}
                planUpdatedSignal={planStateBump}
                planUpdating={planUpdating}
                onOpenPlannerStream={openPlannerStream}
                onOpenPlanDoc={openPlanDocInDock}
              />
            </Tab.Panel>
          ) : null}
          {/* PROJECT MAP panel — matches the Tab.List entry above (isProject-gated, appended last). */}
          {isProject && planTabsOn ? (
            <Tab.Panel unmount={false} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">
              <ProjectMapPanel
                networkId={networkId}
                poolId={poolId}
                planUpdatedSignal={planStateBump}
                planUpdating={planUpdating}
                onOpenPlanDoc={openPlanDocInDock}
                onOpenConnections={(tool) => {
                  // Gaps → "Set up a new connection": open THAT creator (one-shot deep-link) on the Connections tab
                  if (tool) setConnectionsAutoOpenTool(tool);
                  setRoomTabIndex(ROOM_CONNECTIONS_TAB_INDEX);
                }}
                onRefineEpic={(epic) => {
                  // EPIC EXPANSION (§0.1) — "select an epic → CREATE its granular tasks", the
                  // intuitive sibling of wave-select. Sets an epic-kind groomScope carrying
                  // ``epicParent`` (the epic row id, e.g. M1-001): the composer prefills "Create tasks
                  // for {M} → {story}", and the run AUTHORS ≤10 leaf children under that epic (only
                  // leaves execute). Jump to the Tasks tab, where the composer + the new tasks live.
                  if (!epic) return;
                  const msId = epic.milestoneId || (epic.id ? String(epic.id).split('/')[0] : '');
                  setGroomScope({
                    milestone: msId,
                    milestoneName: epic.milestoneName || msId,
                    epicParent: epic.epicId || '',
                    epicStory: epic.story || epic.epicId || '',
                    kind: 'epic',
                    onClear: () => setGroomScope(null),
                  });
                  setRoomTabIndex(tasksTabIndex);
                }}
              />
            </Tab.Panel>
          ) : null}
          {/* ROOM ALARMS panel — matches the right-aligned Tab above (tripped-gated, appended last). */}
          {roomAlarmCount > 0 ? (
            <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">
              <RoomAlarmsPanel alarms={roomAlarms} onReset={resetRoomAlarmFn} onRefresh={bumpRoomAlarms}
                               networkId={networkId} poolId={poolId} />
            </Tab.Panel>
          ) : null}
        </Tab.Panels>
      </Tab.Group>

      {/* SHARED FOOTER — ONE instance of the composer AND the agent-stream dock for the whole room,
          rendered BELOW the tabs. Ordered composer-then-dock so the PROMPT stays sticky ABOVE the
          dock exactly the way it used to be (the regression was the dock landing above the prompt).
          Shown on Activities (0) AND Tasks ({tasksTabIndex}) so the user can type/groom/Send AND watch
          agent streams from either tab, without a second desyncing composer. It stays MOUNTED
          (``hidden`` toggle, not a conditional unmount) so the draft + staged uploads +
          plan-mode/groomScope survive tab switches. On other tabs it's hidden. Auto-nav (in
          handleSendPrompt/handlePlanAllWaves) jumps to Activities on Send so the run stream is visible. */}
      <div className={(roomTabIndex === 0 || roomTabIndex === tasksTabIndex) ? 'flex shrink-0 flex-col' : 'hidden'}>
          <ConfirmDialog
            open={!!deleteAsk}
            testid="context-msg-delete-confirm"
            title="Delete message?"
            message="This can't be undone. It's removed for everyone in the room."
            confirmLabel="Delete"
            cancelLabel="Cancel"
            danger
            onConfirm={confirmDeleteMessage}
            onCancel={() => setDeleteAsk(null)}
          />
          <HandoffDialog
            open={!!leaveAsk}
            ctxBuild={ctxBuild}
            docs={handoffDocs}
            canLead={canExecute}
            busy={handoffBusy}
            error={handoffError}
            onBuild={handleBuildContext}
            onConfirm={confirmHandoff}
            onCancel={() => { const a = leaveAsk; setLeaveAsk(null); a && a.resolve(false); }}
          />
          <DeployIntentsBanner deployIntents={roomModeState.deployIntents}
            onAction={async (req) => {
              const r = await setDeployIntent(networkId, req, { pool: poolId });
              setRoomModeState((st) => ({ ...st, deployIntents: r?.deployIntents || st.deployIntents }));
              if (req.action === 'run') setTimeout(() => loadRoomMode(), 4000);
            }}
            onReadPlan={(req) => setDeployIntent(networkId, req, { pool: poolId })} />
          <Composer
            discussionAgents={discussion.agents}
            contextModel={roomModeState.contextModel || ''}
            internetSearch={roomModeState.internetSearch !== false}
            canSetContextModel={!!roomModeState.canSetModel}
            contextModelSetByName={roomModeState.contextModelSetByName || ''}
            onSetContextModel={handleSetContextModel}
            mentionPeople={mentionPeople}
            mentionTeams={mentionTeams}
            onTeamRequest={handleTeamRequest}
            mentionExperts={roomModeState.expertConsults?.experts || []}
            onExpertConsult={({ expertNetworkId, text }) => handleConsult({ action: 'ask', expertNetworkId, text })}
            consultsWaiting={(() => {
              const q = (roomModeState.expertConsults?.queue || []).filter((c) => ['waiting', 'escalated'].includes(c.status));
              return q.length ? { count: q.length, first: q[0], onReview: () => { setMembersDrawerOpen(true); } } : null;
            })()}
            deckStatus={roomModeState.expertTraining ? (
              // an expert's TRAINING room: Train (what the room settled → training) in place of Build context
              <ExpertTrainBar training={roomModeState.expertTraining} onTrain={() => handleExpertTrain({ action: 'train' })}
                onDecide={(action, id) => handleExpertTrain({ action, id })} />
            ) : modesOn && _discussionOn && !keptConversational ? (
              <ContextBuildBar inline build={ctxBuild} onBuild={handleBuildContext} pendingAgent={pendingAskNames(asksPending) || discussionPending} />
            ) : modesOn && composerMode === 'plan' ? (
              // PLAN'S BASIS (§H3): what this plan is made from — and when the context moved on
              <PlanBasis plan={ctxBuild.plan} onUpdate={() => { setHandoffError(''); setLeaveAsk({ resolve: () => {} }); }} />
            ) : null}
            deckTools={modesOn && _discussionOn ? <StandinControl standin={standin} /> : null}
            onTyping={typing.onTyping}
            onTypingStop={typing.onStopped}
            typingLine={typingText}
            onSendContext={modesOn ? handleSendContext : null}
            onModeChange={setComposerMode}
            onLeaveContext={isProject && !contextOnly ? handleLeaveContext : null}
            hasPlan={hasWorkedPlan(planState)}
            roomMode={modesOn && (roomModeKnown || discussionOnly) ? effectiveRoomMode : undefined}
            canSwitchMode={roomModeState.canSwitch}
            onRequestMode={modesOn ? handleRequestMode : null}
            contextOnly={discussionOnly}
            runNetwork={discussionOnly && !contextOnly}
            keptConversational={keptConversational}
            onOpenPlanGate={discussionOnly && !contextOnly ? () => setPlanGateOpen(true) : null}
            modeLoading={!!networkId && (isProject ? !(roomModeKnown || contextOnly)
              : !(roomModeState.loaded && roomModeState.room === roomKey))}
            dockOpen={dockEnabled}
            onToggleDock={() => setDockEnabled((v) => !v)}
            verbose={verboseMode}
            isProject={modesOn}
            planFinalized={planState.finalized}
            projectKind={planState.projectKind || 'executable'}
            readyTasks={planState.readyTasks}
            currentMilestone={planState.currentMilestone}
            // Guided-journey pipeline stage (Plan→Review→Finalize→Run) for the composer stepper.
            projectStage={projectStage}
            groomScope={groomScope}
            planTier={planTier}
            // Deterministic next-prompt proposal (placeholder + Tab-to-fill) + inline milestone approve.
            suggestedNextPrompt={suggestedNextPrompt}
            seedPrompt={composerSeed}
            // The scope the proposal is ACTUALLY confined to (e.g. a specific wave), so a Tab-filled
            // "Plan X wave N" run matches its label instead of silently planning the whole milestone.
            suggestedGroomScope={suggestedGroomScope}
            approvableMilestone={approvableMilestone}
            suggestedUpdate={roomModeState.roomPlan?.suggestedUpdate || null}
            onSuggestedUpdate={handleSuggestedUpdate}
            gateAdvice={runGateReviews.length ? <GateAdvice reviews={runGateReviews} testId="run-gate-advice" /> : null}
            planAdvice={planGateReviews.length ? <GateAdvice reviews={planGateReviews} testId="plan-gate-advice" /> : null}
            onApproveMilestone={handleApproveMilestone}
            approveMilestoneBusy={bandApproveBusy}
            // "Review" jump target for the band — the milestone the user would inspect/edit: the
            // approvable one if any, else the planning cursor. {id,name} or null.
            reviewMilestone={approvableMilestone || (planState.planningCursor ? {
              id: planState.planningCursor,
              name: (planState.milestones.find((m) => m.id === planState.planningCursor) || {}).name
                || planState.planningCursor,
            } : null)}
            onReviewMilestone={handleReviewMilestone}
            networkId={networkId}
            poolId={poolId}
            canExecute={canExecute}
            uiState={uiState}
            pendingApproval={pendingApproval}
            iHoldClaim={iHoldClaim}
            peerHoldsClaim={peerHoldsClaim}
            peerLabel={peerLabel}
            approvalBusy={approvalBusy}
            sending={sending}
            frozen={lifecycleFrozen}
            frozenReason={lifecycleUi.disabledReason}
            mcpGateReason={mcpGateReason}
            onSendPrompt={handleSendPrompt}
            onClaim={handleClaim}
            onApprove={() => handleApprovalDecision('APPROVE')}
            onDeny={() => handleApprovalDecision('DENY')}
            onSendModify={handleSendModify}
            onDelegate={handleDelegate}
            onEscalate={handleEscalate}
            builderCliWait={composerBuilderCliWait}
            onBuilderCliHandled={composerOnBuilderCliHandled}
            canRespondToBuilderApproval={canRespondToBuilderApproval(poolRoles, workspaceRole)}
            activeBuilderRunId={composerActiveBuilderRunId}
            stoppingRun={stopping}
            onStopRun={handleStopRun}
          />
      </div>
      {/* AGENT STREAM DOCK (new UX, default on) — VSCode-terminal-style multi-tab agent-stream
          viewer. Clicking an agent (tree/row) opens a tab; many concurrent tabs, split panes,
          per-tab Stop. LIVES ON EVERY TAB (2026-09-23): split out of the composer's Activities/Tasks
          gate into its OWN always-mounted footer, so it travels everywhere — plan docs opened from
          Project Map, agent streams from any tab. Always mounted so open tabs survive tab switches.
          Collapse → a thin re-expand bar; the dock is the ONLY agent-stream surface. */}
      <div className="flex shrink-0 flex-col">
          {dockEnabled ? (
            <AgentStreamDock
              agentRoster={(dockExtraRows.length || directRuns.length)
                ? [...mergedRoster,
                   ...directRuns.flatMap((d) => d.rosterRows),
                   ...dockExtraRows.filter((x) => !mergedRoster.some((r) => r?.agentId === x.agentId))]
                : mergedRoster}
              networkId={networkId}
              poolId={poolId}
              taskId={currentRunId || undefined}
              requestedAgentId={dockRequest.agentId}
              requestedNonce={dockRequest.nonce}
              onStopRun={builderControls.stopBuilder}
              stoppingSet={builderControls.stoppingSet}
              onCollapse={() => setDockEnabled(false)}
            />
          ) : null}
          {/* collapsed → no strip: the command deck's lower-left "Agent streams" toggle re-opens it */}
      </div>

      <ContextPreviewPanel
        open={contextDrawerOpen}
        onClose={() => setContextDrawerOpen(false)}
        networkId={networkId}
        poolId={poolId}
        roomLabel={displayName || `${networkId} · ${poolId}`}
      />

      <PoolSettingsDrawer
        open={membersDrawerOpen}
        onClose={() => setMembersDrawerOpen(false)}
        onLeft={() => { setMembersDrawerOpen(false); window.location.assign('/agentic'); }}
        networkId={networkId}
        poolId={poolId}
        poolLabel={displayName || `${networkId} · ${poolId}`}
        tenantId={activeTenantId || ''}
        currentUserSub={user?.sub}
        currentUserPerson={{ userName: user?.name || '', email: user?.email || '' }}
        canManage={canManagePoolSettings}
        canManageConnectors={canManageConnectors}
        channelEvents={messages}
        onRosterChange={handleMemberCountFromRoster}
        triggerMode={triggerMode}
        onTriggerModeChange={handleTriggerModeFromDrawer}
        onNavigateToConnections={navigateRoomToConnections}
        networkWorkspaceId={networkWorkspaceId}
        leadPoolId={leadPoolId}
        internetSearch={{ on: roomModeState.internetSearch !== false, isDefault: roomModeState.internetSearchDefault !== false,
          setByName: roomModeState.internetSearchSetByName || '', canSet: !!roomModeState.canSetInternet }}
        onSetInternetSearch={handleSetInternet}
        deployIntents={roomModeState.deployIntents?.enabled === false ? null : roomModeState.deployIntents}
        onSetDeployConsent={async (req) => {
          const r = await setDeployIntent(networkId, req, { pool: poolId });
          setRoomModeState((st) => ({ ...st, deployIntents: r?.deployIntents || st.deployIntents }));
        }}
        roomStage={roomModeState.room === roomKey && roomModeState.loaded ? { stage: roomModeState.stage,
          cap: roomModeState.stageCap, canSet: !!roomModeState.canSetStageCap } : null}
        onSetRoomStageCap={handleSetStageCap}
        roomExperts={roomModeState.room === roomKey && roomModeState.loaded && roomModeState.expertPlugins
          ? { plugins: roomModeState.expertPlugins, candidates: roomModeState.expertCandidates,
            canSet: !!roomModeState.canSetExperts, canSetNetwork: !!roomModeState.canSetNetworkExperts } : null}
        onSetRoomExpert={handleSetExpert}
        roomFacts={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.expertFacts : null}
        onSetRoomFact={handleSetFact}
        roomLiaison={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.liaisonActions : null}
        onDecideLiaison={handleLiaisonDecide}
        roomTeamAccess={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.teamAccess : null}
        onSetTeamAccess={handleTeamAccess}
        roomExpertServices={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.expertServices : []}
        onOffering={handleOffering}
        onRequestSent={() => loadRoomMode()}
        roomExpertRequests={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.expertRequests : null}
        onRequestAct={handleRequestAct}
        onRequestStart={handleRequestStart}
        roomCertification={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.expertCertification : null}
        onCertify={handleCertify}
        roomRuleChanges={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.ruleChanges : null}
        onRuleChange={handleRuleChange}
        roomExpertConsults={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.expertConsults : null}
        onConsult={handleConsult}
        me={user?.sub ? { sub: user.sub, name: user.name || user.email || '' } : null}
        offeringSeed={offeringSeed}
        loadRoomConnections={loadRoomConnections}
        roomOnboarding={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.onboarding : null}
        expertTemplates={roomModeState.room === roomKey && roomModeState.loaded ? roomModeState.expertTemplates : []}
        onOnboardingAct={handleOnboardingAct}
        onApplyExpertTemplate={handleApplyTemplate}
        onOpenConnection={(tool) => navigateRoomToConnections({ tool })}
        loadConnections={loadToolConnections}
      />

      <PlanGateDialog open={planGateOpen} plan={roomModeState.roomPlan} onPlan={handlePlanRoom}
        onClose={() => setPlanGateOpen(false)} />

      <DelegateMemberDialog
        open={delegateDialogOpen}
        networkId={networkId}
        poolId={poolId}
        currentUserSub={user?.sub}
        onClose={() => setDelegateDialogOpen(false)}
        onConfirm={handleDelegateSubmit}
      />

    </div>
    </PlanWordsContext.Provider>
  );
}

const LIFECYCLE_BANNER_TONES = {
  danger: 'border-red-200 bg-red-50 text-red-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  muted: 'border-gray-200 bg-gray-100 text-gray-700',
};

function LifecycleBanner({ text, tone }) {
  const palette = LIFECYCLE_BANNER_TONES[tone] || LIFECYCLE_BANNER_TONES.muted;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`shrink-0 border-b px-4 py-2 text-xs font-medium ${palette}`}
    >
      {text}
    </div>
  );
}

